import {
  AUTOCOMPLETE_FIELDS,
  AppraisalTimestampSchema,
  ENCRYPTED_STRING_PATTERN,
  EmailScrapeMappingSchema,
  EmailScrapePipelineStepSchema,
  EnrichmentSchema,
  FETCH_RESULT_FIELDS,
  GUESTRIDES_ACCESS_FIELDS,
  GUESTRIDES_ACCESS_KEY,
  GUESTRIDES_DEALER_FIELDS,
  GUESTRIDES_KEY,
  IntegrationSchema,
  MoveTypeSchema,
  RcEmailScrapeConfigSchema,
  RideTypeOverrideSchema,
  allDefinitions,
  encryptedString,
  getCiphertext,
  getEncryptionVersion,
  isEncryptedString,
  readGuestridesAccessSection,
  splitGuestridesConfig
} from "./chunk-BAQPEJWK.mjs";
import {
  ALL_CONFIG_LEVELS,
  CONFIG_LEVELS_REGISTRY,
  CONFIG_TABLES,
  DEFINITION_KINDS,
  LEVELS,
  effectiveReadableAtLevels,
  effectiveWritableByRoles,
  getConfigLevelInfo,
  getConfigTableForLevel,
  levelHasTable
} from "./chunk-B45MFSSL.mjs";

// src/init.ts
import {
  init as coreInit,
  createFetchExecutor
} from "@hopdrive/sdk-core";
import {
  createFetchExecutor as createFetchExecutor2,
  resolveExecutor,
  resetDefaultExecutor
} from "@hopdrive/sdk-core";
function formatKeyVersion(version) {
  if (!Number.isInteger(version) || version < 1) {
    throw new Error(`Invalid key version number: ${version}. Must be a positive integer.`);
  }
  return `v${version}`;
}
function createEncryptionKeys(keys) {
  const result = {};
  for (const [version, keyValue] of Object.entries(keys)) {
    const numericVersion = Number(version);
    if (!Number.isInteger(numericVersion) || numericVersion < 1) {
      throw new Error(`Invalid key version: ${version}. Keys must use positive integer versions.`);
    }
    result[formatKeyVersion(numericVersion)] = keyValue;
  }
  return result;
}
function createEncryptionConfig(config) {
  const formattedKeys = createEncryptionKeys(config.keys);
  let formattedMetadata;
  if (config.versionMetadata) {
    formattedMetadata = {};
    for (const [version, metadata] of Object.entries(config.versionMetadata)) {
      const numericVersion = Number(version);
      formattedMetadata[formatKeyVersion(numericVersion)] = metadata;
    }
  }
  return {
    keys: formattedKeys,
    preferredVersion: config.preferredVersion !== void 0 ? formatKeyVersion(config.preferredVersion) : void 0,
    versionMetadata: formattedMetadata
  };
}
var encryptionConfig = null;
var encryptionVersions = [];
function init(config) {
  if (config.executor && config.executorConfig) {
    throw new Error(
      'Provide either "executor" or "executorConfig", not both.'
    );
  }
  if (!config.executor && !config.executorConfig) {
    throw new Error(
      'Must provide either "executor" (pre-created) or "executorConfig" (to create one).'
    );
  }
  const executor = config.executor ?? createFetchExecutor(config.executorConfig);
  coreInit({ executor });
  if (config.encryption) {
    encryptionConfig = config.encryption;
    encryptionVersions = buildEncryptionVersions(config.encryption);
  } else {
    encryptionConfig = null;
    encryptionVersions = [];
  }
}
function buildEncryptionVersions(config) {
  const versions = [];
  for (const version of Object.keys(config.keys)) {
    const keyValue = config.keys[version];
    const metadata = config.versionMetadata?.[version];
    versions.push({
      version,
      envVar: metadata?.envVar,
      available: !!keyValue && keyValue.length > 0,
      status: metadata?.status ?? "active",
      description: metadata?.description,
      introducedAt: metadata?.introducedAt,
      deprecatedAt: metadata?.deprecatedAt
    });
  }
  return versions;
}
function getEncryptionVersions() {
  return [...encryptionVersions];
}
function getAvailableEncryptionVersions() {
  return encryptionVersions.filter((v) => v.available).map((v) => v.version);
}
function getActiveEncryptionVersions() {
  return encryptionVersions.filter((v) => v.status === "active" && v.available).map((v) => v.version);
}
function getDeprecatedEncryptionVersions() {
  return encryptionVersions.filter((v) => v.status === "deprecated" && v.available).map((v) => v.version);
}
function getPreferredEncryptionVersion() {
  if (!encryptionConfig) return null;
  if (encryptionConfig.preferredVersion) {
    const key = encryptionConfig.keys[encryptionConfig.preferredVersion];
    if (key && key.length > 0) {
      return encryptionConfig.preferredVersion;
    }
  }
  const active = getActiveEncryptionVersions();
  return active.length > 0 ? active[0] : null;
}
function getEncryptionVersionMetadata(version) {
  return encryptionVersions.find((v) => v.version === version) ?? null;
}
function getKeyForVersionInternal(version) {
  if (!encryptionConfig) return null;
  return encryptionConfig.keys[version] ?? null;
}
function resetEncryptionConfig() {
  encryptionConfig = null;
  encryptionVersions = [];
}

// ../../gql/operations.ts
import { resolveExecutor as resolveExecutor2 } from "@hopdrive/sdk-core";

// ../../gql/schema.ts
var GetContractConfigDocument = { "__text": "query GetContractConfig($contractId: bigint!, $includeMetadata: Boolean!) {\n  contracts_by_pk(id: $contractId) {\n    id\n    effective_config(args: {include_metadata: $includeMetadata})\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "GetContractConfig" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "contractId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "includeMetadata" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Boolean" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "contracts_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "contractId" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "effective_config" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "args" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "include_metadata" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "includeMetadata" } } }] } }] }] } }] } }] };
var GetCustomerConfigDocument = { "__text": "query GetCustomerConfig($customerId: bigint!, $includeMetadata: Boolean!) {\n  customers_by_pk(id: $customerId) {\n    id\n    effective_config(args: {include_metadata: $includeMetadata})\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "GetCustomerConfig" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "includeMetadata" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Boolean" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "customers_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "customerId" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "effective_config" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "args" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "include_metadata" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "includeMetadata" } } }] } }] }] } }] } }] };
var GetMoveConfigDocument = { "__text": "query GetMoveConfig($moveId: bigint!, $includeMetadata: Boolean!) {\n  moves_by_pk(id: $moveId) {\n    id\n    effective_config(args: {include_metadata: $includeMetadata})\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "GetMoveConfig" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "moveId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "includeMetadata" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Boolean" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "moves_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "moveId" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "effective_config" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "args" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "include_metadata" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "includeMetadata" } } }] } }] }] } }] } }] };
var GetOrganizationConfigDocument = { "__text": "query GetOrganizationConfig($organizationId: bigint!, $includeMetadata: Boolean!) {\n  organizations_by_pk(id: $organizationId) {\n    id\n    effective_config(args: {include_metadata: $includeMetadata})\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "GetOrganizationConfig" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "organizationId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "includeMetadata" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Boolean" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "organizations_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "organizationId" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "effective_config" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "args" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "include_metadata" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "includeMetadata" } } }] } }] }] } }] } }] };
var GetRegionConfigDocument = { "__text": "query GetRegionConfig($regionId: bigint!, $includeMetadata: Boolean!) {\n  regions_by_pk(id: $regionId) {\n    id\n    effective_config(args: {include_metadata: $includeMetadata})\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "GetRegionConfig" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "regionId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "bigint" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "includeMetadata" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Boolean" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "regions_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "regionId" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "effective_config" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "args" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "include_metadata" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "includeMetadata" } } }] } }] }] } }] } }] };
var GetWorkflowsetConfigDocument = { "__text": "query GetWorkflowsetConfig($workflowsetId: Int!, $includeMetadata: Boolean!) {\n  workflowsets_by_pk(id: $workflowsetId) {\n    id\n    effective_config(args: {include_metadata: $includeMetadata})\n  }\n}", "kind": "Document", "definitions": [{ "kind": "OperationDefinition", "operation": "query", "name": { "kind": "Name", "value": "GetWorkflowsetConfig" }, "variableDefinitions": [{ "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "workflowsetId" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Int" } } } }, { "kind": "VariableDefinition", "variable": { "kind": "Variable", "name": { "kind": "Name", "value": "includeMetadata" } }, "type": { "kind": "NonNullType", "type": { "kind": "NamedType", "name": { "kind": "Name", "value": "Boolean" } } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "workflowsets_by_pk" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "id" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "workflowsetId" } } }], "selectionSet": { "kind": "SelectionSet", "selections": [{ "kind": "Field", "name": { "kind": "Name", "value": "id" } }, { "kind": "Field", "name": { "kind": "Name", "value": "effective_config" }, "arguments": [{ "kind": "Argument", "name": { "kind": "Name", "value": "args" }, "value": { "kind": "ObjectValue", "fields": [{ "kind": "ObjectField", "name": { "kind": "Name", "value": "include_metadata" }, "value": { "kind": "Variable", "name": { "kind": "Name", "value": "includeMetadata" } } }] } }] }] } }] } }] };

// ../../gql/operations.ts
async function getContractConfig(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    GetContractConfigDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.contracts_by_pk;
}
async function getCustomerConfig(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    GetCustomerConfigDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.customers_by_pk;
}
async function getMoveConfig(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    GetMoveConfigDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.moves_by_pk;
}
async function getOrganizationConfig(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    GetOrganizationConfigDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.organizations_by_pk;
}
async function getRegionConfig(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    GetRegionConfigDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.regions_by_pk;
}
async function getWorkflowsetConfig(variables, options) {
  const { executor, raw, ...requestOptions } = options ?? {};
  const result = await resolveExecutor2(executor).query(
    GetWorkflowsetConfigDocument,
    variables,
    requestOptions
  );
  if (raw) return result;
  return result.workflowsets_by_pk;
}

// src/_generated/config.schema.json
var config_schema_default = {
  $schema: "http://json-schema.org/draft-07/schema#",
  $id: "https://hopdrive.com/generated/config.schema.json",
  title: "HopDrive Configuration Schema",
  description: "Configuration schema for HopDrive Organizations and Customers. Configs are merged hierarchically: Organization config provides defaults, Customer config overrides. Built from canonical definition modules.",
  type: "object",
  properties: {
    version: {
      type: "string",
      description: 'Configuration schema version for compatibility (e.g., "1.0.0").',
      "x-hopdrive-label": "Version",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup",
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "metadata"
    },
    api_enabled: {
      type: "boolean",
      description: "Activate/Deactivate the API feature for clients at this level. Currently, this is only a visual indicator of the client to show that they use the API. In the future, this switch may behave differently.",
      "x-hopdrive-label": "API Enabled",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "integrations"
    },
    api_config: {
      type: "object",
      properties: {
        hmac_shared_secret_1: {
          type: "string",
          description: "Primary HMAC shared secret for anonymous JWT handshake authentication"
        },
        hmac_shared_secret_2: {
          type: "string",
          description: "Secondary HMAC shared secret for key rotation support"
        },
        oauth_client_id: {
          type: "string",
          description: "OAuth Client ID for HopDrive REST API access"
        },
        oauth_client_secret: {
          type: "string",
          description: "OAuth Client Secret for HopDrive REST API access"
        }
      },
      additionalProperties: false,
      description: "Customer/organization API configuration including HMAC shared secrets for anonymous JWT authentication and OAuth credentials for HopDrive REST API access.",
      "x-hopdrive-label": "API Configuration",
      "x-hopdrive-writable-at-levels": [
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Contains sensitive authentication secrets. HMAC secrets and OAuth credentials must be encrypted before storage.",
      "x-hopdrive-category": "integrations"
    },
    price_estimates: {
      type: "boolean",
      description: "Activate/Deactivate the price estimates feature for this client. This will allow the client to see price estimates for their moves before they create them.",
      "x-hopdrive-label": "Activate Price Estimates",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    distance_estimates: {
      type: "boolean",
      description: "Activate/Deactivate the distance estimates feature for clients at this level. This will allow the client to see distance estimates for their moves before they create them.",
      "x-hopdrive-label": "Activate Distance Estimates",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    allow_email_notifications: {
      type: "boolean",
      description: "Select whether clients at this level prefer their users to receive email notifications when a move has been picked up or delivered successfully. If selected, the user who created the move, as well as an optional list of other users specified during move planning, will receive a notification via email.",
      "x-hopdrive-label": "Move Status Updates",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "notifications"
    },
    notify_contact_of_outcomes: {
      type: "boolean",
      description: "Select whether the move's pickup and delivery contacts should be cc'd on the email sent when an outcome on the move is resolved. The user who created the move is always notified.",
      "x-hopdrive-label": "Notify Contacts on Outcome Resolution",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "outcomes"
    },
    dealer_visibility_enabled: {
      type: "boolean",
      description: "Gates whether dealers see resolved move outcomes on the dealer-portal/embed read-only surface. This is a feature flag for a Part 2 (not-yet-shipped) dealer-facing outcomes surface.",
      "x-hopdrive-label": "Dealer Outcome Visibility",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "outcomes"
    },
    customer_feedback_recipients_fallback: {
      type: "array",
      items: {
        type: "string",
        format: "email"
      },
      description: "Explicit fallback email list used by the outcome-feedback recipient resolver when the move's contacts and the customer's contact list are both empty. Referenced by Part 1 Decision 21 (move_contacts_with_customer_fallback resolver).",
      "x-hopdrive-label": "Customer Feedback Recipients (Fallback)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "outcomes"
    },
    allow_triangle_moves: {
      type: "boolean",
      description: "Activate/Deactivate the triangle moves feature for clients at this level. Triangle moves allow for price adjustments to a ride-drive-ride bundle of moves.",
      "x-hopdrive-label": "Activate Triangle Moves",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "If you do not understand what this feature does, please leave it disabled.",
      "x-hopdrive-category": "move-planning"
    },
    enable_csv_import: {
      type: "boolean",
      description: "Activate/Deactivate the CSV import feature for customers in Portal. When enabled, the customer can import moves via CSV file upload.",
      "x-hopdrive-label": "Enable CSV Import",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    public_workflowsets: {
      type: "boolean",
      description: "LEGACY: Controls whether public workflowsets are publicly visible or not.",
      "x-hopdrive-label": "Show Public Workflowsets",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "workflows"
    },
    rideshare_calling_enabled: {
      type: "boolean",
      description: "Hopdrive Lite only. This setting will determine whether Lite clients at this level have opted to enable calling rideshares for their drivers.",
      "x-hopdrive-label": "Rideshare Calling Enabled",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "rideshare"
    },
    ride_provider_algorithm_key: {
      type: "string",
      description: "Select the algorithm when choosing between ride providers. Each algorithm has different selection criteria and logic.",
      "x-hopdrive-label": "Ride Provider Selection Algorithm",
      "x-hopdrive-writable-at-levels": [
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup",
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "rideshare"
    },
    passthrough_rideshare_surges: {
      type: "boolean",
      description: "Activate/Deactivate rideshare surge charges for this client. Surge charges are incurred when the actual rideshare cost is higher than anticipated. When active, the client will cover any rideshare surge charges related to their moves. When inactive, HopDrive will cover the extra cost and waive all rideshare surge charges for this client.",
      "x-hopdrive-label": "Surge Charge Accountability",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "pricing"
    },
    require_third_party_payer: {
      type: "boolean",
      description: "Require the client to input a third party payer when creating moves through the portal or API.",
      "x-hopdrive-label": "Require Third Party Payer",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "third-party-payer"
    },
    auto_assign_ref: {
      type: "boolean",
      description: "When clients at this level are creating a concierge return move, automatically assign its reference number to the related concierge pickup of this move.",
      "x-hopdrive-label": "Auto-Assign Ref",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "reference-number"
    },
    default_move_type: {
      type: "string",
      enum: [
        "concierge",
        "concierge-loaner",
        "one-way",
        "round-trip"
      ],
      description: "Default move type pre-selected for new moves. Users can change, but this sets initial selection.",
      "x-hopdrive-label": "Default Move Type",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    allowed_move_types: {
      type: "array",
      items: {
        type: "string",
        enum: [
          "concierge",
          "concierge-loaner",
          "one-way",
          "round-trip"
        ]
      },
      description: "Choose which move types are allowed for clients at this level in the move planner. More often than not, there is no reason to restrict a client unless they request/require it.",
      "x-hopdrive-label": "Allowed Move Types",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    allowed_templates: {
      type: "array",
      items: {
        type: "string"
      },
      description: "LEGACY - Allowed template identifiers. Restricts which move templates this customer can use.",
      "x-hopdrive-label": "Allowed Templates",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    add_moves_always: {
      type: "boolean",
      description: "Bypass the automatic deactivation of move planning. As mentioned in the Move Planning Activation option, move planning will deactivate automatically when a client goes over their move limit. This switch will override that behavior and allow the client to continue creating moves.",
      "x-hopdrive-label": "Bypass Auto-Deactivation",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "dealer-admin",
        "driver"
      ],
      "x-hopdrive-category": "move-planning"
    },
    sixt_settings: {
      type: "object",
      properties: {
        slackChannel: {
          type: "string",
          description: "Slack channel HopBot posts Sixt order intake outcomes to (handled / needs-decision / blocked), without the leading `#`. **HopBot must be invited to the channel or nothing posts.** Leave blank to use the integration's default channel (SLACK_CHANNEL). Organization level only \u2014 a customer-level value is not read."
        },
        customerMatchMaxMiles: {
          type: "number",
          exclusiveMinimum: 0,
          description: "Farthest a Sixt customer record can be from an order's pickup station (straight-line miles) and still be auto-assigned the batch's moves. The nearest candidate beyond this parks the job Blocked for dispatch. Leave blank to use the integration default (100). Organization level only \u2014 the match runs before any customer is known."
        },
        customerMatchTieMiles: {
          type: "number",
          minimum: 0,
          description: "When the two nearest Sixt customer records are within this many straight-line miles of each other the match is ambiguous and the job parks Blocked for dispatch. Leave blank to use the integration default (5). Organization level only \u2014 the match runs before any customer is known."
        },
        locationMatchMaxMiles: {
          type: "number",
          exclusiveMinimum: 0,
          description: "Farthest a seeded location may sit from an order's station (straight-line miles) and still be treated as that station. Stations are geocoded from their address, so a large site \u2014 an airport rental centre, an auction lot, a de-fleet yard \u2014 can place well over a mile from the stored coordinate and block the order as an unknown location. Leave blank to use the integration default (3). A customer-level value overrides the organization's for that customer's orders, since this match runs after the customer is known."
        },
        moveCreationMode: {
          type: "string",
          enum: [
            "off",
            "approval",
            "auto"
          ],
          description: 'How a validated Sixt order becomes HopDrive moves. `approval` (the default): the integration posts an Approve / Reject card to the Slack channel and creates the moves only when a dispatcher approves. `auto`: moves are created immediately, no human step. `off`: intake stops at a ready job and dispatch creates the moves by hand. When set, this replaces the legacy "Create moves automatically" switch. Customer-level values are honored like the switch was.'
        },
        moveCreationEnabled: {
          type: "boolean",
          description: "LEGACY \u2014 superseded by moveCreationMode (kept one release so existing values keep their meaning: on = auto, off = off). When on, a validated Sixt order immediately creates HopDrive moves (one per vehicle) under the nearest matched customer. When off (the default), intake stops at a ready job and dispatch creates the moves by hand. The organization switch is the master gate; once an order's customer is matched, a customer-level off holds that customer's orders at ready for manual dispatch."
        },
        moveRetryMaxAttempts: {
          type: "integer",
          exclusiveMinimum: 0,
          description: "How many times the watchdog retries creating a move for one vehicle before giving up and parking it for a human. Leave blank to use the integration default (5). A customer-level value overrides the organization's for that customer's orders."
        },
        moveRetryBaseSeconds: {
          type: "number",
          exclusiveMinimum: 0,
          description: "Base delay (seconds) for the watchdog's exponential backoff between move-creation retries \u2014 the wait after attempt N is base \xD7 2^(N-1). Leave blank to use the integration default (60). A customer-level value overrides the organization's for that customer's orders."
        },
        portalWritesEnabled: {
          type: "boolean",
          description: "Whether Approve and Reject on the Slack card also accept or decline the contract in the Sixt portal. Off unless set to true. This is one of two gates: the Sixt site must also have SIXT_PORTAL_WRITES=on, and with either one off the integration only records what it would have sent. Organization level only \u2014 a customer-level value is not read."
        }
      },
      additionalProperties: false,
      description: "Settings for the Sixt integration \u2014 the Slack channel its Dispatch Review cards post to, the match bounds move creation uses for customers and for stations, and the move-creation switch. Set at the organization level; a customer-level value applies to the move-creation switch, the retry settings and the station-match bound only, once an order's customer is matched.",
      "x-hopdrive-label": "Sixt Integration",
      "x-hopdrive-writable-at-levels": [
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "sixt"
    },
    add_moves_disabled: {
      type: "boolean",
      description: "Activate/Deactivate the move planning feature for this client. This will allow the client to create moves in our portal or API. NOTE: This switch will deactivate automatically when a client goes over their move limit. Please use the Bypass Auto-Deactivation option to override this behavior.",
      "x-hopdrive-label": "Move Planner Activation",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    cancellation_fee: {
      type: "number",
      minimum: 0,
      maximum: 1e3,
      description: "Amount to charge clients at this level for cancelling a move that has already been started.",
      "x-hopdrive-label": "Cancellation Fee",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "This does not apply to dry-run cancellations.",
      "x-hopdrive-category": "pricing"
    },
    delay_price_per_minute: {
      type: "number",
      minimum: 0,
      maximum: 1,
      description: "Amount to charge clients at this level per each minute our driver is delayed. This only applies when we choose to specifically charge clients at this level for the delay. Min: $0.00, Max: $1.00.",
      "x-hopdrive-label": "Delay Price Per Minute",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "pricing"
    },
    dry_run_behavior: {
      type: "string",
      enum: [
        "default-off",
        "default-on",
        "forced-off",
        "forced-on"
      ],
      description: "Choose the behavior around dry run cancellations through move outcome. When a dry run is applied to a move, this will charge the ar cost of a move to the customer for cancellations instead of using the cancellation fee.",
      "x-hopdrive-label": "Dry Run Behavior",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "pricing"
    },
    fuel_multiplier: {
      type: "number",
      minimum: 0,
      maximum: 3,
      description: "The multiplier used to calculate the fuel surcharge. This is a multiplier of the base fare. Min: 0.0, Max: 3.0.",
      "x-hopdrive-label": "Fuel Multiplier",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Does NOT affect clients with flat rate pricing.",
      "x-hopdrive-category": "pricing"
    },
    default_workflow_set_id: {
      type: "integer",
      exclusiveMinimum: 0,
      description: "The default workflow set is the preferred set of pickup and delivery workflows used for clients at this level.",
      "x-hopdrive-label": "Default Workflow Set",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "workflows"
    },
    pickup_workflow_id: {
      type: "integer",
      exclusiveMinimum: 0,
      description: "LEGACY: Workflow ID for pickup operations. Defines driver steps during vehicle pickup.",
      "x-hopdrive-label": "Pickup Workflow ID",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "workflows"
    },
    delivery_workflow_id: {
      type: "integer",
      exclusiveMinimum: 0,
      description: "LEGACY: Workflow ID for delivery operations. Defines driver steps during vehicle delivery.",
      "x-hopdrive-label": "Delivery Workflow ID",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "workflows"
    },
    default_pickup_workflow_id: {
      type: "integer",
      exclusiveMinimum: 0,
      description: "Default pickup workflow ID. Auto-populated from workflowset selection.",
      "x-hopdrive-label": "Default Pickup Workflow ID",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "workflows"
    },
    default_delivery_workflow_id: {
      type: "integer",
      exclusiveMinimum: 0,
      description: "Default delivery workflow ID. Auto-populated from workflowset selection.",
      "x-hopdrive-label": "Default Delivery Workflow ID",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "workflows"
    },
    move_workflow_overrides: {
      type: "object",
      properties: {
        operational: {
          type: "object",
          properties: {
            pickup_workflow_id: {
              type: "integer",
              exclusiveMinimum: 0,
              description: "Pickup workflow ID for this move role."
            },
            delivery_workflow_id: {
              type: "integer",
              exclusiveMinimum: 0,
              description: "Delivery workflow ID for this move role."
            }
          },
          additionalProperties: false,
          description: "Workflows used for operational moves."
        },
        valet_customer: {
          type: "object",
          properties: {
            pickup_workflow_id: {
              type: "integer",
              exclusiveMinimum: 0,
              description: "Pickup workflow ID for this move role."
            },
            delivery_workflow_id: {
              type: "integer",
              exclusiveMinimum: 0,
              description: "Delivery workflow ID for this move role."
            }
          },
          additionalProperties: false,
          description: "Workflows used for the customer vehicle in a valet move."
        },
        valet_loaner: {
          type: "object",
          properties: {
            pickup_workflow_id: {
              type: "integer",
              exclusiveMinimum: 0,
              description: "Pickup workflow ID for this move role."
            },
            delivery_workflow_id: {
              type: "integer",
              exclusiveMinimum: 0,
              description: "Delivery workflow ID for this move role."
            }
          },
          additionalProperties: false,
          description: "Workflows used for the loaner vehicle in a valet move."
        }
      },
      additionalProperties: false,
      description: "Override the workflowset pickup and delivery workflows for operational, valet customer, or valet loaner moves.",
      "x-hopdrive-label": "Move Workflow Overrides (Preview)",
      "x-hopdrive-writable-at-levels": [
        "workflowset"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver",
        "dealer-admin",
        "dealer-super-admin",
        "dispatcher"
      ],
      "x-hopdrive-category": "workflows"
    },
    default_payer: {
      anyOf: [
        {
          type: "integer",
          exclusiveMinimum: 0
        },
        {
          type: "null"
        }
      ],
      description: "The default payer for moves created through the portal or API. For now, we have to select this from the full list of customers. Please be sure you have selected the correct customer before you save. To remove the default payer, select 'None'.",
      "x-hopdrive-label": "Default Payer",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "third-party-payer"
    },
    reference_num_label: {
      type: "string",
      description: 'A client-specific label to display for the reference number field (e.g. "RO Number", "Claim Number", "Fleet ID", etc.).',
      "x-hopdrive-label": "Reference ID Label",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "reference-number"
    },
    reference_num_required_for_pickup: {
      type: "boolean",
      description: "Require the reference number to be entered for concierge pickup moves.",
      "x-hopdrive-label": "Ref Required for Concierge Pickup",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "reference-number"
    },
    reference_num_required_for_return: {
      type: "boolean",
      description: "Require the reference number to be entered for concierge return moves.",
      "x-hopdrive-label": "Ref Required for Concierge Return",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "reference-number"
    },
    reference_num_report_enabled: {
      type: "boolean",
      description: "Enable the missing reference number report feature for clients at this level.",
      "x-hopdrive-label": "Missing Ref Report Enabled",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "reference-number"
    },
    reference_num_report_label: {
      type: "string",
      description: "A client-specific label to display for the missing reference number report.",
      "x-hopdrive-label": "Missing Ref Report Label",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "reference-number"
    },
    reporting: {
      type: "object",
      properties: {
        enabled: {
          type: "boolean",
          description: "Master gate for the reporting dashboard. When false the dashboard is hidden entirely."
        },
        tabs: {
          type: "object",
          properties: {
            operations: {
              type: "object",
              properties: {
                enabled: {
                  type: "boolean",
                  description: "Show the Operations Overview tab"
                },
                metrics: {
                  type: "object",
                  properties: {
                    total_moves: {
                      type: "boolean"
                    },
                    completed: {
                      type: "boolean"
                    },
                    pending: {
                      type: "boolean"
                    },
                    canceled: {
                      type: "boolean"
                    },
                    on_time_pct: {
                      type: "boolean"
                    },
                    avg_distance: {
                      type: "boolean"
                    },
                    active_drivers: {
                      type: "boolean"
                    },
                    moves_per_driver: {
                      type: "boolean"
                    },
                    repeat_customers: {
                      type: "boolean"
                    },
                    missing_ref: {
                      type: "boolean"
                    },
                    rideshares_utilized: {
                      type: "boolean"
                    }
                  },
                  additionalProperties: false,
                  description: "Per-metric-card visibility on the Operations Overview tab"
                },
                charts: {
                  type: "object",
                  properties: {
                    move_volume: {
                      type: "boolean"
                    },
                    status_distribution: {
                      type: "boolean"
                    },
                    moves_by_weekday: {
                      type: "boolean"
                    },
                    moves_by_hour: {
                      type: "boolean"
                    },
                    driver_activity: {
                      type: "boolean"
                    },
                    distance_distribution: {
                      type: "boolean"
                    }
                  },
                  additionalProperties: false,
                  description: "Per-chart visibility on the Operations Overview tab"
                }
              },
              additionalProperties: false
            },
            appointments: {
              type: "object",
              properties: {
                enabled: {
                  type: "boolean",
                  description: "Show the Appointment Usage tab"
                },
                metrics: {
                  type: "object",
                  properties: {
                    est_utilization: {
                      type: "boolean"
                    },
                    upcoming_appts: {
                      type: "boolean"
                    },
                    completed_appts: {
                      type: "boolean"
                    },
                    canceled_appts: {
                      type: "boolean"
                    },
                    configured_drivers: {
                      type: "boolean"
                    },
                    avg_moves_per_driver: {
                      type: "boolean"
                    },
                    unused_slots: {
                      type: "boolean"
                    },
                    avg_move_duration: {
                      type: "boolean"
                    }
                  },
                  additionalProperties: false,
                  description: "Per-metric-card visibility on the Appointment Usage tab"
                },
                charts: {
                  type: "object",
                  properties: {
                    daily_utilization: {
                      type: "boolean"
                    },
                    booked_vs_capacity: {
                      type: "boolean"
                    },
                    appt_volume_by_hour: {
                      type: "boolean"
                    },
                    driver_activity: {
                      type: "boolean"
                    }
                  },
                  additionalProperties: false,
                  description: "Per-chart visibility on the Appointment Usage tab"
                }
              },
              additionalProperties: false
            },
            executive: {
              type: "object",
              properties: {
                enabled: {
                  type: "boolean",
                  description: "Show the Executive KPIs tab"
                },
                kpis: {
                  type: "object",
                  properties: {
                    trips_assigned: {
                      type: "boolean"
                    },
                    trips_completed: {
                      type: "boolean"
                    },
                    trips_validated: {
                      type: "boolean"
                    }
                  },
                  additionalProperties: false,
                  description: "Per-KPI-gauge visibility on the Executive KPIs tab"
                },
                charts: {
                  type: "object",
                  properties: {
                    kpi_trend: {
                      type: "boolean"
                    },
                    validation_breakdown: {
                      type: "boolean"
                    }
                  },
                  additionalProperties: false,
                  description: "Per-chart visibility on the Executive KPIs tab"
                }
              },
              additionalProperties: false
            }
          },
          additionalProperties: false,
          description: "Per-tab and per-module visibility for the reporting dashboard"
        }
      },
      additionalProperties: false,
      description: "Controls the customer-facing reporting dashboard in the dealer portal: a master on/off gate plus per-tab and per-module visibility flags.",
      "x-hopdrive-label": "Reporting",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "This config is replaced wholesale at each config level (shallow top-level merge), not deep-merged. A level that sets `reporting` must store the complete object \u2014 partial overrides at a higher level discard the lower level entirely.",
      "x-hopdrive-category": "reporting"
    },
    serviceable_radius_miles: {
      type: [
        "number",
        "string"
      ],
      description: "Used to validate move eligibility within the radius for clients who pre-verify using our API. For more information, see our API documentation here.",
      "x-hopdrive-label": "HopDrive Service Radius",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "If set, must enable pickup or delivery verification.",
      "x-hopdrive-category": "service-radius"
    },
    customer_serviceable_radius_miles: {
      type: "number",
      minimum: 0,
      description: "Used to validate move eligibility within the radius for clients who use our HopDrive Lite tier. Regions do not apply to these customers as they do not utilize our driver network.",
      "x-hopdrive-label": "Customer Serviceable Radius Miles",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "service-radius"
    },
    serviceable_radius_by_delivery: {
      type: "boolean",
      description: "Toggle on to verify that the delivery address is within the HopDrive Service Radius.",
      "x-hopdrive-label": "Delivery Verification for HopDrive Service Radius",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "service-radius"
    },
    serviceable_radius_by_pickup: {
      type: "boolean",
      description: "Toggle on to verify that the pickup address is within the HopDrive Service Radius.",
      "x-hopdrive-label": "Pickup Verification for HopDrive Service Radius",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "service-radius"
    },
    ride_type_override: {
      type: "string",
      enum: [
        "",
        "lyft",
        "uber",
        "shuttle"
      ],
      description: "Select the preferred rideshare service for clients at this level. When planning an 'auto' ride, this partner will be selected by default",
      "x-hopdrive-label": "Preferred Ride Partner",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "rideshare"
    },
    branding: {
      type: "object",
      properties: {
        active: {
          type: "boolean",
          description: "Activate/Deactivate the branding feature for clients at this level. Branded clients will be visible to their customers not as HopDrive, but as the client's own brand."
        },
        name: {
          type: "string",
          description: "Name to be displayed to the client's customers."
        },
        primary_color: {
          anyOf: [
            {
              anyOf: [
                {
                  not: {}
                },
                {
                  anyOf: [
                    {
                      type: "string",
                      pattern: "^#[0-9A-Fa-f]{6}$",
                      description: 'Primary brand color in hex format (e.g., "#0d5298"). Used for core UI elements with emphasis.'
                    },
                    {
                      type: "null"
                    }
                  ],
                  description: 'Primary brand color in hex format (e.g., "#0d5298"). Used for core UI elements with emphasis.'
                }
              ],
              description: 'Primary brand color in hex format (e.g., "#0d5298"). Used for core UI elements with emphasis.'
            },
            {
              type: "string",
              const: ""
            }
          ],
          description: 'Primary brand color in hex format (e.g., "#0d5298"). Used for core UI elements with emphasis.'
        },
        secondary_color: {
          anyOf: [
            {
              anyOf: [
                {
                  not: {}
                },
                {
                  anyOf: [
                    {
                      type: "string",
                      pattern: "^#[0-9A-Fa-f]{6}$",
                      description: "Secondary/accent brand color in hex format. Used for secondary UI elements and highlights."
                    },
                    {
                      type: "null"
                    }
                  ],
                  description: "Secondary/accent brand color in hex format. Used for secondary UI elements and highlights."
                }
              ],
              description: "Secondary/accent brand color in hex format. Used for secondary UI elements and highlights."
            },
            {
              type: "string",
              const: ""
            }
          ],
          description: "Secondary/accent brand color in hex format. Used for secondary UI elements and highlights."
        },
        backdrop_color: {
          anyOf: [
            {
              anyOf: [
                {
                  not: {}
                },
                {
                  anyOf: [
                    {
                      type: "string",
                      pattern: "^#[0-9A-Fa-f]{6}$",
                      description: "Background color for logo area in hex format. Be sure to contrast this to the logo for visibility."
                    },
                    {
                      type: "null"
                    }
                  ],
                  description: "Background color for logo area in hex format. Be sure to contrast this to the logo for visibility."
                }
              ],
              description: "Background color for logo area in hex format. Be sure to contrast this to the logo for visibility."
            },
            {
              type: "string",
              const: ""
            }
          ],
          description: "Background color for logo area in hex format. Be sure to contrast this to the logo for visibility."
        },
        logo_url: {
          anyOf: [
            {
              anyOf: [
                {
                  not: {}
                },
                {
                  type: "string",
                  format: "uri",
                  description: "The logo is a link to a PNG or SVG file to give clients at this level some imagery to go along with their name & colors. Due to limitations, the maximum file size is 0.5MB/500KB. Please also make sure the image dimensions are at least 40px tall as anything lower will be too low of a resolution. Stored in hopdrive-branding-logos S3 bucket."
                }
              ],
              description: "The logo is a link to a PNG or SVG file to give clients at this level some imagery to go along with their name & colors. Due to limitations, the maximum file size is 0.5MB/500KB. Please also make sure the image dimensions are at least 40px tall as anything lower will be too low of a resolution. Stored in hopdrive-branding-logos S3 bucket."
            },
            {
              type: "string",
              const: ""
            }
          ],
          description: "The logo is a link to a PNG or SVG file to give clients at this level some imagery to go along with their name & colors. Due to limitations, the maximum file size is 0.5MB/500KB. Please also make sure the image dimensions are at least 40px tall as anything lower will be too low of a resolution. Stored in hopdrive-branding-logos S3 bucket."
        },
        customer_support_phone_number: {
          type: "string",
          description: "Customer support phone for HopDrive Lite tier ONLY. Displayed in concierge SMS to consumers. Not applicable to Standard tier."
        }
      },
      additionalProperties: false,
      description: "The branding module is a way for us to white-label our clients. White-labeling is a way to display our clients in a unique way compared to the HopDrive default branding.",
      "x-hopdrive-label": "Branding",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Only available to organizations. Customer branding inherits from parent org.",
      "x-hopdrive-category": "branding"
    },
    appraisals: {
      type: "object",
      properties: {
        active: {
          type: "boolean",
          description: 'Deprecated (card #2346). Appraisals is a product: an active contract carrying a trigger with product_key "appraisals" is what grants it, and nothing reads this flag to decide whether a customer has Appraisals. Kept so stored config rows still render and stay editable. Use Add Product on the contract instead.'
        },
        active_timestamps: {
          type: "array",
          items: {
            type: "object",
            properties: {
              from: {
                type: "string",
                format: "date-time",
                description: 'Start date in ISO 8601 format (e.g., "2025-01-01T00:00:00Z")'
              },
              to: {
                anyOf: [
                  {
                    type: "string",
                    format: "date-time"
                  },
                  {
                    type: "string",
                    const: "now"
                  }
                ],
                description: 'End date in ISO 8601 format, or "now" to indicate currently active'
              }
            },
            additionalProperties: false,
            description: 'Timestamp range for appraisal activation. Toggle on sets "from" to month start; toggle off sets "to" to month end.'
          },
          description: "Deprecated (card #2346). These windows bounded activation when the flag above was the entitlement. A contract carries its own begin and end dates now, and the product service anchors history on the expired contract and the latest appraisal instead. Kept so stored config rows still render."
        },
        allowed_move_types: {
          type: "array",
          items: {
            type: "string",
            enum: [
              "concierge",
              "operations"
            ]
          },
          description: "Move types that support appraisals. Only these types will have appraisal functionality. Generally, we should allow all move types unless specified in the client contract."
        },
        level: {
          type: "integer",
          minimum: 0,
          description: "Appraisal level/tier (0 = default). Higher levels may unlock additional features."
        }
      },
      additionalProperties: false,
      description: "The appraisals module is a way for our clients to appraise vehicles right off the lot. This system is powered by the Blackbook API and allows the client to build their own appraisals based on vehicle trim and condition.",
      "x-hopdrive-label": "Appraisals",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "dealer-admin",
        "dealer-super-admin"
      ],
      "x-hopdrive-writable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Requires premium subscription. Check client contract before activating.",
      "x-hopdrive-category": "appraisals"
    },
    appointments: {
      type: "object",
      properties: {
        enabled: {
          type: "boolean",
          description: "LEGACY: Enable/disable the appointments feature for clients at this level. When enabled, the appointments feature will be available in the Portal."
        },
        ref_num_label: {
          type: "string",
          description: "LEGACY: Reference number label within the appointments feature."
        },
        notificationEmail: {
          type: "string",
          format: "email",
          description: "LEGACY: Email address that receives appointment notifications and alerts."
        }
      },
      additionalProperties: false,
      description: "LEGACY: Appointments feature config. For new scheduling, see Appointment Management.",
      "x-hopdrive-label": "Appointments",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "appointments"
    },
    appointment_management: {
      type: "object",
      properties: {
        enabled: {
          type: "boolean",
          description: "Enable or disable appointment management features for clients at this level. When enabled, users can view the timeline in Portal and manage driver schedules. When disabled, all appointment-related functionality will be inactive."
        },
        full_planning_enabled: {
          type: "boolean",
          description: "Enable full planning and time suggestions for appointment management. When enabled, customers can take advantage of all planning features and intelligent time slot recommendations offered by the appointment management system. Note: Appointment Management must be enabled first."
        },
        strategy: {
          type: "string",
          description: "Select the algorithm to use for appointment scheduling. Each algorithm has different scheduling criteria and driver utilization logic."
        },
        driver_count: {
          type: "integer",
          exclusiveMinimum: 0,
          description: "For Gig customers only: Expected number of drivers typically available for this rooftop. Used by the algorithm to calculate capacity and suggest appropriate appointment times. For SaaS customers, the system reads from their actual drivers."
        },
        move_buffer_minutes: {
          type: "integer",
          minimum: 0,
          description: "Minimum time gap (in minutes) required between consecutive moves. Prevents back-to-back scheduling that doesn't account for traffic delays, customer interaction time, and vehicle inspection/handoff. Example: 40 minutes ensures drivers have breathing room between appointments."
        },
        enforcement_mode: {
          type: "string",
          enum: [
            "suggestion",
            "forced"
          ],
          description: 'Controls whether suggested times are recommendations or hard constraints. "Suggestion" displays suggested times but allows dealer to override. "Forced" only allows suggested times to be selectable, preventing submission of non-compliant appointment times.'
        },
        offset_max_minutes: {
          type: "integer",
          minimum: 0,
          description: "Maximum time deviation (in minutes) a dealer can adjust from a suggested appointment time. Controls how far dealers can shift appointments from algorithm-suggested slots. Example: 30-minute offset with 8:00 AM suggested slot allows 7:30 AM (30 minutes earlier), 8:00 AM (suggested time), or 8:30 AM (30 minutes later)."
        },
        offset_increment_minutes: {
          type: "integer",
          description: "Defines the time increment (in minutes) for allowable offset adjustments. Prevents arbitrary times like 8:12 AM or 8:37 AM that complicate scheduling. Works in conjunction with Offset Max to control available override options. Example: 15-minute increments allow 8:00, 8:15, 8:30, 8:45, etc."
        }
      },
      additionalProperties: false,
      description: "Configure how appointment times are calculated, presented, and enforced for dealers. Control driver utilization algorithms, time buffers, enforcement modes, and allowable scheduling adjustments.",
      "x-hopdrive-label": "Appointment Management",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "appointment-management"
    },
    hopdrive_contacts: {
      type: "object",
      properties: {
        account_manager: {
          anyOf: [
            {
              type: "integer",
              description: "Account manager user ID. Primary point of contact for day-to-day account needs."
            },
            {
              type: "null"
            }
          ],
          description: "Account manager user ID. Primary point of contact for day-to-day account needs."
        },
        ar_rep: {
          anyOf: [
            {
              type: "integer",
              description: "Accounts Receivable representative user ID. Handles billing inquiries and payment issues."
            },
            {
              type: "null"
            }
          ],
          description: "Accounts Receivable representative user ID. Handles billing inquiries and payment issues."
        },
        sales_rep: {
          anyOf: [
            {
              type: "integer",
              description: "Sales representative user ID. Primary contact for upselling and account expansion."
            },
            {
              type: "null"
            }
          ],
          description: "Sales representative user ID. Primary contact for upselling and account expansion."
        },
        market_manager: {
          anyOf: [
            {
              type: "integer",
              description: "Market manager user ID. Oversees regional operations and driver relationships."
            },
            {
              type: "null"
            }
          ],
          description: "Market manager user ID. Oversees regional operations and driver relationships."
        }
      },
      additionalProperties: false,
      description: "HopDrive employee assignments. Internal team members responsible for this account relationship.",
      "x-hopdrive-label": "HopDrive Contacts",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "hopdrive-contacts"
    },
    customer_values: {
      type: "object",
      properties: {
        program: {
          type: "string",
          description: 'Program name identifier (e.g., "GMA")'
        },
        dealer_code: {
          type: "string",
          description: "The dealer's own public dealer code (e.g., OEM-stamped code). For external-system surrogate IDs use the per-integration keys below."
        },
        billing_program: {
          type: "string",
          description: "Billing program identifier for invoicing categorization"
        },
        gma_market: {
          type: "string",
          description: 'GMA market code (e.g., "GX4", "GC2")'
        },
        gma_region: {
          type: "string",
          description: 'GMA region code (e.g., "SC", "EA", "WE")'
        },
        gma_program: {
          type: "string",
          description: "GMA program name for reporting"
        },
        gma_dealer_cd: {
          type: "string",
          description: 'GMA dealer code (e.g., "TX712")'
        },
        gma_retailer_name: {
          type: "string",
          description: "GMA retailer display name for reports"
        },
        dealerware_dealership_id: {
          type: "string",
          description: `Dealerware's internal dealership identifier for this rooftop (e.g., "4259"). Used by the Dealerware connector to route API requests; set during onboarding.`
        },
        spark_dealership_id: {
          type: "string",
          description: "Spark's internal dealership UUID for this rooftop. Used by sparkserv-integration to map inbound Spark webhooks to a HopDrive customer; set during onboarding."
        }
      },
      additionalProperties: true,
      description: "Custom key-value data for reporting and integrations. Includes external-system surrogate IDs (e.g., Spark, Dealerware, GMA) plus arbitrary passthrough keys. Keys are auto-formatted: lowercase with spaces to underscores.",
      "x-hopdrive-label": "Customer Values",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup",
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "custom-settings"
    },
    fuel: {
      type: "object",
      properties: {
        fuel_level: {
          type: "number",
          minimum: 0,
          maximum: 1,
          description: "The expected fuel level at vehicle delivery (in percent)."
        },
        min_fuel_price: {
          type: "number",
          minimum: 0,
          description: "Minimum fuel spend in dollars. Amounts below this will NOT be authorized. Prevents trivial purchases."
        },
        max_fuel_price: {
          type: "number",
          minimum: 0,
          description: "Maximum fuel spend in dollars. Amounts above this will NOT be authorized at pump. Prevents excessive purchases."
        },
        min_fuel_spend: {
          type: "number",
          minimum: 0,
          description: "LEGACY: Alternative minimum fuel spend threshold. DEPRECATED: Use min_fuel_price instead. Still used in fuel-sdk, event-handlers, hasura-migrations. At least one production customer (ID 3) still uses this field. Both min_fuel_spend and min_fuel_price are supported for backwards compatibility."
        },
        max_fuel_spend: {
          type: "number",
          minimum: 0,
          description: "LEGACY: Alternative maximum fuel spend cap. DEPRECATED: Use max_fuel_price instead. Still used in fuel-sdk, event-handlers, hasura-migrations. Both max_fuel_spend and max_fuel_price are supported for backwards compatibility."
        }
      },
      additionalProperties: false,
      description: "Fuel reimbursement and authorization configuration. Controls driver fuel spending limits and required fuel levels.",
      "x-hopdrive-label": "Fuel",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "fuel"
    },
    fraud_audit: {
      type: "object",
      properties: {
        enabled: {
          type: "boolean",
          description: "Turn on fraud audit monitoring to detect potential fraud cases. This will flag any trips that are outside the configured distance and time tolerances."
        },
        time_tolerance: {
          type: "integer",
          minimum: 0,
          maximum: 120,
          description: "Maximum allowed time deviation (in minutes) before flagging as potentially fraudulent behavior."
        },
        distance_tolerance: {
          type: "integer",
          minimum: 0,
          maximum: 50,
          description: "Maximum allowed distance deviation (in miles) before flagging as potentially fraudulent behavior."
        }
      },
      additionalProperties: false,
      description: "Fraud audit settings help detect potential fraud cases. This will flag any trips that are outside the configured distance and time tolerances.",
      "x-hopdrive-label": "Fraud Audit",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "fraud-audit"
    },
    gps_transition_mode: {
      type: "string",
      enum: [
        "shadow",
        "live"
      ],
      description: "How the server treats GPS-proposed move status transitions. In shadow mode every proposal is evaluated and logged to gps_transition_log but the move is never changed; in live mode accepted proposals update the move status and timestamp. Leave on shadow until the comparison data shows the engine agrees with driver taps.",
      "x-hopdrive-label": "GPS Transition Mode",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-move-status"
    },
    arrival_policy_arrive: {
      anyOf: [
        {
          type: "object",
          properties: {
            mode: {
              type: "string",
              const: "automaticWithManual"
            }
          },
          required: [
            "mode"
          ],
          additionalProperties: false,
          description: "Automatic arrival with the manual control always available (driver app 3 default)."
        },
        {
          type: "object",
          properties: {
            mode: {
              type: "string",
              const: "automaticOnly"
            },
            acceptsStrandingWhenSensorFails: {
              type: "boolean",
              const: true,
              description: "Required acknowledgement: with a dead fence or no permission the driver has no manual control."
            }
          },
          required: [
            "mode",
            "acceptsStrandingWhenSensorFails"
          ],
          additionalProperties: false,
          description: "GPS enforcement: the manual control never appears. Driver app 4 only; driver app 3 runs this as automaticWithManual."
        },
        {
          type: "object",
          properties: {
            mode: {
              type: "string",
              const: "manualOnly"
            }
          },
          required: [
            "mode"
          ],
          additionalProperties: false,
          description: "Automation off. Crossings are still detected and logged but never move the workflow."
        },
        {
          type: "object",
          properties: {
            mode: {
              type: "string",
              const: "automaticWithDelayedManual"
            },
            manualDelayMs: {
              type: "integer",
              exclusiveMinimum: 0,
              description: "Milliseconds before the manual control opens. No default; zero is automaticWithManual."
            }
          },
          required: [
            "mode",
            "manualDelayMs"
          ],
          additionalProperties: false,
          description: "Automation first, manual as a fallback after the delay. Driver app 4 only; driver app 3 runs this as automaticWithManual."
        }
      ],
      description: 'How a driver arrives at a stop (pickup arrived, delivery arrived). The GPS transition engine applies a corroborated arrival only when this mode allows automation. One of four modes. automaticWithManual (default): GPS advances the move and the driver can always advance it too. automaticOnly: GPS enforcement, no manual control; requires "acceptsStrandingWhenSensorFails": true in the same block. manualOnly: GPS crossings are logged but never move the workflow. automaticWithDelayedManual: manual control opens after "manualDelayMs" (required, positive). A block that fails validation falls through to the level below. Driver app 3 enforces automaticWithManual and manualOnly only; it runs automaticOnly and automaticWithDelayedManual as automaticWithManual and logs move.arrival_policy.degraded.',
      "x-hopdrive-label": "Arrival Policy: Arrive",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-move-status"
    },
    arrival_policy_depart: {
      anyOf: [
        {
          type: "object",
          properties: {
            mode: {
              type: "string",
              const: "automaticWithManual"
            }
          },
          required: [
            "mode"
          ],
          additionalProperties: false,
          description: "Automatic arrival with the manual control always available (driver app 3 default)."
        },
        {
          type: "object",
          properties: {
            mode: {
              type: "string",
              const: "automaticOnly"
            },
            acceptsStrandingWhenSensorFails: {
              type: "boolean",
              const: true,
              description: "Required acknowledgement: with a dead fence or no permission the driver has no manual control."
            }
          },
          required: [
            "mode",
            "acceptsStrandingWhenSensorFails"
          ],
          additionalProperties: false,
          description: "GPS enforcement: the manual control never appears. Driver app 4 only; driver app 3 runs this as automaticWithManual."
        },
        {
          type: "object",
          properties: {
            mode: {
              type: "string",
              const: "manualOnly"
            }
          },
          required: [
            "mode"
          ],
          additionalProperties: false,
          description: "Automation off. Crossings are still detected and logged but never move the workflow."
        },
        {
          type: "object",
          properties: {
            mode: {
              type: "string",
              const: "automaticWithDelayedManual"
            },
            manualDelayMs: {
              type: "integer",
              exclusiveMinimum: 0,
              description: "Milliseconds before the manual control opens. No default; zero is automaticWithManual."
            }
          },
          required: [
            "mode",
            "manualDelayMs"
          ],
          additionalProperties: false,
          description: "Automation first, manual as a fallback after the delay. Driver app 4 only; driver app 3 runs this as automaticWithManual."
        }
      ],
      description: 'How a driver departs a stop. Independent of the arrive policy. For drive-service moves the GPS transition engine never applies a departure regardless of mode, because pickup successful also means the inspection and VIN capture are done; the mode is still recorded with every departure decision. One of four modes. automaticWithManual (default): GPS advances the move and the driver can always advance it too. automaticOnly: GPS enforcement, no manual control; requires "acceptsStrandingWhenSensorFails": true in the same block. manualOnly: GPS crossings are logged but never move the workflow. automaticWithDelayedManual: manual control opens after "manualDelayMs" (required, positive). A block that fails validation falls through to the level below. Driver app 3 enforces automaticWithManual and manualOnly only; it runs automaticOnly and automaticWithDelayedManual as automaticWithManual and logs move.arrival_policy.degraded.',
      "x-hopdrive-label": "Arrival Policy: Depart",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-move-status"
    },
    pickup_start_gate: {
      type: "string",
      enum: [
        "off",
        "soft",
        "force"
      ],
      description: "Whether the driver app gates Start Pickup on the pickup geofence. off: no gate, the Start control is always available. soft: outside the fence the app shows Head to Pickup with the distance; the driver can start anyway immediately, and the start is logged as a force start. force: as soft, but starting anyway requires a countdown and an acknowledgement. Force starts are logged as move.pickup.force_started either way.",
      "x-hopdrive-label": "Pickup Start Gate",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-move-status"
    },
    gps_optimistic_arrivals: {
      type: "boolean",
      description: "Whether the driver app advances a move to pickup arrived / delivery arrived on its own when the phone enters the stop geofence, instead of waiting for the driver to swipe. Arrivals only; the departure from pickup has its own key (gps_optimistic_departures) and every other step stays with the driver. Respects arrival_policy_arrive (manualOnly disables it). The server ignores this key: its transition engine still evaluates and logs the same crossing under gps_transition_mode, so the shadow report can compare the two.",
      "x-hopdrive-label": "Optimistic GPS Arrivals",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-move-status"
    },
    gps_optimistic_departures: {
      type: "boolean",
      description: "Whether the driver app advances a move from pickup successful to delivery started on its own when the phone leaves the pickup geofence, instead of waiting for the driver to swipe Start Delivery. Departure from pickup only; the pickup inspection still sets pickup successful. Respects arrival_policy_depart (manualOnly disables it). The server ignores this key: its transition engine still evaluates and logs the same crossing under gps_transition_mode.",
      "x-hopdrive-label": "Optimistic GPS Departures",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-move-status"
    },
    geofence_default_radius_meters: {
      type: "integer",
      minimum: 50,
      maximum: 5e3,
      description: "Radius in meters of the circular geofence used around a pickup or delivery location when the location has no custom geofence radius or polygon.",
      "x-hopdrive-label": "Geofence Default Radius (m)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-geofence-evidence"
    },
    geofence_corroboration_window_minutes: {
      type: "integer",
      minimum: 1,
      maximum: 60,
      description: "How many minutes of GPS pings before a proposed transition the server looks at when checking that the driver really was inside (or outside) the geofence.",
      "x-hopdrive-label": "Geofence Corroboration Window (min)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-geofence-evidence"
    },
    geofence_corroboration_min_pings: {
      type: "integer",
      minimum: 1,
      maximum: 20,
      description: "Minimum number of recent GPS pings that must fall inside the target geofence (outside it for a departure) before the server accepts a GPS-proposed transition.",
      "x-hopdrive-label": "Geofence Corroboration Minimum Pings",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-geofence-evidence"
    },
    geofence_corroboration_accuracy_cutoff_meters: {
      type: "integer",
      minimum: 10,
      maximum: 1e3,
      description: "GPS pings whose reported accuracy is worse than this many meters are ignored when the server corroborates a transition. Server-side geofence evidence only; the driver app's route monitoring has its own cutoff (route_gps_accuracy_cutoff).",
      "x-hopdrive-label": "Geofence Corroboration Accuracy Cutoff (m)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-geofence-evidence"
    },
    geofence_corroboration_drift_buffer_meters: {
      type: "integer",
      minimum: 0,
      maximum: 1e3,
      description: "Extra meters added to a circular geofence radius when corroborating a transition, to absorb GPS drift. Polygon geofences are checked exactly.",
      "x-hopdrive-label": "Geofence Corroboration Drift Buffer (m)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-geofence-evidence"
    },
    gps_stale_pickup_started_minutes: {
      type: "integer",
      minimum: 5,
      maximum: 1440,
      description: "Minutes a move may sit in pickup started before the stale-move cron evaluates its GPS trail for a missed arrival.",
      "x-hopdrive-label": "Stale Threshold: Pickup Started (min)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-stale-moves"
    },
    gps_stale_pickup_arrived_minutes: {
      type: "integer",
      minimum: 5,
      maximum: 1440,
      description: "Minutes a move may sit in pickup arrived before the stale-move cron evaluates its GPS trail for a missed departure.",
      "x-hopdrive-label": "Stale Threshold: Pickup Arrived (min)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-stale-moves"
    },
    gps_stale_pickup_successful_minutes: {
      type: "integer",
      minimum: 5,
      maximum: 1440,
      description: "Minutes a move may sit in pickup successful before the stale-move cron evaluates its GPS trail for a missed delivery start or arrival.",
      "x-hopdrive-label": "Stale Threshold: Pickup Successful (min)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-stale-moves"
    },
    gps_stale_delivery_started_minutes: {
      type: "integer",
      minimum: 5,
      maximum: 1440,
      description: "Minutes a move may sit in delivery started before the stale-move cron evaluates its GPS trail for a missed arrival at delivery.",
      "x-hopdrive-label": "Stale Threshold: Delivery Started (min)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-stale-moves"
    },
    gps_stale_delivery_arrived_minutes: {
      type: "integer",
      minimum: 5,
      maximum: 1440,
      description: "Minutes a move may sit in delivery arrived before the stale-move cron flags it for review.",
      "x-hopdrive-label": "Stale Threshold: Delivery Arrived (min)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "gps-stale-moves"
    },
    route_corridor_off_route: {
      type: "integer",
      minimum: 10,
      maximum: 1e3,
      description: "Distance in meters from the planned route polyline beyond which the driver app records a route deviation.",
      "x-hopdrive-label": "Route Corridor: Off-Route Distance (m)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "route-monitoring"
    },
    route_deviation_alerts: {
      type: "boolean",
      description: "Whether the driver app alerts the driver when they leave the planned route while carrying the vehicle: a banner, a haptic, and a notification when the app is in the background. Route deviations are still detected and recorded when this is off; only the driver-facing alert is suppressed.",
      "x-hopdrive-label": "Route Deviation Alerts",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "route-monitoring"
    },
    route_corridor_rejoin: {
      type: "integer",
      minimum: 5,
      maximum: 1e3,
      description: "Distance in meters from the planned route within which a deviated driver is considered back on route. Kept below the off-route distance so the state does not flap at the boundary.",
      "x-hopdrive-label": "Route Corridor: Rejoin Distance (m)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "route-monitoring"
    },
    route_gps_accuracy_cutoff: {
      type: "integer",
      minimum: 10,
      maximum: 1e3,
      description: "GPS fixes with reported accuracy worse than this many meters are ignored by route deviation, stop detection and compliance analysis. Does not affect GPS arrivals and departures, whose server-side evidence check has its own cutoff (geofence_corroboration_accuracy_cutoff_meters).",
      "x-hopdrive-label": "Route Monitoring: GPS Accuracy Cutoff (m)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "route-monitoring"
    },
    route_stop_dwell_threshold: {
      type: "integer",
      minimum: 60,
      maximum: 7200,
      description: "Seconds a driver must remain stopped before the stop counts as an unplanned stop rather than traffic.",
      "x-hopdrive-label": "Unplanned Stop: Dwell Threshold (s)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "route-monitoring"
    },
    route_stop_radius: {
      type: "integer",
      minimum: 10,
      maximum: 500,
      description: "Radius in meters within which GPS fixes are treated as the same stop while measuring dwell. Default 100 m matches the driver app's field-tuned anchor radius (the pitch listed 50 m).",
      "x-hopdrive-label": "Unplanned Stop: Radius (m)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "route-monitoring"
    },
    route_stop_on_route_distance: {
      type: "integer",
      minimum: 10,
      maximum: 1e3,
      description: "Stops within this many meters of the planned route are ignored; stops farther away are recorded as unplanned off-route stops.",
      "x-hopdrive-label": "Unplanned Stop: On-Route Distance (m)",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "route-monitoring"
    },
    route_compliance_excess_distance: {
      type: "number",
      minimum: 1,
      maximum: 5,
      description: "Post-move check. Actual driven distance divided by the planned lane distance above this ratio records an excess distance event (1.15 = 15% farther than planned).",
      "x-hopdrive-label": "Route Compliance: Excess Distance Ratio",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "route-monitoring"
    },
    route_compliance_excess_time: {
      type: "number",
      minimum: 1,
      maximum: 5,
      description: "Post-move check. Actual transit time divided by the planned lane duration above this ratio records an excess time event (1.2 = 20% longer than planned).",
      "x-hopdrive-label": "Route Compliance: Excess Time Ratio",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "driver"
      ],
      "x-hopdrive-category": "route-monitoring"
    },
    program_visuals: {
      type: "object",
      properties: {
        active: {
          type: "boolean",
          description: "Show the program surfaces in the dealer portal: coverage on the move planner, the program breakdown on invoices, and the Programs page."
        },
        show_recoup: {
          type: "boolean",
          description: 'Also show the "you owe the program" line when the program fronts a cost the dealer pays back.'
        }
      },
      additionalProperties: false,
      description: "Whether dealers enrolled in a program see its coverage, claims and money in the dealer portal. Set on the program to switch every enrolled dealer at once; set on a customer to override it for that dealer.",
      "x-hopdrive-label": "Program Visuals",
      "x-hopdrive-writable-at-levels": [
        "program",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "dealer-admin",
        "dealer"
      ],
      "x-hopdrive-category": "programs"
    },
    program_reporting: {
      type: "object",
      properties: {
        enabled: {
          type: "boolean",
          description: "Generate and deliver the program report on the schedule below."
        },
        timezone: {
          type: "string",
          minLength: 1,
          description: "IANA time zone the run days are evaluated in, e.g. America/New_York."
        },
        run_days: {
          type: "array",
          items: {
            type: "integer",
            minimum: 1,
            maximum: 28
          },
          minItems: 1,
          description: "Days of the month the report runs (1 to 28, so every month has them)."
        },
        delay_days: {
          type: "integer",
          minimum: 0,
          maximum: 27,
          description: "How many days after the period closes to wait before reporting it, so late data lands first."
        },
        report_types: {
          type: "array",
          items: {
            type: "string",
            minLength: 1
          },
          minItems: 1,
          description: "Which reports to generate on each run."
        }
      },
      required: [
        "enabled",
        "timezone",
        "run_days",
        "delay_days",
        "report_types"
      ],
      additionalProperties: false,
      description: "When the program report is generated for the OEM. One schedule covers every enrolled dealer, so this is set on the program only.",
      "x-hopdrive-label": "Program Reporting",
      "x-hopdrive-writable-at-levels": [
        "program"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "programs"
    },
    guestrides: {
      type: "object",
      properties: {
        guest_portal_enabled: {
          type: "boolean",
          description: "Enable or disable the ability for guests to call their own ride."
        },
        max_trip_miles: {
          type: "number",
          minimum: 0,
          maximum: 200,
          description: "Maximum trip distance in miles allowed for a guest ride, at most 200."
        },
        require_approval: {
          type: "boolean",
          description: "Require a named manager to approve each guest ride before it is booked with a rideshare provider. Absent or false books as soon as the ride is called. Turning it on requires approver_user_id."
        },
        approver_user_id: {
          anyOf: [
            {
              type: "integer",
              exclusiveMinimum: 0
            },
            {
              type: "null"
            }
          ],
          description: "The HopDrive user id of the manager who approves guest rides for this rooftop. Stored as an id so contact changes follow the user record. One approver per rooftop."
        },
        approval_notification_channels: {
          type: "array",
          items: {
            type: "string",
            enum: [
              "sms",
              "email"
            ]
          },
          minItems: 1,
          description: 'How the approver is told a ride is waiting: ["sms"], ["email"], or both. An empty list is refused, because an approval nobody hears about strands the ride.'
        }
      },
      additionalProperties: false,
      description: "Guest ride settings for your rooftop: whether guests can call their own ride, how far a guest ride may go, and whether a manager approves each ride before it is booked.",
      "x-hopdrive-label": "Guest Rides",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "dealer-admin",
        "dealer-super-admin",
        "dealer"
      ],
      "x-hopdrive-writable-by-roles": [
        "admin",
        "dealer-admin",
        "dealer-super-admin"
      ],
      "x-hopdrive-category": "guestrides"
    },
    guestrides_access: {
      type: "object",
      properties: {
        enabled: {
          type: "boolean",
          description: "Enable or disable the guest ride feature for clients at this level. Guest rides give clients the ability to call rides for their guests or have their guests call their own ride."
        },
        credit_limit: {
          type: "number",
          minimum: 0,
          description: "Maximum outstanding Guest Rides balance in dollars before new rides are restricted. Consumed by the guestrides_credit_v1 product algorithm in @hopdrive/sdk-products, which resolves the product LIMITED (history and settlement stay available, only new rides are blocked). Absent or 0 means no credit gating."
        },
        credit_warning_threshold: {
          type: "number",
          minimum: 0,
          maximum: 1,
          description: "Fraction of credit_limit at which the Guest Rides decision starts carrying a creditWarning marker, so a dealer sees trouble coming before new rides are blocked. 0.8 means warn once the outstanding balance reaches 80% of the limit. Consumed by the guestrides_credit_v1 product algorithm in @hopdrive/sdk-products; clients read the marker and never compute the threshold themselves. Absent or 0 disables the warning tier. It never affects the hard limit, which keeps gating at credit_limit either way."
        },
        marketable: {
          type: "boolean",
          description: "Show Guest Rides marketing/discovery content to customers who are not entitled to it. Absent means TRUE (marketing by default, owner decision 2026-08-28 on #1225); set false to hide the product entirely (HIDDEN) for EVERY customer at this scope, entitled ones included. Suppression outranks entitlement in the resolution tree, which is why this is admin-only after #1411. Consumed by @hopdrive/sdk-products."
        },
        algorithm_key: {
          type: "string",
          description: "Key of a product algorithm registered in @hopdrive/sdk-products that refines the default Guest Rides access decision (e.g. guestrides_credit_v1 producing LIMITED from credit exposure). Absent means the default resolution tree stands."
        }
      },
      additionalProperties: false,
      description: "Guest Rides entitlement, credit terms and access algorithm. Admin only: a dealer must not enable their own product or raise their own credit line.",
      "x-hopdrive-label": "Guest Rides Access",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-writable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "guestrides"
    },
    product_history: {
      type: "object",
      properties: {
        retention_days: {
          type: "integer",
          minimum: 0,
          description: "Days after a product's last order or its enablement ending during which the portal still shows the product as history-only. Past the window the product is advertised (or hidden when suppressed). A product under an active contract stays history-only regardless of this window."
        }
      },
      additionalProperties: false,
      description: "How long a product with no active enablement stays visible as history-only in the customer portals before it is advertised or hidden.",
      "x-hopdrive-label": "Product History Window",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "product-experience"
    },
    dms: {
      type: "object",
      properties: {
        service: {
          type: "string",
          description: 'DMS service provider name (e.g., "CDK", "Reynolds", "DealerTrack")'
        },
        props: {
          type: "object",
          additionalProperties: {},
          description: "Additional DMS-specific properties. Structure varies by provider."
        }
      },
      additionalProperties: false,
      description: "Dealer Management System integration. Enables automatic data sync with dealership DMS.",
      "x-hopdrive-label": "DMS",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "dms"
    },
    scheduler: {
      type: "object",
      properties: {
        type: {
          type: "string",
          description: 'The scheduling system the dealer uses, e.g. "xtime". Should match a value from KNOWN_SCHEDULERS where possible; arbitrary strings are accepted for vendors not yet in the seeded list.'
        }
      },
      additionalProperties: false,
      description: `The dealer's appointment scheduling system (e.g. "xtime"). Auto-populated from inbound integration webhooks; the canonical place for Client Success to confirm which scheduling system a rooftop is on.`,
      "x-hopdrive-label": "Scheduler",
      "x-hopdrive-writable-at-levels": [
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "dealer-admin",
        "dealer-super-admin"
      ],
      "x-hopdrive-category": "integrations"
    },
    email_scrape_config: {
      type: "object",
      properties: {
        name: {
          type: "string",
          description: "Configuration name for identification."
        },
        version: {
          type: "string",
          description: "Schema version for compatibility tracking."
        },
        key: {
          type: "string",
          description: "Unique key identifier for this configuration."
        },
        mappings: {
          type: "object",
          properties: {
            name: {
              type: "object",
              properties: {
                selector: {
                  type: "string",
                  description: "CSS selector to locate the element containing the field value."
                },
                pipeline: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      ordinal: {
                        type: "integer",
                        minimum: 0,
                        description: "Execution order (0-indexed). Steps run in ascending order."
                      },
                      plugin: {
                        type: "string",
                        enum: [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        description: "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      params: {
                        type: "object",
                        additionalProperties: {
                          type: "string"
                        },
                        description: "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    additionalProperties: false,
                    description: "Single step in email scraping pipeline."
                  },
                  description: "Ordered processing steps to extract and transform the value."
                }
              },
              additionalProperties: false,
              description: "Field mapping for extracting data from emails."
            },
            phone: {
              type: "object",
              properties: {
                selector: {
                  type: "string",
                  description: "CSS selector to locate the element containing the field value."
                },
                pipeline: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      ordinal: {
                        type: "integer",
                        minimum: 0,
                        description: "Execution order (0-indexed). Steps run in ascending order."
                      },
                      plugin: {
                        type: "string",
                        enum: [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        description: "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      params: {
                        type: "object",
                        additionalProperties: {
                          type: "string"
                        },
                        description: "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    additionalProperties: false,
                    description: "Single step in email scraping pipeline."
                  },
                  description: "Ordered processing steps to extract and transform the value."
                }
              },
              additionalProperties: false,
              description: "Field mapping for extracting data from emails."
            },
            address: {
              type: "object",
              properties: {
                selector: {
                  type: "string",
                  description: "CSS selector to locate the element containing the field value."
                },
                pipeline: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      ordinal: {
                        type: "integer",
                        minimum: 0,
                        description: "Execution order (0-indexed). Steps run in ascending order."
                      },
                      plugin: {
                        type: "string",
                        enum: [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        description: "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      params: {
                        type: "object",
                        additionalProperties: {
                          type: "string"
                        },
                        description: "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    additionalProperties: false,
                    description: "Single step in email scraping pipeline."
                  },
                  description: "Ordered processing steps to extract and transform the value."
                }
              },
              additionalProperties: false,
              description: "Field mapping for extracting data from emails."
            },
            vehicle: {
              type: "object",
              properties: {
                selector: {
                  type: "string",
                  description: "CSS selector to locate the element containing the field value."
                },
                pipeline: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      ordinal: {
                        type: "integer",
                        minimum: 0,
                        description: "Execution order (0-indexed). Steps run in ascending order."
                      },
                      plugin: {
                        type: "string",
                        enum: [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        description: "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      params: {
                        type: "object",
                        additionalProperties: {
                          type: "string"
                        },
                        description: "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    additionalProperties: false,
                    description: "Single step in email scraping pipeline."
                  },
                  description: "Ordered processing steps to extract and transform the value."
                }
              },
              additionalProperties: false,
              description: "Field mapping for extracting data from emails."
            },
            vin: {
              type: "object",
              properties: {
                selector: {
                  type: "string",
                  description: "CSS selector to locate the element containing the field value."
                },
                pipeline: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      ordinal: {
                        type: "integer",
                        minimum: 0,
                        description: "Execution order (0-indexed). Steps run in ascending order."
                      },
                      plugin: {
                        type: "string",
                        enum: [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        description: "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      params: {
                        type: "object",
                        additionalProperties: {
                          type: "string"
                        },
                        description: "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    additionalProperties: false,
                    description: "Single step in email scraping pipeline."
                  },
                  description: "Ordered processing steps to extract and transform the value."
                }
              },
              additionalProperties: false,
              description: "Field mapping for extracting data from emails."
            },
            advisor: {
              type: "object",
              properties: {
                selector: {
                  type: "string",
                  description: "CSS selector to locate the element containing the field value."
                },
                pipeline: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      ordinal: {
                        type: "integer",
                        minimum: 0,
                        description: "Execution order (0-indexed). Steps run in ascending order."
                      },
                      plugin: {
                        type: "string",
                        enum: [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        description: "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      params: {
                        type: "object",
                        additionalProperties: {
                          type: "string"
                        },
                        description: "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    additionalProperties: false,
                    description: "Single step in email scraping pipeline."
                  },
                  description: "Ordered processing steps to extract and transform the value."
                }
              },
              additionalProperties: false,
              description: "Field mapping for extracting data from emails."
            },
            services: {
              type: "object",
              properties: {
                selector: {
                  type: "string",
                  description: "CSS selector to locate the element containing the field value."
                },
                pipeline: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      ordinal: {
                        type: "integer",
                        minimum: 0,
                        description: "Execution order (0-indexed). Steps run in ascending order."
                      },
                      plugin: {
                        type: "string",
                        enum: [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        description: "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      params: {
                        type: "object",
                        additionalProperties: {
                          type: "string"
                        },
                        description: "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    additionalProperties: false,
                    description: "Single step in email scraping pipeline."
                  },
                  description: "Ordered processing steps to extract and transform the value."
                }
              },
              additionalProperties: false,
              description: "Field mapping for extracting data from emails."
            },
            dealer_code: {
              type: "object",
              properties: {
                selector: {
                  type: "string",
                  description: "CSS selector to locate the element containing the field value."
                },
                pipeline: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      ordinal: {
                        type: "integer",
                        minimum: 0,
                        description: "Execution order (0-indexed). Steps run in ascending order."
                      },
                      plugin: {
                        type: "string",
                        enum: [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        description: "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      params: {
                        type: "object",
                        additionalProperties: {
                          type: "string"
                        },
                        description: "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    additionalProperties: false,
                    description: "Single step in email scraping pipeline."
                  },
                  description: "Ordered processing steps to extract and transform the value."
                }
              },
              additionalProperties: false,
              description: "Field mapping for extracting data from emails."
            },
            appointment_time: {
              type: "object",
              properties: {
                selector: {
                  type: "string",
                  description: "CSS selector to locate the element containing the field value."
                },
                pipeline: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      ordinal: {
                        type: "integer",
                        minimum: 0,
                        description: "Execution order (0-indexed). Steps run in ascending order."
                      },
                      plugin: {
                        type: "string",
                        enum: [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        description: "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      params: {
                        type: "object",
                        additionalProperties: {
                          type: "string"
                        },
                        description: "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    additionalProperties: false,
                    description: "Single step in email scraping pipeline."
                  },
                  description: "Ordered processing steps to extract and transform the value."
                }
              },
              additionalProperties: false,
              description: "Field mapping for extracting data from emails."
            },
            appointment_type: {
              type: "object",
              properties: {
                selector: {
                  type: "string",
                  description: "CSS selector to locate the element containing the field value."
                },
                pipeline: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      ordinal: {
                        type: "integer",
                        minimum: 0,
                        description: "Execution order (0-indexed). Steps run in ascending order."
                      },
                      plugin: {
                        type: "string",
                        enum: [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        description: "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      params: {
                        type: "object",
                        additionalProperties: {
                          type: "string"
                        },
                        description: "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    additionalProperties: false,
                    description: "Single step in email scraping pipeline."
                  },
                  description: "Ordered processing steps to extract and transform the value."
                }
              },
              additionalProperties: false,
              description: "Field mapping for extracting data from emails."
            },
            arrangement: {
              type: "object",
              properties: {
                selector: {
                  type: "string",
                  description: "CSS selector to locate the element containing the field value."
                },
                pipeline: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      ordinal: {
                        type: "integer",
                        minimum: 0,
                        description: "Execution order (0-indexed). Steps run in ascending order."
                      },
                      plugin: {
                        type: "string",
                        enum: [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        description: "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      params: {
                        type: "object",
                        additionalProperties: {
                          type: "string"
                        },
                        description: "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    additionalProperties: false,
                    description: "Single step in email scraping pipeline."
                  },
                  description: "Ordered processing steps to extract and transform the value."
                }
              },
              additionalProperties: false,
              description: "Field mapping for extracting data from emails."
            },
            confirmation_code: {
              type: "object",
              properties: {
                selector: {
                  type: "string",
                  description: "CSS selector to locate the element containing the field value."
                },
                pipeline: {
                  type: "array",
                  items: {
                    type: "object",
                    properties: {
                      ordinal: {
                        type: "integer",
                        minimum: 0,
                        description: "Execution order (0-indexed). Steps run in ascending order."
                      },
                      plugin: {
                        type: "string",
                        enum: [
                          "trim",
                          "getMetaContent",
                          "matchThenNextElement",
                          "extractBetween"
                        ],
                        description: "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                      },
                      params: {
                        type: "object",
                        additionalProperties: {
                          type: "string"
                        },
                        description: "Plugin-specific parameters as key-value pairs."
                      }
                    },
                    additionalProperties: false,
                    description: "Single step in email scraping pipeline."
                  },
                  description: "Ordered processing steps to extract and transform the value."
                }
              },
              additionalProperties: false,
              description: "Field mapping for extracting data from emails."
            }
          },
          additionalProperties: false,
          description: "Field extraction mappings for appointment email data."
        }
      },
      additionalProperties: false,
      description: "Email scraping configuration for automated data extraction.",
      "x-hopdrive-label": "Email Scrape Config",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "email-scraping"
    },
    rc_config: {
      type: "object",
      properties: {
        advisor_email: {
          type: "string",
          format: "email",
          description: "Service advisor account the integration signs in to RedCap as. Provisioned by onboarding \u2014 usually an `@hd3.io` service address, not a real person."
        },
        advisor_name: {
          type: "string",
          description: "Display name shown in RedCap for the advisor account."
        },
        advisor_phone: {
          type: "string",
          description: "Phone number on the advisor account in RedCap."
        },
        advisor_otp_secret: {
          type: "string",
          description: "One-time-password secret for the advisor sign-in, when RedCap requires two-factor for the account."
        },
        driver_email: {
          type: "string",
          format: "email",
          description: "Driver account the integration signs in to RedCap as, for the driver-facing side of the portal."
        },
        driver_name: {
          type: "string",
          description: "Display name shown in RedCap for the driver account."
        },
        driver_phone: {
          type: "string",
          description: "Phone number on the driver account in RedCap."
        },
        email_scrape_config: {
          type: "object",
          properties: {
            name: {
              type: "string"
            },
            version: {
              type: "string"
            },
            mappings: {
              type: "object",
              properties: {
                tempPass: {
                  type: "object",
                  properties: {
                    selector: {
                      type: "string",
                      description: "CSS selector to locate the element containing the field value."
                    },
                    pipeline: {
                      type: "array",
                      items: {
                        type: "object",
                        properties: {
                          ordinal: {
                            type: "integer",
                            minimum: 0,
                            description: "Execution order (0-indexed). Steps run in ascending order."
                          },
                          plugin: {
                            type: "string",
                            enum: [
                              "trim",
                              "getMetaContent",
                              "matchThenNextElement",
                              "extractBetween"
                            ],
                            description: "Plugin: trim=whitespace, getMetaContent=meta tag, matchThenNextElement=find+next, extractBetween=markers"
                          },
                          params: {
                            type: "object",
                            additionalProperties: {
                              type: "string"
                            },
                            description: "Plugin-specific parameters as key-value pairs."
                          }
                        },
                        additionalProperties: false,
                        description: "Single step in email scraping pipeline."
                      },
                      description: "Ordered processing steps to extract and transform the value."
                    }
                  },
                  additionalProperties: false,
                  description: "Field mapping for extracting data from emails."
                }
              },
              additionalProperties: false
            }
          },
          additionalProperties: false,
          description: "Email scraping config for extracting temporary passwords from RedCap's sign-in emails."
        },
        rc_dealer_admin_config_id: {
          type: "integer",
          description: "Which RedCap program this customer belongs to \u2014 `12` is Ford, `3` is Lincoln. Set by onboarding; a customer with none is not fully configured."
        }
      },
      additionalProperties: false,
      description: "Credentials the RedCap integration uses to sign in to their portal on this customer's behalf, and which RedCap program the customer belongs to.",
      "x-hopdrive-label": "RedCap Config",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "redcap"
    },
    private: {
      type: "object",
      additionalProperties: {},
      description: "Private/internal config. Freeform structure with dynamic boolean toggles for data sharing.",
      "x-hopdrive-label": "Private",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup",
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "private"
    },
    routing: {
      type: "object",
      properties: {
        avoid_tolls: {
          type: "boolean",
          description: "When true, routes will avoid toll roads where possible. This may result in longer travel times or distances."
        },
        avoid_highways: {
          type: "boolean",
          description: "When true, routes will avoid highways where possible. Useful for vehicles with highway restrictions or customers preferring local roads."
        },
        avoid_ferries: {
          type: "boolean",
          description: "When true, routes will avoid ferries where possible. Useful for customers who cannot use ferry routes."
        },
        route_preference: {
          type: "string",
          enum: [
            "fastest",
            "shortest"
          ],
          description: "Route optimization preference. 'fastest' optimizes for travel time (default), 'shortest' optimizes for distance."
        },
        no_toll_charges: {
          type: "boolean",
          description: "When true, toll charges are excluded from the transport cost. Lane tolls are billed as $0 in AR. When false, tolls charge normally."
        }
      },
      additionalProperties: false,
      description: "Routing preferences for lane enrichment. Controls toll avoidance, highway avoidance, ferry avoidance, and route optimization preferences.",
      "x-hopdrive-label": "Routing",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    allow_vinless_valet: {
      type: "boolean",
      description: "Select whether valet service is permitted without a VIN for customers at this level.",
      "x-hopdrive-label": "Allow Vinless Valet",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    },
    auto_create_valet_moves: {
      type: "boolean",
      description: "Automatically create a concierge pickup move + scenario when an appointment becomes type=valet. Dispatches a driver to pick up the consumer\u2019s vehicle without manual move planning. Return leg is created separately when service is complete.",
      "x-hopdrive-label": "Auto-Create Valet Moves",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "dealer-admin"
      ],
      "x-hopdrive-category": "appointments"
    },
    ar: {
      type: "object",
      properties: {
        services: {
          type: "object",
          additionalProperties: {
            type: "object",
            properties: {
              serviceKey: {
                type: "string",
                minLength: 1,
                description: 'Template key (e.g. "valet"). MUST match the SDK ServiceTemplate.key it was applied from.'
              },
              tier: {
                type: "string",
                enum: [
                  "standard",
                  "lite"
                ],
                description: "Tier variant. 'standard' = event-based per-use; 'lite' = SaaS variants."
              },
              frequency: {
                type: "string",
                enum: [
                  "weekly",
                  "monthly"
                ],
                description: "Frequency for any time-based triggers this service emits."
              },
              enableServiceFee: {
                type: "boolean",
                description: "Emit a recurring service-fee trigger? When false, no service-fee trigger fires regardless of serviceFee value."
              },
              serviceFee: {
                type: "number",
                exclusiveMinimum: 0,
                minimum: 0.01,
                description: "Service fee amount in dollars. Required when enableServiceFee=true."
              },
              perUseFee: {
                type: "number",
                exclusiveMinimum: 0,
                minimum: 0.01,
                description: "Per-use fee in dollars (GuestRides platform fee)."
              },
              enableMileageBands: {
                type: "boolean",
                description: "Use mileage-band pricing for per-move charges? Bands emit per-move event triggers with pricing read from the contract's raterulegroup at runtime."
              },
              ridesharePlatformFee: {
                type: "number",
                exclusiveMinimum: 0,
                minimum: 0.01,
                description: "Per-ride rideshare platform fee (dollars). Set by lite Moves bespokes; read by hoprides via getMoveService(contractId) when creating the rideshare accessorial."
              }
            },
            required: [
              "serviceKey",
              "frequency"
            ],
            additionalProperties: false
          },
          description: 'Per-service configuration keyed on the SDK template key (e.g. "valet"). One entry per applied template.'
        }
      },
      required: [
        "services"
      ],
      additionalProperties: false,
      description: "Per-contract Accounts Receivable service configuration. Stores pricing values and on/off toggles for every service (template) applied to a contract. Written by the SDK applyTemplate function; read by AR algorithms at run time.",
      "x-hopdrive-label": "AR Services",
      "x-hopdrive-writable-at-levels": [
        "raterulegroup"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "billing"
    },
    mobile_service: {
      type: "object",
      properties: {
        active: {
          type: "boolean",
          description: "Activate/Deactivate the mobile service feature for this client. When inactive, the Mobile Service nav item and route are hidden in the dealer portal."
        }
      },
      additionalProperties: false,
      description: "Mobile Service allows dealerships to submit completed mobile service runs for OEM reimbursement. Technicians record vehicle stops with VIN, mileage, and R/O data that are compiled into claim submissions.",
      "x-hopdrive-label": "Mobile Service",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-writable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Requires active mobility contract. Verify client contract before activating.",
      "x-hopdrive-category": "mobility"
    },
    mobile_service_claims: {
      type: "object",
      properties: {
        active: {
          type: "boolean",
          description: "Activate/Deactivate the mobile service claims feature for this client. When inactive, the Mobile Service Claims nav item and routes are hidden in the dealer portal."
        }
      },
      additionalProperties: false,
      description: "Mobile Service Claims allows dealerships to submit completed mobile service runs for OEM reimbursement. Technicians record vehicle stops with VIN, mileage, and R/O data that are compiled into claim submissions.",
      "x-hopdrive-label": "Mobile Service Claims",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-writable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Requires active mobility contract. Verify client contract before activating.",
      "x-hopdrive-category": "mobility"
    },
    shuttle: {
      type: "object",
      properties: {
        active: {
          type: "boolean",
          description: "Activate/Deactivate the shuttle feature for this client. When inactive, the Shuttle nav item and route are hidden in the dealer portal."
        }
      },
      additionalProperties: false,
      description: "Shuttle manages scheduled shuttle runs between dealership locations, allowing dealers to plan multi-stop routes for vehicle transport.",
      "x-hopdrive-label": "Shuttle",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Requires active mobility contract. Verify client contract before activating.",
      "x-hopdrive-category": "mobility"
    },
    parts_delivery: {
      type: "object",
      properties: {
        active: {
          type: "boolean",
          description: "Activate/Deactivate the parts delivery feature for this client. When inactive, the Parts Delivery nav item and route are hidden in the dealer portal."
        },
        third_party: {
          type: "boolean",
          description: "Enable HopDrive Managed couriers (3rd party): the dealer portal can quote and book Uber Direct / DoorDash Drive deliveries. Requires active."
        }
      },
      additionalProperties: false,
      description: "Parts Delivery manages delivery runs for transporting parts between dealership locations and service centers.",
      "x-hopdrive-label": "Parts Delivery",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-writable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Requires active mobility contract. Verify client contract before activating.",
      "x-hopdrive-category": "mobility"
    },
    driver_pipeline: {
      type: "object",
      properties: {
        funnel_open: {
          type: "boolean",
          description: "Whether the driver recruitment funnel is open for this region. When true, new drivers can be recruited."
        },
        active_driver_target: {
          type: "integer",
          minimum: 0,
          description: "Target number of active drivers for this region. Used to determine recruitment goals."
        },
        waitlist_target: {
          type: "integer",
          minimum: 0,
          description: "Target number of drivers on the waitlist for this region. Maintains a pipeline of ready drivers."
        },
        backlog_target: {
          type: "integer",
          minimum: 0,
          description: "Maximum number of drivers allowed in the backlog. When reached, recruitment funnel may be closed."
        },
        engagement_frequency_days: {
          type: "integer",
          exclusiveMinimum: 0,
          description: "How often (in days) to engage with drivers in the pipeline. Used for scheduling follow-up communications."
        },
        max_missed_confirmations: {
          type: "integer",
          minimum: 0,
          description: "Maximum number of missed confirmations allowed before removing a driver from the recruitment pipeline."
        }
      },
      additionalProperties: false,
      description: "Driver recruitment pipeline configuration with funnel management and engagement settings.",
      "x-hopdrive-label": "Driver Pipeline",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "region"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "region-pipeline"
    },
    connector_triggers: {
      type: "object",
      properties: {
        enabled: {
          type: "boolean",
          description: "Master enable/disable toggle for the entire integration"
        },
        secrets: {
          type: "object",
          additionalProperties: {
            type: "string"
          },
          default: {},
          description: 'Encrypted secrets storage. Keys are secret names (e.g., "DEALERWARE_CLIENT_ID"). Values are encrypted strings (e.g., "encrypted:v1:abc123...")'
        },
        authProviders: {
          type: "object",
          additionalProperties: {
            anyOf: [
              {
                type: "object",
                properties: {
                  type: {
                    type: "string",
                    const: "oauth2_client_credentials"
                  },
                  tokenEndpoint: {
                    type: "string",
                    format: "uri",
                    description: "OAuth token endpoint URL"
                  },
                  clientId: {
                    type: "string",
                    description: "Client ID - can be a secret reference like {SECRET_NAME}"
                  },
                  clientSecret: {
                    type: "string",
                    description: "Client secret - can be a secret reference like {SECRET_NAME}"
                  },
                  audience: {
                    type: "string",
                    description: "OAuth audience (optional)"
                  },
                  scope: {
                    type: "string",
                    description: "OAuth scope (optional)"
                  },
                  cacheTtlSeconds: {
                    type: "integer",
                    exclusiveMinimum: 0,
                    default: 3540,
                    description: "Token cache TTL in seconds (default: 3540 = 59 minutes)"
                  }
                },
                required: [
                  "type",
                  "tokenEndpoint",
                  "clientId",
                  "clientSecret"
                ],
                additionalProperties: false
              },
              {
                type: "object",
                properties: {
                  type: {
                    type: "string",
                    const: "api_key"
                  },
                  headerName: {
                    type: "string",
                    description: "Header name for the API key"
                  },
                  apiKey: {
                    type: "string",
                    description: "API key value - can be a secret reference like {SECRET_NAME}"
                  }
                },
                required: [
                  "type",
                  "headerName",
                  "apiKey"
                ],
                additionalProperties: false
              },
              {
                type: "object",
                properties: {
                  type: {
                    type: "string",
                    const: "basic"
                  },
                  username: {
                    type: "string",
                    description: "Username - can be a secret reference"
                  },
                  password: {
                    type: "string",
                    description: "Password - can be a secret reference"
                  }
                },
                required: [
                  "type",
                  "username",
                  "password"
                ],
                additionalProperties: false
              }
            ]
          },
          default: {},
          description: 'Auth provider configurations. Keys are provider names referenced by triggers (e.g., "dealerware-oauth")'
        },
        autocompleteTriggers: {
          type: "object",
          additionalProperties: {
            type: "object",
            properties: {
              enabled: {
                type: "boolean",
                description: "Whether this trigger is enabled"
              },
              authProvider: {
                type: "string",
                description: "Which auth provider to use (references key in authProviders)"
              },
              httpMethod: {
                type: "string",
                enum: [
                  "GET",
                  "POST"
                ],
                description: "HTTP method for the API call"
              },
              endpoint: {
                type: "string",
                description: "API endpoint URL - supports {search_query} interpolation"
              },
              requestBody: {
                type: "string",
                description: "Request body template for POST requests (optional)"
              },
              headers: {
                type: "object",
                additionalProperties: {
                  type: "string"
                },
                description: 'Optional custom headers to include with the request - supports template variable interpolation (e.g., { "api-version": "{cdi.api_version}" })',
                default: {}
              },
              minChars: {
                type: "integer",
                minimum: 1,
                default: 3,
                description: "Minimum characters before triggering autocomplete"
              },
              debounceMs: {
                type: "integer",
                minimum: 0,
                default: 300,
                description: "Debounce delay in milliseconds"
              },
              maxResults: {
                type: "integer",
                minimum: 1,
                maximum: 50,
                default: 10,
                description: "Maximum results to return"
              },
              mappings: {
                type: "object",
                properties: {
                  id: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  displayValue: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  subtext: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  }
                },
                required: [
                  "id",
                  "displayValue"
                ],
                additionalProperties: false,
                description: "Field mappings for autocomplete output"
              }
            },
            required: [
              "enabled",
              "authProvider",
              "httpMethod",
              "endpoint",
              "mappings"
            ],
            additionalProperties: false
          },
          default: {},
          description: "Autocomplete trigger configurations. Keys are trigger types matching form field triggers: consumer-name, consumer-phone, reference-number, consumer-vin, loaner-vin, stock-number"
        },
        fetchTriggers: {
          type: "object",
          additionalProperties: {
            type: "object",
            properties: {
              enabled: {
                type: "boolean",
                description: "Whether this trigger is enabled"
              },
              authProvider: {
                type: "string",
                description: "Which auth provider to use (references key in authProviders)"
              },
              httpMethod: {
                type: "string",
                enum: [
                  "GET",
                  "POST",
                  "PUT",
                  "PATCH"
                ],
                description: "HTTP method for the API call"
              },
              endpoint: {
                type: "string",
                description: "API endpoint URL - supports {lookup_value} interpolation"
              },
              requestBody: {
                type: "string",
                description: "Request body template for POST/PUT/PATCH requests (optional)"
              },
              headers: {
                type: "object",
                additionalProperties: {
                  type: "string"
                },
                description: 'Optional custom headers to include with the request - supports template variable interpolation (e.g., { "api-version": "{cdi.api_version}" })',
                default: {}
              },
              overwriteExisting: {
                type: "boolean",
                default: false,
                description: "Whether fetch results should overwrite existing form field values. When true, all mapped fields from the fetch response will replace existing values. When false (default), only empty form fields will be populated."
              },
              mappings: {
                type: "object",
                properties: {
                  id: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  displayLabel: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  referenceNumber: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  customerName: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  customerPhone: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  customerEmail: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  customerAddressLine1: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  customerAddressLine2: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  customerAddressCity: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  customerAddressState: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  customerAddressZip: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  vin: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  year: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  make: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  model: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  color: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  stockNumber: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  },
                  licensePlate: {
                    type: "object",
                    properties: {
                      source: {
                        type: "string",
                        description: "JSONPath expression to extract value from API response"
                      },
                      transforms: {
                        type: "array",
                        items: {
                          anyOf: [
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "uppercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "lowercase"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "trim"
                                }
                              },
                              required: [
                                "type"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "regex"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    pattern: {
                                      type: "string",
                                      description: "Regular expression pattern to match"
                                    },
                                    replacement: {
                                      type: "string",
                                      default: "",
                                      description: "Replacement string for regex matches"
                                    }
                                  },
                                  required: [
                                    "pattern"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "template"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    template: {
                                      type: "string",
                                      description: "Template string supporting {value} and {fieldName} placeholders"
                                    }
                                  },
                                  required: [
                                    "template"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "split"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    delimiter: {
                                      type: "string",
                                      description: "String delimiter to split on"
                                    },
                                    index: {
                                      type: "integer",
                                      minimum: 0,
                                      description: "Index of the split segment to extract"
                                    }
                                  },
                                  required: [
                                    "delimiter",
                                    "index"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "concat"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    prefix: {
                                      type: "string",
                                      default: "",
                                      description: "Prefix string to prepend"
                                    },
                                    suffix: {
                                      type: "string",
                                      default: "",
                                      description: "Suffix string to append"
                                    }
                                  },
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "default"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    value: {
                                      type: "string",
                                      description: "Default value to use if field is empty"
                                    }
                                  },
                                  required: [
                                    "value"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            },
                            {
                              type: "object",
                              properties: {
                                type: {
                                  type: "string",
                                  const: "date_format"
                                },
                                config: {
                                  type: "object",
                                  properties: {
                                    inputFormat: {
                                      type: "string",
                                      description: "Input date format (optional, auto-detected if not provided)"
                                    },
                                    outputFormat: {
                                      type: "string",
                                      description: "Output date format string"
                                    }
                                  },
                                  required: [
                                    "outputFormat"
                                  ],
                                  additionalProperties: false
                                }
                              },
                              required: [
                                "type",
                                "config"
                              ],
                              additionalProperties: false
                            }
                          ]
                        },
                        description: "Optional chain of transforms to apply to the extracted value"
                      }
                    },
                    required: [
                      "source"
                    ],
                    additionalProperties: false
                  }
                },
                additionalProperties: false,
                description: "Field mappings from 3rd party API response to canonical FetchResult"
              },
              enrichments: {
                type: "array",
                items: {
                  type: "object",
                  properties: {
                    condition: {
                      type: "string",
                      description: 'JSONPath condition evaluated against the **raw primary API response**.\n\nEnrichment is skipped if the condition returns a falsy value (`null`, `undefined`, `""`, `0`, `false`, or empty array).\n\n**Condition Styles:**\n\n| Style | Syntax | Behavior |\n|-------|--------|----------|\n| Simple existence | `$.id` | Truthy if field exists and has value |\n| Filter expression | `$[?(@.field == "value")]` | Truthy if filter matches |\n\n**Filter Expression Examples:**\n\n| Condition | Description |\n|-----------|-------------|\n| `$[?(@.tier == "premium")]` | Tier equals "premium" |\n| `$.vehicle[?(@.year >= 2020)]` | Vehicle year is 2020 or newer |\n| `$.features[?(@ == "roadside_assistance")]` | Features array contains value |\n| `$.customer[?(@.membership_level == "gold")]` | Nested property equals value |\n| `$.vehicles[?(@.primary == true)]` | Array contains object with primary=true |'
                    },
                    authProvider: {
                      type: "string",
                      description: "Auth provider to use for this enrichment request.\n\nDefaults to the parent fetch trigger's `authProvider` if not specified. Use this to authenticate enrichment calls against a different API than the primary request."
                    },
                    httpMethod: {
                      type: "string",
                      enum: [
                        "GET",
                        "POST",
                        "PUT",
                        "PATCH"
                      ],
                      default: "GET",
                      description: "HTTP method for the enrichment API call.\n\nDefaults to `GET`. Use `POST`, `PUT`, or `PATCH` when the enrichment endpoint requires a request body."
                    },
                    endpoint: {
                      type: "string",
                      description: "Endpoint URL with variable interpolation.\n\n**Supported Variables:**\n\n| Variable | Source | Example |\n|----------|--------|---------|\n| `{primary.fieldName}` | Mapped field from primary response | `{primary.customerId}` |\n| `{primary_raw.$.path}` | JSONPath from raw primary response | `{primary_raw.$.data[0].id}` |\n| `{lookup_value}` | Original lookup value | `{lookup_value}` |\n\n**Example:**\n```\nhttps://api.example.com/customers/{primary.customerId}/address\n```\n\nAll interpolated values are URL-encoded automatically."
                    },
                    requestBody: {
                      type: "string",
                      description: 'Request body template for `POST`, `PUT`, or `PATCH` requests.\n\nSupports the same variable interpolation as `endpoint`:\n- `{primary.fieldName}` - Mapped field from primary response\n- `{primary_raw.$.path}` - JSONPath from raw primary response\n- `{lookup_value}` - Original lookup value\n\n**Example:**\n```json\n{"customerId": "{primary.customerId}", "action": "getAddress"}\n```'
                    },
                    headers: {
                      type: "object",
                      additionalProperties: {
                        type: "string"
                      },
                      description: 'Optional custom headers to include with the enrichment request.\n\nSupports the same variable interpolation as `endpoint`:\n- `{primary.fieldName}` - Mapped field from primary response\n- `{primary_raw.$.path}` - JSONPath from raw primary response\n- `{lookup_value}` - Original lookup value\n\n**Example:**\n```json\n{ "api-version": "4", "x-tenant-id": "{primary.tenantId}" }\n```',
                      default: {}
                    },
                    mappings: {
                      type: "object",
                      properties: {
                        id: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        displayLabel: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        referenceNumber: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        customerName: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        customerPhone: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        customerEmail: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        customerAddressLine1: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        customerAddressLine2: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        customerAddressCity: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        customerAddressState: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        customerAddressZip: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        vin: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        year: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        make: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        model: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        color: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        stockNumber: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        },
                        licensePlate: {
                          type: "object",
                          properties: {
                            source: {
                              type: "string",
                              description: "JSONPath expression to extract value from API response"
                            },
                            transforms: {
                              type: "array",
                              items: {
                                anyOf: [
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "uppercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "lowercase"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "trim"
                                      }
                                    },
                                    required: [
                                      "type"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "regex"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          pattern: {
                                            type: "string",
                                            description: "Regular expression pattern to match"
                                          },
                                          replacement: {
                                            type: "string",
                                            default: "",
                                            description: "Replacement string for regex matches"
                                          }
                                        },
                                        required: [
                                          "pattern"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "template"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          template: {
                                            type: "string",
                                            description: "Template string supporting {value} and {fieldName} placeholders"
                                          }
                                        },
                                        required: [
                                          "template"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "split"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          delimiter: {
                                            type: "string",
                                            description: "String delimiter to split on"
                                          },
                                          index: {
                                            type: "integer",
                                            minimum: 0,
                                            description: "Index of the split segment to extract"
                                          }
                                        },
                                        required: [
                                          "delimiter",
                                          "index"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "concat"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          prefix: {
                                            type: "string",
                                            default: "",
                                            description: "Prefix string to prepend"
                                          },
                                          suffix: {
                                            type: "string",
                                            default: "",
                                            description: "Suffix string to append"
                                          }
                                        },
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "default"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          value: {
                                            type: "string",
                                            description: "Default value to use if field is empty"
                                          }
                                        },
                                        required: [
                                          "value"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  },
                                  {
                                    type: "object",
                                    properties: {
                                      type: {
                                        type: "string",
                                        const: "date_format"
                                      },
                                      config: {
                                        type: "object",
                                        properties: {
                                          inputFormat: {
                                            type: "string",
                                            description: "Input date format (optional, auto-detected if not provided)"
                                          },
                                          outputFormat: {
                                            type: "string",
                                            description: "Output date format string"
                                          }
                                        },
                                        required: [
                                          "outputFormat"
                                        ],
                                        additionalProperties: false
                                      }
                                    },
                                    required: [
                                      "type",
                                      "config"
                                    ],
                                    additionalProperties: false
                                  }
                                ]
                              },
                              description: "Optional chain of transforms to apply to the extracted value"
                            }
                          },
                          required: [
                            "source"
                          ],
                          additionalProperties: false
                        }
                      },
                      additionalProperties: false,
                      description: "Field mappings applied to the enrichment response.\n\nMapped values are **merged** with the primary response result. Non-null enrichment values will overwrite existing fields from the primary response.\n\nUses the same JSONPath syntax as primary fetch trigger mappings."
                    }
                  },
                  required: [
                    "endpoint",
                    "mappings"
                  ],
                  additionalProperties: false
                },
                default: [],
                description: "Optional enrichments for chained API calls.\n\nEach enrichment makes a **secondary API call** using data from the primary response, then merges the results into the final `FetchResult`.\n\n**Use Cases:**\n- Fetch customer address from a separate endpoint\n- Get additional details that require the primary record's ID\n- Call different APIs conditionally based on primary response data\n\n**Execution Flow:**\n1. Primary fetch request executes and maps response\n2. Each enrichment's `condition` is evaluated against raw primary response\n3. Enrichments with passing conditions execute in order\n4. Results merge into final response (non-null values overwrite)\n\n**Error Handling:**\nEnrichment failures are non-fatal. If an enrichment fails, the primary data is preserved and processing continues with remaining enrichments."
              }
            },
            required: [
              "enabled",
              "authProvider",
              "httpMethod",
              "endpoint",
              "mappings"
            ],
            additionalProperties: false
          },
          default: {},
          description: "Fetch trigger configurations. Keys are trigger type identifiers (e.g., 'contract-detail', 'consumer-detail', 'vehicle-detail'). The trigger type is arbitrary - form code defines what trigger types it fires, and the config defines how to handle each one."
        }
      },
      required: [
        "enabled"
      ],
      additionalProperties: false,
      description: "Configure triggered connectors for move form autocomplete and data fetching from partner APIs",
      "x-hopdrive-label": "Connector Triggers",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin",
        "dealer-admin",
        "dealer-super-admin"
      ],
      "x-hopdrive-category": "integrations"
    },
    minimum_certification_ids: {
      type: "array",
      items: {
        type: "string"
      },
      description: "Minimum certification IDs all drivers must have in order to be receive move offers.",
      "x-hopdrive-label": "Minimum Certification IDs",
      "x-hopdrive-writable-at-levels": [
        "region",
        "organization",
        "customer",
        "workflowset",
        "raterulegroup",
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "custom-settings"
    },
    billing_frequency: {
      type: "string",
      enum: [
        "monthly",
        "weekly",
        "daily"
      ],
      description: "Determines how often we generate invoices for this client, which directly affects how often they are billed. This should be set to monthly, unless the client specifically requests otherwise.",
      "x-hopdrive-label": "Billing Frequency",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "billing"
    },
    payment_terms: {
      type: "integer",
      minimum: 1,
      maximum: 30,
      description: "Payment terms determine how many days we give the client to pay an invoice after it's end date. This should be set to 30 days, unless the client specifically requests otherwise.",
      "x-hopdrive-label": "Payment Terms",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "billing"
    },
    auto_pay: {
      type: "boolean",
      description: "Activate/Deactivate auto-pay for this client. Auto-pay determines whether or not we automatically charge the client's credit card after an invoice closes.",
      "x-hopdrive-label": "Auto Pay",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "billing"
    },
    isShuttle: {
      type: "boolean",
      description: "System flag marking this move as part of a shuttle sequence. Set by shuttle planning, not user-configurable.",
      "x-hopdrive-label": "Is Shuttle",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    firstShuttleMove: {
      type: "boolean",
      description: "System flag marking this as the first move in a shuttle sequence. Set by shuttle planning, not user-configurable.",
      "x-hopdrive-label": "First Shuttle Move",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    lastShuttleMove: {
      type: "boolean",
      description: 'System flag marking this as the last move in a shuttle sequence. Triggers chase vehicle status to "returned". Set by shuttle planning, not user-configurable.',
      "x-hopdrive-label": "Last Shuttle Move",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    chase_id: {
      type: "string",
      format: "uuid",
      description: "UUID linking chase moves together for ridegroup management, cost allocation, and status sync. Set by shuttle/chase planning, not user-configurable.",
      "x-hopdrive-label": "Chase ID",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    parent_chase_id: {
      type: "string",
      format: "uuid",
      description: "UUID referencing the parent move in a chase sequence. Set by autoplanner, not user-configurable.",
      "x-hopdrive-label": "Parent Chase ID",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    batch: {
      type: "object",
      properties: {
        order_ref: {
          type: "string",
          description: "External order reference from Super Dispatch"
        },
        size: {
          type: "integer",
          exclusiveMinimum: 0,
          description: "Number of vehicles in this batch"
        }
      },
      additionalProperties: false,
      description: "Groups moves from multi-vehicle Super Dispatch orders. Contains order reference and batch size. Set by integrations, not user-configurable.",
      "x-hopdrive-label": "Batch",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    ride_orchestration: {
      type: "object",
      properties: {
        last_estimate_at: {
          type: "string",
          description: "ISO timestamp of last estimate fetch"
        },
        next_estimate_at: {
          type: "string",
          description: "ISO timestamp for next scheduled estimate refresh"
        },
        rideshare_eta_seconds: {
          type: "number",
          description: "Estimated time of arrival in seconds"
        },
        selected_provider: {
          type: "string",
          enum: [
            "uber",
            "lyft"
          ],
          description: "Selected rideshare provider (uber or lyft)"
        },
        selected_product: {
          type: "string",
          description: "Selected product/tier (e.g., UberX, Lyft XL)"
        },
        target_pickup_time: {
          type: "string",
          description: "Target pickup time for ride-after scenarios (ISO timestamp)"
        },
        best_time_to_call: {
          type: "string",
          description: "Optimal time to request the ride (ISO timestamp)"
        },
        booking_lead_time_seconds: {
          type: "number",
          description: "Lead time needed before target pickup"
        },
        calculation_method: {
          type: "string",
          description: 'Method used to calculate timing (e.g., "eta-based", "fixed")'
        }
      },
      additionalProperties: false,
      description: "Rideshare booking state including estimates, provider selection, and timing calculations. Set by hoprides service, not user-configurable.",
      "x-hopdrive-label": "Ride Orchestration",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    superDispatch: {
      type: "object",
      properties: {
        vehicle_id: {
          type: "number",
          description: "Super Dispatch vehicle ID"
        },
        reference_num: {
          type: "number",
          description: "Super Dispatch reference number for status sync"
        }
      },
      additionalProperties: false,
      description: "Super Dispatch integration reference data for status sync. Set by super-dispatch-integration, not user-configurable.",
      "x-hopdrive-label": "Super Dispatch",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    superdispatch_routing: {
      type: "object",
      properties: {
        enabled: {
          type: "boolean",
          description: "Master switch for this organization. When off, HopDrive stops ACCEPTING their Super Dispatch offers, so no new moves are created.\n\n**Moves that already exist keep syncing.** Their status changes, charges and photos still reach Super Dispatch."
        },
        prefix: {
          type: "string",
          description: "Super Dispatch order numbers starting with this prefix belong to this organization \u2014 `TS` for Merchants Fleet, `HC` for Hertz. Case-insensitive, and the LONGEST matching prefix wins, so `HCL` beats `HC` when both exist. An order matching no organization falls back to a default set in the integration code."
        },
        autoAcceptOffer: {
          type: "boolean",
          description: "Accept Super Dispatch load offers for this organization automatically, instead of leaving them for a human. Requires an explicit prefix match \u2014 an order that only reached this organization through the code-level fallback is never auto-accepted."
        },
        ghostMoveOffer: {
          type: "boolean",
          description: 'Accept offers whose vehicle details are still placeholders (make and model literally "make" and "model"). Some shippers send the offer before the real vehicle is known.\n\n**A placeholder offer left unaccepted is not revisited.** HopDrive does not hold it and accept it once the real vehicle arrives \u2014 it waits for a human, and is only re-evaluated if Super Dispatch sends the offer again.'
        }
      },
      additionalProperties: false,
      description: "Whether this organization's Super Dispatch orders are accepted, which order numbers belong to them, and how their offers are handled. Set this to bring a new Super Dispatch client online. What HopDrive sends BACK to Super Dispatch is configured separately, under Super Dispatch Sync.",
      "x-hopdrive-label": "Super Dispatch",
      "x-hopdrive-writable-at-levels": [
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Two organizations must never claim the same prefix \u2014 orders would route to whichever matched first. The longest matching prefix wins, so a more specific prefix can safely sit alongside a shorter one.",
      "x-hopdrive-category": "super-dispatch"
    },
    superdispatch_settings: {
      type: "object",
      properties: {
        syncVehiclePhotos: {
          type: "boolean",
          description: "Sends HopDrive inspection photos to the matching Super Dispatch vehicle. Only the photo steps Super Dispatch has a slot for are sent (the four corner shots on the standard workflows). **This delays the pickup and delivery notification the shipper receives**, because Super Dispatch closes each inspection the moment the status changes, so the photos must be sent first \u2014 normally seconds, up to ten minutes on a bad connection. Worth the customer knowing before it is switched on."
        },
        syncDamagePhotos: {
          type: "boolean",
          description: "Sends damage-report photos to the Super Dispatch vehicle with a damage record attached to each. Independent of inspection photos \u2014 either can be on without the other. Damage photos are sent on a best-effort basis: any still uploading when the status change goes through are skipped, because Super Dispatch will not accept them afterwards."
        },
        arBehavior: {
          type: "string",
          enum: [
            "default",
            "override_hopdrive_price",
            "correct_transport_cost"
          ],
          description: "How HopDrive's price and the shipper's order price are reconciled. The two corrections are mutually exclusive \u2014 replacing our price with theirs and then adjusting their total to match ours would be circular \u2014 so this is one choice, not two switches.\n\n- **Default** \u2014 neither is touched. HopDrive bills its own price; their order keeps whatever they entered, and any difference stays a difference.\n- **Use their price** \u2014 at ingest, HopDrive's AR price is replaced by the price on the Super Dispatch order, so billing follows what the shipper entered rather than HopDrive lane pricing.\n- **Correct their total** \u2014 HopDrive bills its own price and posts a signed adjustment line on their order (labelled *price adjusted for lane pricing*) so the shipper's total matches. Their original entry stays intact and the difference is visible rather than silent. **This changes what the customer is billed on their own record \u2014 get their agreement first.**"
        },
        cancelBehavior: {
          type: "string",
          enum: [
            "notify_only",
            "auto_cancel"
          ],
          description: "What happens on the shipper's order when HopDrive cancels a move. One choice, because cancelling their order and asking a human to decide are alternatives, not additions.\n\n- **Post to Slack, leave the order alone** (default) \u2014 the order is untouched and a Slack message goes to this customer's channel so the team can decide whether to cancel it or reschedule. A cancellation is not always the end of the job, and HopDrive cannot tell the difference from the move alone.\n- **Cancel it in Super Dispatch** \u2014 the order is cancelled automatically, with HopDrive's cancel reason mapped onto Super Dispatch's own reason types. **This closes the job on the customer's own record with no human in the loop** \u2014 if they habitually reschedule rather than cancel, this will cancel orders they expected to keep. A failure to cancel still raises a Slack alert, so a missed cancellation is never silent."
        },
        invoiceEmail: {
          type: "string",
          description: "Where Super Dispatch sends its copy of the invoice. **Required as soon as any charge above is synced**, because submitting the invoice is what makes those charges visible and an invoice has to be addressed somewhere. With no address configured nothing is submitted, the charges stay invisible to the shipper, and a Slack alert is raised rather than guessing at a recipient. Super Dispatch does not validate this against the shipper, so it is ours to choose: a HopDrive billing alias is the usual answer."
        },
        allowedAccessorialCodes: {
          type: "array",
          items: {
            type: "string"
          },
          description: "Which HopDrive accessorial codes are posted to the Super Dispatch order as expenses once the move settles \u2014 e.g. `fuel`, `tow`, `toll`, `delay`. Codes with a native Super Dispatch expense type use it (`tow` becomes *pulling fee*); anything else posts as *other*, labelled with the code. Empty means no expenses are sent at all.\n\n**Enabling any of these also submits the Super Dispatch invoice for each load**, because an expense on an order is not shown to the shipper until the load is invoiced \u2014 so syncing charges without invoicing would reach nobody. Every settled load is invoiced, including loads that carry no accessorials, so their board does not end up half invoiced. Set the invoice email below."
        },
        slackChannel: {
          type: "string",
          description: "Slack channel for this customer's Super Dispatch failure alerts, without the leading `#`. **The alert bot must be invited to the channel or the messages will never post.** Leave blank to use the integration's default channel."
        }
      },
      additionalProperties: false,
      description: "What HopDrive sends back to Super Dispatch once a move is under way: which accessorial charges reach the shipper's order, whether their transport cost is corrected, what happens to their order when a move is cancelled, and whether inspection and damage photos are sent. Set on the organization; override on a customer only where that rooftop genuinely differs.",
      "x-hopdrive-label": "Super Dispatch Sync",
      "x-hopdrive-writable-at-levels": [
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Several of these write to the customer's own Super Dispatch record \u2014 their billed total, whether their order gets cancelled, when their pickup and delivery notifications arrive, and whether their loads get invoiced. Confirm with the customer before enabling charge sync, transport-cost correction, automatic cancellation or photo sync.",
      "x-hopdrive-category": "super-dispatch"
    },
    superdispatch_subscription: {
      type: "object",
      properties: {},
      additionalProperties: false,
      description: "Whether Super Dispatch receives webhooks about this customer's moves. Read live from the subscriptions table and changed here directly \u2014 this is not stored in config, so there is only ever one answer.",
      "x-hopdrive-label": "Super Dispatch Subscription",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "super-dispatch"
    },
    carsarrive_connection: {
      type: "object",
      properties: {},
      additionalProperties: false,
      description: "What it takes to get this rooftop live with CarsArrive: the manual step CarsArrive has to do on their side, and the webhook subscription that lets HopDrive report the move back. Read live from the subscriptions table and changed here directly \u2014 this is not stored in config, so there is only ever one answer.",
      "x-hopdrive-label": "CarsArrive Connection",
      "x-hopdrive-writable-at-levels": [
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "cars-arrive"
    },
    carsarrive_routing: {
      type: "object",
      properties: {
        enabled: {
          type: "boolean",
          description: "Master switch for this organization. When off, HopDrive stops ACCEPTING their CarsArrive loads \u2014 an assigned load is rejected back to CarsArrive rather than becoming a move.\n\n**Moves that already exist keep reporting.** Their status changes, photos and invoice still reach CarsArrive.\n\nThis does not stop CarsArrive quoting: a `Quote:DriveService` request is priced as usual, because a quote creates nothing and refusing to price one looks to CarsArrive like an outage rather than a decision."
        }
      },
      additionalProperties: false,
      description: "Whether this organization's CarsArrive loads are accepted. What HopDrive sends BACK to CarsArrive is configured separately, under CarsArrive Sync; getting an individual rooftop live is under CarsArrive Connection.",
      "x-hopdrive-label": "CarsArrive",
      "x-hopdrive-writable-at-levels": [
        "organization"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "cars-arrive"
    },
    carsarrive_settings: {
      type: "object",
      properties: {
        syncVehiclePhotos: {
          type: "boolean",
          description: "Waits for the move's inspection photos before reporting pickup and delivery to CarsArrive, so the photos actually make it onto the load. **This delays the pickup and delivery notification CarsArrive receives** \u2014 normally seconds, up to ten minutes on a bad connection. With this off the callback goes out immediately and carries only whatever photos happened to have finished uploading, which on a fast move is usually none."
        },
        cancelBehavior: {
          type: "string",
          enum: [
            "auto_cancel",
            "notify_only"
          ],
          description: "What happens to the CarsArrive load when HopDrive cancels a move. One choice, because cancelling their load and asking a human to decide are alternatives, not additions.\n\n- **Cancel it in CarsArrive** (default, and the long-standing behaviour) \u2014 the load is rejected back to CarsArrive automatically. Unlike Super Dispatch this causes no echo loop, because CarsArrive does not webhook the cancellation back to us.\n- **Post to Slack, leave the load alone** \u2014 the load is untouched and a Slack message goes to this customer's channel so the team can decide whether to cancel it or reschedule. **CarsArrive will keep believing the vehicle is being moved until somebody acts.**"
        },
        invoiceMapping: {
          type: "object",
          properties: {
            standardTransport: {
              type: "array",
              items: {
                type: "string"
              }
            },
            tolls: {
              type: "array",
              items: {
                type: "string"
              }
            },
            fuel: {
              type: "array",
              items: {
                type: "string"
              }
            },
            other: {
              type: "array",
              items: {
                type: "string"
              }
            }
          },
          additionalProperties: false,
          description: "Which HopDrive accessorial names land in each of CarsArrive's four invoice buckets when a move is delivered. Matched case-insensitively against the accounts-receivable line names. Anything not listed in any bucket is **not invoiced to CarsArrive at all** \u2014 HopDrive still bills it, but their invoice will not show it, so a charge left unmapped is a silent revenue gap rather than an error."
        },
        slackChannel: {
          type: "string",
          description: "Slack channel for this customer's CarsArrive failure alerts, without the leading `#`. **The alert bot must be invited to the channel or the messages will never post.** Leave blank to use the integration's default channel."
        }
      },
      additionalProperties: false,
      description: "What HopDrive sends back to CarsArrive once a move is under way: whether inspection photos are waited for and sent, what happens to their load when a move is cancelled, and which of HopDrive's charges land in each bucket of their invoice. Set on the organization; override on a customer only where that rooftop genuinely differs.",
      "x-hopdrive-label": "CarsArrive Sync",
      "x-hopdrive-writable-at-levels": [
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-warning": "Two of these are customer-visible on CarsArrive's own record: when their pickup and delivery notifications arrive, and what their invoice adds up to. Confirm with the customer before turning photo sync on or changing the invoice mapping.",
      "x-hopdrive-category": "cars-arrive"
    },
    referenceNumbers: {
      type: "object",
      additionalProperties: {
        type: "string"
      },
      description: "External system reference IDs from CarsArrive, Central Dispatch, etc. Set by integrations, not user-configurable.",
      "x-hopdrive-label": "Reference Numbers",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    isGhost: {
      type: "boolean",
      description: "Marks a placeholder move requiring full VIN capture. Set when created from Super Dispatch with shell vehicle data. Not user-configurable.",
      "x-hopdrive-label": "Is Ghost",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    vin: {
      type: "object",
      properties: {
        capture: {
          type: "string",
          enum: [
            "full",
            "partial",
            "none"
          ],
          description: "Required VIN capture level in driver workflow"
        }
      },
      additionalProperties: false,
      description: "VIN capture requirements for driver workflow. Set by system for ghost moves, not user-configurable.",
      "x-hopdrive-label": "VIN Capture",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    triangle_move: {
      type: "boolean",
      description: "Marks move as triangle (ride before AND after charged to customer). Set via admin UI when customer has allow_triangle_moves permission.",
      "x-hopdrive-label": "Triangle Move",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    isFlex: {
      type: "boolean",
      description: "Marks a round trip as flex type for scenario detection. Set by system, not user-configurable.",
      "x-hopdrive-label": "Is Flex",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    moveInput: {
      type: "object",
      additionalProperties: {},
      description: "Output data from the dynamic form presented during move create in React. Contains indeterminate key-value pairs (one level deep). Not user-configurable.",
      "x-hopdrive-label": "Move Input",
      "x-hopdrive-writable-at-levels": [
        "move"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "operational-state"
    },
    loaner_vin_edit_disabled: {
      type: "boolean",
      description: "When enabled, prevents editing the loaner vehicle VIN field in move details.",
      "x-hopdrive-label": "Disable Loaner VIN Editing",
      "x-hopdrive-writable-at-levels": [
        "hopdrive",
        "organization",
        "customer"
      ],
      "x-hopdrive-readable-by-roles": [
        "admin"
      ],
      "x-hopdrive-category": "move-planning"
    }
  },
  additionalProperties: false
};

// src/utils/json-schema-lookup.ts
function loadJsonSchema() {
  return config_schema_default;
}
function getJsonSchemaForSetting(settingKey) {
  const schema = loadJsonSchema();
  return schema.properties?.[settingKey];
}

// src/utils/filter-by-role.ts
function filterConfigByRole(config, role) {
  if (!role) {
    const { __metadata, ...rest } = config;
    return rest;
  }
  const filtered = {};
  for (const [key, value] of Object.entries(config)) {
    if (key === "__metadata") continue;
    const definition = allDefinitions[key];
    if (definition && definition.readableByRoles.includes(role)) {
      filtered[key] = value;
    }
  }
  return filtered;
}
function enrichMetadata(sqlMetadata, mergedConfig) {
  const settings = {};
  for (const key of Object.keys(mergedConfig)) {
    if (key === "__metadata") continue;
    const definition = allDefinitions[key];
    if (definition) {
      const jsonSchema = getJsonSchemaForSetting(key);
      const simplifiedSchema = jsonSchema ? {
        ...jsonSchema.type && { type: jsonSchema.type },
        ...jsonSchema.properties && { properties: jsonSchema.properties },
        ...jsonSchema.additionalProperties !== void 0 && {
          additionalProperties: jsonSchema.additionalProperties
        }
      } : null;
      settings[key] = {
        schema: simplifiedSchema,
        label: definition.label,
        description: definition.description,
        ...definition.warning && { warning: definition.warning },
        kind: definition.kind,
        ...definition.category && { category: definition.category },
        writableAtLevels: definition.writableAtLevels,
        readableByRoles: definition.readableByRoles,
        ...definition.defaultValue !== void 0 && { defaultValue: definition.defaultValue },
        ...definition.deprecated && { deprecated: definition.deprecated },
        sourceLevel: sqlMetadata.sourceLevels[key]
      };
    }
  }
  return {
    ...sqlMetadata,
    settings
  };
}

// src/errors.ts
var EntityNotFoundError = class extends Error {
  constructor(level, entityId) {
    super(`${level} ${entityId} not found`);
    this.level = level;
    this.entityId = entityId;
    this.name = "EntityNotFoundError";
  }
};
var EncryptionKeyNotFoundError = class extends Error {
  constructor(version, availableVersions) {
    const availableMsg = availableVersions.length > 0 ? `Available versions: ${availableVersions.join(", ")}` : "No encryption keys have been configured. Call init() with encryption keys.";
    super(
      `Encryption key version "${version}" not found. An encrypted config value requires this key for decryption. ${availableMsg}`
    );
    this.version = version;
    this.availableVersions = availableVersions;
    this.name = "EncryptionKeyNotFoundError";
  }
};

// src/fetchers/region.ts
async function getRegionConfig2(params) {
  const { regionId, options } = params;
  const includeMetadata = options?.includeMetadata ?? false;
  const region = await getRegionConfig(
    { regionId, includeMetadata },
    { executor: params.executor }
  );
  if (!region) {
    throw new EntityNotFoundError(LEVELS.REGION, regionId);
  }
  const sqlResponse = region.effective_config;
  const { __metadata, ...rawConfig } = sqlResponse;
  const filteredConfig = filterConfigByRole(rawConfig, options?.role);
  if (includeMetadata && __metadata) {
    const enrichedMetadata = enrichMetadata(__metadata, filteredConfig);
    return {
      ...filteredConfig,
      __metadata: enrichedMetadata
    };
  }
  return filteredConfig;
}

// src/fetchers/organization.ts
async function getOrganizationConfig2(params) {
  const { organizationId, options } = params;
  const includeMetadata = options?.includeMetadata ?? false;
  const org = await getOrganizationConfig(
    { organizationId, includeMetadata },
    { executor: params.executor }
  );
  if (!org) {
    throw new EntityNotFoundError(LEVELS.ORGANIZATION, organizationId);
  }
  const sqlResponse = org.effective_config;
  const { __metadata, ...rawConfig } = sqlResponse;
  const filteredConfig = filterConfigByRole(rawConfig, options?.role);
  if (includeMetadata && __metadata) {
    const enrichedMetadata = enrichMetadata(__metadata, filteredConfig);
    return {
      ...filteredConfig,
      __metadata: enrichedMetadata
    };
  }
  return filteredConfig;
}

// src/fetchers/customer.ts
async function getCustomerConfig2(params) {
  const { customerId, options } = params;
  const includeMetadata = options?.includeMetadata ?? false;
  const customer = await getCustomerConfig(
    { customerId, includeMetadata },
    { executor: params.executor }
  );
  if (!customer) {
    throw new EntityNotFoundError(LEVELS.CUSTOMER, customerId);
  }
  const sqlResponse = customer.effective_config;
  const { __metadata, ...rawConfig } = sqlResponse;
  const filteredConfig = filterConfigByRole(rawConfig, options?.role);
  if (includeMetadata && __metadata) {
    const enrichedMetadata = enrichMetadata(__metadata, filteredConfig);
    return {
      ...filteredConfig,
      __metadata: enrichedMetadata
    };
  }
  return filteredConfig;
}

// src/fetchers/workflowset.ts
async function getWorkflowsetConfig2(params) {
  const { workflowsetId, options } = params;
  const includeMetadata = options?.includeMetadata ?? false;
  const ws = await getWorkflowsetConfig(
    { workflowsetId, includeMetadata },
    { executor: params.executor }
  );
  if (!ws) {
    throw new EntityNotFoundError(LEVELS.WORKFLOWSET, workflowsetId);
  }
  const sqlResponse = ws.effective_config;
  const { __metadata, ...rawConfig } = sqlResponse;
  const filteredConfig = filterConfigByRole(rawConfig, options?.role);
  if (includeMetadata && __metadata) {
    const enrichedMetadata = enrichMetadata(__metadata, filteredConfig);
    return {
      ...filteredConfig,
      __metadata: enrichedMetadata
    };
  }
  return filteredConfig;
}

// src/fetchers/contract.ts
async function getContractConfig2(params) {
  const { contractId, options } = params;
  const includeMetadata = options?.includeMetadata ?? false;
  const contract = await getContractConfig(
    { contractId, includeMetadata },
    { executor: params.executor }
  );
  if (!contract) {
    throw new EntityNotFoundError(LEVELS.CONTRACT, contractId);
  }
  const sqlResponse = contract.effective_config;
  const { __metadata, ...rawConfig } = sqlResponse;
  const filteredConfig = filterConfigByRole(rawConfig, options?.role);
  if (includeMetadata && __metadata) {
    const enrichedMetadata = enrichMetadata(__metadata, filteredConfig);
    return {
      ...filteredConfig,
      __metadata: enrichedMetadata
    };
  }
  return filteredConfig;
}
var getRateruleGroupConfig = getContractConfig2;

// src/fetchers/move.ts
async function getMoveConfig2(params) {
  const { moveId, options } = params;
  const includeMetadata = options?.includeMetadata ?? false;
  const move = await getMoveConfig(
    { moveId, includeMetadata },
    { executor: params.executor }
  );
  if (!move) {
    throw new EntityNotFoundError(LEVELS.MOVE, moveId);
  }
  const sqlResponse = move.effective_config;
  const { __metadata, ...rawConfig } = sqlResponse;
  const filteredConfig = filterConfigByRole(rawConfig, options?.role);
  if (includeMetadata && __metadata) {
    const enrichedMetadata = enrichMetadata(__metadata, filteredConfig);
    return {
      ...filteredConfig,
      __metadata: enrichedMetadata
    };
  }
  return filteredConfig;
}

// src/storage/query-generator.ts
var PACKAGE_VERSION = "0.14.0";
var DEFAULT_UPDATED_BY = `sdk-configs@${PACKAGE_VERSION}`;
function resolveUpdatedBy(updatedBy) {
  return updatedBy ?? DEFAULT_UPDATED_BY;
}
function formatTimestamp(date) {
  if (!date) {
    return (/* @__PURE__ */ new Date()).toISOString();
  }
  if (date instanceof Date) {
    return date.toISOString();
  }
  return date;
}
var QueryGeneratorError = class extends Error {
  constructor(message, code, context) {
    super(message);
    this.code = code;
    this.context = context;
    this.name = "QueryGeneratorError";
  }
};
var VALID_IDENTIFIER_PATTERN = /^[a-zA-Z_][a-zA-Z0-9_]*$/;
function isValidIdentifier(name) {
  return VALID_IDENTIFIER_PATTERN.test(name);
}
function validateIdentifier(name, type) {
  if (!name || typeof name !== "string") {
    throw new QueryGeneratorError(
      `Invalid ${type} name: ${type} name must be a non-empty string`,
      type === "table" ? "INVALID_TABLE_NAME" : "INVALID_FIELD_NAME",
      { name }
    );
  }
  if (!isValidIdentifier(name)) {
    throw new QueryGeneratorError(
      `Invalid ${type} name "${name}": must start with a letter or underscore and contain only alphanumeric characters and underscores`,
      type === "table" ? "INVALID_TABLE_NAME" : "INVALID_FIELD_NAME",
      { name }
    );
  }
}
function validateSelectionSet(select, context) {
  if (!select || typeof select !== "object" || Object.keys(select).length === 0) {
    throw new QueryGeneratorError(
      `Empty selection set for ${context}: at least one field must be selected`,
      "EMPTY_SELECTION_SET",
      { context }
    );
  }
}
function buildSelectionSetString(select, indent = "    ") {
  const lines = [];
  for (const [field, value] of Object.entries(select)) {
    validateIdentifier(field, "field");
    if (value === true) {
      lines.push(`${indent}${field}`);
    } else if (typeof value === "object" && value !== null) {
      lines.push(`${indent}${field} {`);
      lines.push(buildSelectionSetString(value, indent + "  "));
      lines.push(`${indent}}`);
    }
  }
  return lines.join("\n");
}
function buildWhereClauseString(where) {
  const parts = [];
  for (const [key, value] of Object.entries(where)) {
    if (key === "_and" || key === "_or") {
      const clauses = value;
      const clauseStrings = clauses.map((c) => buildWhereClauseString(c));
      parts.push(`${key}: [${clauseStrings.join(", ")}]`);
    } else if (key === "_not") {
      parts.push(`_not: ${buildWhereClauseString(value)}`);
    } else {
      validateIdentifier(key, "field");
      const operatorObj = value;
      const operators = [];
      for (const [op, opValue] of Object.entries(operatorObj)) {
        operators.push(`${op}: ${formatGraphQLValue(opValue)}`);
      }
      parts.push(`${key}: { ${operators.join(", ")} }`);
    }
  }
  return `{ ${parts.join(", ")} }`;
}
function buildOrderByClauseString(orderBy) {
  if (Array.isArray(orderBy)) {
    const items = orderBy.map((item) => buildSingleOrderBy(item));
    return `[${items.join(", ")}]`;
  }
  return buildSingleOrderBy(orderBy);
}
function buildSingleOrderBy(item) {
  const parts = [];
  for (const [field, direction] of Object.entries(item)) {
    validateIdentifier(field, "field");
    parts.push(`${field}: ${direction}`);
  }
  return `{ ${parts.join(", ")} }`;
}
function formatGraphQLValue(value) {
  if (value === null) {
    return "null";
  }
  if (typeof value === "string") {
    const escaped = value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
    return `"${escaped}"`;
  }
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value);
  }
  if (Array.isArray(value)) {
    return `[${value.map(formatGraphQLValue).join(", ")}]`;
  }
  if (typeof value === "object") {
    const parts = Object.entries(value).map(([k, v]) => `${k}: ${formatGraphQLValue(v)}`);
    return `{ ${parts.join(", ")} }`;
  }
  return String(value);
}
function toPascalCase(str) {
  return str.split(/[-_\s]+/).map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()).join("");
}
function getLevelIdType(level) {
  if (level === "workflowset") {
    return "Int!";
  }
  return "bigint!";
}
function getLevelVariableName(level) {
  return `${level}Id`;
}
function generateVariablePrefix(definitionKey, level) {
  const camelKey = definitionKey.split("_").map((word, i) => i === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)).join("");
  return `${camelKey}_${level}`;
}
function prefixVariable(varName, prefix) {
  return `${prefix}_${varName}`;
}
function generateEnumOptionsQuery(enumConfig) {
  const { from, select, where, orderBy } = enumConfig;
  validateIdentifier(from, "table");
  validateSelectionSet(select, `enum source "${from}"`);
  const operationName = `Get${toPascalCase(from)}Options`;
  const args = [];
  if (where && Object.keys(where).length > 0) {
    args.push(`where: ${buildWhereClauseString(where)}`);
  }
  if (orderBy) {
    args.push(`order_by: ${buildOrderByClauseString(orderBy)}`);
  }
  const argsString = args.length > 0 ? `(${args.join(", ")})` : "";
  const selectionSet = buildSelectionSetString(select);
  const query = `query ${operationName} {
  ${from}${argsString} {
${selectionSet}
  }
}`;
  return {
    query,
    operationName,
    variableTypes: {}
  };
}
function generateLinkTableGetterQuery(config, level) {
  const { table, foreignKey, valueKey } = config;
  validateIdentifier(table, "table");
  validateIdentifier(foreignKey, "field");
  validateIdentifier(valueKey, "field");
  const operationName = `Get${toPascalCase(table)}`;
  const variableName = getLevelVariableName(level);
  const variableType = getLevelIdType(level);
  const query = `query ${operationName}($${variableName}: ${variableType}) {
  ${table}(where: { ${foreignKey}: { _eq: $${variableName} } }) {
    ${valueKey}
  }
}`;
  return {
    query,
    operationName,
    variableTypes: { [variableName]: variableType }
  };
}
function generateLinkTableSetterMutationInternal(config, level, definitionKey, forBatching) {
  const { table, foreignKey } = config;
  validateIdentifier(table, "table");
  validateIdentifier(foreignKey, "field");
  const baseIdVar = getLevelVariableName(level);
  const variableType = getLevelIdType(level);
  const insertInputType = `[${table}_insert_input!]!`;
  const prefix = forBatching && definitionKey ? generateVariablePrefix(definitionKey, level) : void 0;
  const idVar = prefix ? prefixVariable(baseIdVar, prefix) : baseIdVar;
  const objectsVar = prefix ? prefixVariable("objects", prefix) : "objects";
  const deleteFieldName = `delete_${table}`;
  const deleteFieldBody = `(where: { ${foreignKey}: { _eq: $${idVar} } }) {
    affected_rows
  }`;
  const insertFieldName = `insert_${table}`;
  const insertFieldBody = `(objects: $${objectsVar}) {
    affected_rows
  }`;
  const operationName = `Set${toPascalCase(table)}`;
  const query = `mutation ${operationName}($${idVar}: ${variableType}, $${objectsVar}: ${insertInputType}) {
  ${deleteFieldName}${deleteFieldBody}
  ${insertFieldName}${insertFieldBody}
}`;
  const result = {
    query,
    operationName,
    variableTypes: {
      [idVar]: variableType,
      [objectsVar]: insertInputType
    }
  };
  if (forBatching && prefix) {
    result.variablePrefix = prefix;
    result.mutationFields = [
      {
        fieldName: deleteFieldName,
        fieldBody: deleteFieldBody,
        variables: [idVar]
      },
      {
        fieldName: insertFieldName,
        fieldBody: insertFieldBody,
        variables: [objectsVar]
      }
    ];
  }
  return result;
}
function generateLinkTableSetterMutation(config, level) {
  return generateLinkTableSetterMutationInternal(config, level, void 0, false);
}
function generateJsonbGetterQuery(definitionKey, level) {
  const levelInfo = getConfigLevelInfo(level);
  if (!levelInfo || !levelInfo.table) {
    throw new QueryGeneratorError(
      `Cannot generate JSONB getter for level "${level}": level has no associated table`,
      "INVALID_LEVEL",
      { level }
    );
  }
  const { tableName } = levelInfo.table;
  const operationName = `Storage${toPascalCase(level)}ConfigGet`;
  const variableName = getLevelVariableName(level);
  const variableType = getLevelIdType(level);
  const query = `query ${operationName}($${variableName}: ${variableType}) {
  ${tableName}_by_pk(id: $${variableName}) {
    config
  }
}`;
  return {
    query,
    operationName,
    variableTypes: { [variableName]: variableType }
  };
}
function generateJsonbSetterMutationInternal(definitionKey, level, path, forBatching) {
  const levelInfo = getConfigLevelInfo(level);
  if (!levelInfo || !levelInfo.table) {
    throw new QueryGeneratorError(
      `Cannot generate JSONB setter for level "${level}": level has no associated table`,
      "INVALID_LEVEL",
      { level }
    );
  }
  const { tableName } = levelInfo.table;
  const baseIdVar = getLevelVariableName(level);
  const variableType = getLevelIdType(level);
  const prefix = forBatching ? generateVariablePrefix(definitionKey, level) : void 0;
  const idVar = prefix ? prefixVariable(baseIdVar, prefix) : baseIdVar;
  const configVar = prefix ? prefixVariable("config", prefix) : "config";
  const fieldName = `update_${tableName}_by_pk`;
  const prepareFieldAlias = `prepare_${tableName}`;
  const prepareFieldName = `update_${tableName}`;
  const prepareFieldBody = `(where: { id: { _eq: $${idVar} }, config: { _is_null: true } }, _set: { config: {} }) {
    affected_rows
  }`;
  const fieldBody = `(pk_columns: { id: $${idVar} }, _append: { config: $${configVar} }) {
    id
    config
  }`;
  const operationName = `Storage${toPascalCase(level)}ConfigSet`;
  const query = `mutation ${operationName}($${idVar}: ${variableType}, $${configVar}: jsonb!) {
  ${prepareFieldAlias}: ${prepareFieldName}${prepareFieldBody}
  ${fieldName}${fieldBody}
}`;
  const result = {
    query,
    operationName,
    variableTypes: {
      [idVar]: variableType,
      [configVar]: "jsonb!"
    }
  };
  if (forBatching && prefix) {
    result.variablePrefix = prefix;
    result.mutationFields = [
      {
        fieldName: prepareFieldName,
        fieldBody: prepareFieldBody,
        variables: [idVar]
      },
      {
        fieldName,
        fieldBody,
        variables: [idVar, configVar]
      }
    ];
  }
  return result;
}
function generateJsonbSetterMutation(definitionKey, level, path) {
  return generateJsonbSetterMutationInternal(definitionKey, level, path, false);
}
function generateJsonbDeleteMutation(definitionKey, level, forBatching) {
  const levelInfo = getConfigLevelInfo(level);
  if (!levelInfo || !levelInfo.table) {
    throw new QueryGeneratorError(
      `Level "${level}" does not have a table configured`,
      "INVALID_LEVEL",
      { level, definitionKey }
    );
  }
  const { tableName } = levelInfo.table;
  const baseIdVar = getLevelVariableName(level);
  const variableType = getLevelIdType(level);
  const prefix = forBatching ? generateVariablePrefix(definitionKey, level) : void 0;
  const idVar = prefix ? prefixVariable(baseIdVar, prefix) : baseIdVar;
  const keyVar = prefix ? prefixVariable("deleteKey", prefix) : "deleteKey";
  const fieldName = `update_${tableName}_by_pk`;
  const fieldBody = `(pk_columns: { id: $${idVar} }, _delete_key: { config: $${keyVar} }) {
    id
  }`;
  const operationName = `Delete${toPascalCase(level)}ConfigKey`;
  const query = `mutation ${operationName}($${idVar}: ${variableType}, $${keyVar}: String!) {
  ${fieldName}${fieldBody}
}`;
  const result = {
    query,
    operationName,
    variableTypes: {
      [idVar]: variableType,
      [keyVar]: "String!"
    }
  };
  if (forBatching && prefix) {
    result.variablePrefix = prefix;
    result.mutationFields = [
      {
        fieldName,
        fieldBody,
        variables: [idVar, keyVar]
      }
    ];
  }
  return result;
}
function generateDedicatedColumnGetterQuery(config, level) {
  const { table, column } = config;
  validateIdentifier(table, "table");
  validateIdentifier(column, "field");
  const operationName = `Get${toPascalCase(table)}${toPascalCase(column)}`;
  const variableName = getLevelVariableName(level);
  const variableType = getLevelIdType(level);
  const query = `query ${operationName}($${variableName}: ${variableType}) {
  ${table}_by_pk(id: $${variableName}) {
    ${column}
  }
}`;
  return {
    query,
    operationName,
    variableTypes: { [variableName]: variableType }
  };
}
function generateDedicatedColumnSetterMutationInternal(config, level, definitionKey, forBatching) {
  const { table, column, valueType, auditColumns } = config;
  validateIdentifier(table, "table");
  validateIdentifier(column, "field");
  const baseIdVar = getLevelVariableName(level);
  const variableType = getLevelIdType(level);
  const columnValueType = valueType || "String";
  const prefix = forBatching && definitionKey ? generateVariablePrefix(definitionKey, level) : void 0;
  const idVar = prefix ? prefixVariable(baseIdVar, prefix) : baseIdVar;
  const valueVar = prefix ? prefixVariable("value", prefix) : "value";
  const setFields = [`${column}: $${valueVar}`];
  const variableTypes = {
    [idVar]: variableType,
    [valueVar]: columnValueType
  };
  const fieldVariables = [idVar, valueVar];
  if (auditColumns?.updatedByColumn) {
    const updatedByVar = prefix ? prefixVariable("updatedBy", prefix) : "updatedBy";
    setFields.push(`${auditColumns.updatedByColumn}: $${updatedByVar}`);
    variableTypes[updatedByVar] = "String!";
    fieldVariables.push(updatedByVar);
  }
  if (auditColumns?.updatedAtColumn) {
    const updatedAtVar = prefix ? prefixVariable("updatedAt", prefix) : "updatedAt";
    setFields.push(`${auditColumns.updatedAtColumn}: $${updatedAtVar}`);
    variableTypes[updatedAtVar] = "timestamptz!";
    fieldVariables.push(updatedAtVar);
  }
  const fieldName = `update_${table}_by_pk`;
  const fieldBody = `(pk_columns: { id: $${idVar} }, _set: { ${setFields.join(", ")} }) {
    id
    ${column}
  }`;
  const operationName = `Set${toPascalCase(table)}${toPascalCase(column)}`;
  const varDefsStr = Object.entries(variableTypes).map(([name, type]) => `$${name}: ${type}`).join(", ");
  const query = `mutation ${operationName}(${varDefsStr}) {
  ${fieldName}${fieldBody}
}`;
  const result = {
    query,
    operationName,
    variableTypes
  };
  if (forBatching && prefix) {
    result.variablePrefix = prefix;
    result.mutationFields = [
      {
        fieldName,
        fieldBody,
        variables: fieldVariables
      }
    ];
  }
  return result;
}
function generateDedicatedColumnSetterMutation(config, level) {
  return generateDedicatedColumnSetterMutationInternal(config, level, void 0, false);
}
function generateStorageQueries(definition, options) {
  const result = {
    getterQueries: {},
    setterMutations: {},
    deleteMutations: {}
  };
  const storage = definition.storage;
  const forBatching = options?.forBatching ?? false;
  if (!storage) {
    for (const level of definition.writableAtLevels) {
      const levelInfo = getConfigLevelInfo(level);
      if (levelInfo?.table) {
        result.getterQueries[level] = generateJsonbGetterQuery(definition.key, level);
        result.setterMutations[level] = generateJsonbSetterMutationInternal(
          definition.key,
          level,
          void 0,
          forBatching
        );
        result.deleteMutations[level] = generateJsonbDeleteMutation(
          definition.key,
          level,
          forBatching
        );
      }
    }
    return result;
  }
  if (storage.enum) {
    result.enumOptionsQuery = generateEnumOptionsQuery(storage.enum);
  }
  for (const level of definition.writableAtLevels) {
    const levelConfig = storage.levels?.[level];
    const levelInfo = getConfigLevelInfo(level);
    if (!levelInfo?.table) {
      continue;
    }
    if (!levelConfig) {
      result.getterQueries[level] = generateJsonbGetterQuery(definition.key, level);
      result.setterMutations[level] = generateJsonbSetterMutationInternal(
        definition.key,
        level,
        void 0,
        forBatching
      );
      result.deleteMutations[level] = generateJsonbDeleteMutation(
        definition.key,
        level,
        forBatching
      );
    } else if (levelConfig.type === "link_table") {
      result.getterQueries[level] = generateLinkTableGetterQuery(levelConfig, level);
      result.setterMutations[level] = generateLinkTableSetterMutationInternal(
        levelConfig,
        level,
        definition.key,
        forBatching
      );
    } else if (levelConfig.type === "jsonb") {
      const path = levelConfig.path || definition.key;
      result.getterQueries[level] = generateJsonbGetterQuery(definition.key, level);
      result.setterMutations[level] = generateJsonbSetterMutationInternal(
        definition.key,
        level,
        path,
        forBatching
      );
      result.deleteMutations[level] = generateJsonbDeleteMutation(
        definition.key,
        level,
        forBatching
      );
    } else if (levelConfig.type === "dedicated_column") {
      result.getterQueries[level] = generateDedicatedColumnGetterQuery(levelConfig, level);
      result.setterMutations[level] = generateDedicatedColumnSetterMutationInternal(
        levelConfig,
        level,
        definition.key,
        forBatching
      );
    } else {
      throw new QueryGeneratorError(
        `Unsupported storage type for level "${level}"`,
        "UNSUPPORTED_STORAGE_TYPE",
        { level, config: levelConfig }
      );
    }
  }
  return result;
}
function buildLinkTableInsertObjects(foreignKeyColumn, foreignKeyValue, valueKeyColumn, values, auditConfig, auditValues) {
  const updatedBy = auditValues?.updatedBy ?? DEFAULT_UPDATED_BY;
  const timestamp = formatTimestamp(auditValues?.updatedAt);
  return values.map((value) => {
    const obj = {
      [foreignKeyColumn]: foreignKeyValue,
      [valueKeyColumn]: value
    };
    if (auditConfig) {
      if (auditConfig.createdByColumn) {
        obj[auditConfig.createdByColumn] = updatedBy;
      }
      if (auditConfig.updatedByColumn) {
        obj[auditConfig.updatedByColumn] = updatedBy;
      }
      if (auditConfig.createdAtColumn) {
        obj[auditConfig.createdAtColumn] = timestamp;
      }
      if (auditConfig.updatedAtColumn) {
        obj[auditConfig.updatedAtColumn] = timestamp;
      }
    }
    return obj;
  });
}
function extractLinkTableValues(rows, valueKey) {
  return rows.map((row) => row[valueKey]);
}
function combineMutations(mutations, operationName = "BatchedMutation") {
  if (mutations.length === 0) {
    return {
      query: `mutation ${operationName} { __typename }`,
      operationName,
      variableTypes: {},
      variablePrefixes: {}
    };
  }
  for (const mut of mutations) {
    if (!mut.mutationFields || !mut.variablePrefix) {
      throw new QueryGeneratorError(
        "All mutations must be generated with forBatching: true",
        "MISSING_REQUIRED_FIELD",
        { operationName: mut.operationName }
      );
    }
  }
  const mergedVariableTypes = {};
  const variablePrefixes = {};
  for (const mut of mutations) {
    Object.assign(mergedVariableTypes, mut.variableTypes);
    if (mut.variablePrefix) {
      variablePrefixes[mut.operationName] = mut.variablePrefix;
    }
  }
  const varDefs = Object.entries(mergedVariableTypes).map(([name, type]) => `$${name}: ${type}`).join(", ");
  const fields = [];
  const aliasCounter = /* @__PURE__ */ new Map();
  for (const mut of mutations) {
    for (const field of mut.mutationFields) {
      const cleanFieldSuffix = field.fieldName.replace(/^(delete_|insert_|update_)/, "");
      let alias = `${mut.variablePrefix}_${cleanFieldSuffix}`;
      const baseAlias = alias;
      const count = aliasCounter.get(baseAlias) || 0;
      if (count > 0) {
        alias = `${baseAlias}_${count}`;
      }
      aliasCounter.set(baseAlias, count + 1);
      fields.push(`  ${alias}: ${field.fieldName}${field.fieldBody}`);
    }
  }
  const query = `mutation ${operationName}(${varDefs}) {
${fields.join("\n")}
}`;
  return {
    query,
    operationName,
    variableTypes: mergedVariableTypes,
    variablePrefixes
  };
}

// src/editable/compose-settings-view.ts
var UNSET_SOURCE_LEVEL = "unset";
function buildSettingJsonSchema(key) {
  const schema = getJsonSchemaForSetting(key);
  if (!schema) return null;
  return Object.fromEntries(Object.entries(schema).filter(([k]) => !k.startsWith("x-hopdrive-")));
}
function getSettingAccess(definition, role, level) {
  if (!definition.readableByRoles.includes(role)) return "hidden";
  if (!effectiveReadableAtLevels(definition).includes(level)) return "hidden";
  if (!definition.writableAtLevels.includes(level)) return "readonly";
  return effectiveWritableByRoles(definition).includes(role) ? "editable" : "readonly";
}
function composeSettingsView(params) {
  const { role, level, effectiveConfig, sourceLevels, storedAtLevel } = params;
  const view = [];
  for (const definition of Object.values(allDefinitions)) {
    if (definition.kind !== DEFINITION_KINDS.SETTING) continue;
    const access = getSettingAccess(definition, role, level);
    if (access === "hidden") continue;
    const stored = storedAtLevel?.[definition.key];
    view.push({
      key: definition.key,
      label: definition.label,
      description: definition.description,
      warning: definition.warning ?? null,
      category: definition.category ?? "uncategorized",
      kind: definition.kind,
      jsonSchema: buildSettingJsonSchema(definition.key) ?? {},
      currentValue: stored === void 0 ? null : stored,
      effectiveValue: effectiveConfig[definition.key] ?? null,
      sourceLevel: sourceLevels[definition.key] ?? UNSET_SOURCE_LEVEL,
      deprecated: Boolean(definition.deprecated),
      access
    });
  }
  return view.sort((a, b) => a.key.localeCompare(b.key));
}
function composeEditableSettings(params) {
  return composeSettingsView(params).filter((setting) => setting.access === "editable").map(({ access: _access, ...setting }) => setting);
}

// src/_generated/hopdrive-config-schema.ts
import { z } from "zod";
var HopDriveConfigSchema = z.object({
  version: allDefinitions.version.schema.optional(),
  api_enabled: allDefinitions.api_enabled.schema.optional(),
  api_config: allDefinitions.api_config.schema.optional(),
  price_estimates: allDefinitions.price_estimates.schema.optional(),
  distance_estimates: allDefinitions.distance_estimates.schema.optional(),
  allow_email_notifications: allDefinitions.allow_email_notifications.schema.optional(),
  notify_contact_of_outcomes: allDefinitions.notify_contact_of_outcomes.schema.optional(),
  dealer_visibility_enabled: allDefinitions.dealer_visibility_enabled.schema.optional(),
  customer_feedback_recipients_fallback: allDefinitions.customer_feedback_recipients_fallback.schema.optional(),
  allow_triangle_moves: allDefinitions.allow_triangle_moves.schema.optional(),
  enable_csv_import: allDefinitions.enable_csv_import.schema.optional(),
  public_workflowsets: allDefinitions.public_workflowsets.schema.optional(),
  rideshare_calling_enabled: allDefinitions.rideshare_calling_enabled.schema.optional(),
  ride_provider_algorithm_key: allDefinitions.ride_provider_algorithm_key.schema.optional(),
  passthrough_rideshare_surges: allDefinitions.passthrough_rideshare_surges.schema.optional(),
  require_third_party_payer: allDefinitions.require_third_party_payer.schema.optional(),
  auto_assign_ref: allDefinitions.auto_assign_ref.schema.optional(),
  default_move_type: allDefinitions.default_move_type.schema.optional(),
  allowed_move_types: allDefinitions.allowed_move_types.schema.optional(),
  allowed_templates: allDefinitions.allowed_templates.schema.optional(),
  add_moves_always: allDefinitions.add_moves_always.schema.optional(),
  sixt_settings: allDefinitions.sixt_settings.schema.optional(),
  add_moves_disabled: allDefinitions.add_moves_disabled.schema.optional(),
  cancellation_fee: allDefinitions.cancellation_fee.schema.optional(),
  delay_price_per_minute: allDefinitions.delay_price_per_minute.schema.optional(),
  dry_run_behavior: allDefinitions.dry_run_behavior.schema.optional(),
  fuel_multiplier: allDefinitions.fuel_multiplier.schema.optional(),
  default_workflow_set_id: allDefinitions.default_workflow_set_id.schema.optional(),
  pickup_workflow_id: allDefinitions.pickup_workflow_id.schema.optional(),
  delivery_workflow_id: allDefinitions.delivery_workflow_id.schema.optional(),
  default_pickup_workflow_id: allDefinitions.default_pickup_workflow_id.schema.optional(),
  default_delivery_workflow_id: allDefinitions.default_delivery_workflow_id.schema.optional(),
  move_workflow_overrides: allDefinitions.move_workflow_overrides.schema.optional(),
  default_payer: allDefinitions.default_payer.schema.optional(),
  reference_num_label: allDefinitions.reference_num_label.schema.optional(),
  reference_num_required_for_pickup: allDefinitions.reference_num_required_for_pickup.schema.optional(),
  reference_num_required_for_return: allDefinitions.reference_num_required_for_return.schema.optional(),
  reference_num_report_enabled: allDefinitions.reference_num_report_enabled.schema.optional(),
  reference_num_report_label: allDefinitions.reference_num_report_label.schema.optional(),
  reporting: allDefinitions.reporting.schema.optional(),
  serviceable_radius_miles: allDefinitions.serviceable_radius_miles.schema.optional(),
  customer_serviceable_radius_miles: allDefinitions.customer_serviceable_radius_miles.schema.optional(),
  serviceable_radius_by_delivery: allDefinitions.serviceable_radius_by_delivery.schema.optional(),
  serviceable_radius_by_pickup: allDefinitions.serviceable_radius_by_pickup.schema.optional(),
  ride_type_override: allDefinitions.ride_type_override.schema.optional(),
  branding: allDefinitions.branding.schema.optional(),
  appraisals: allDefinitions.appraisals.schema.optional(),
  appointments: allDefinitions.appointments.schema.optional(),
  appointment_management: allDefinitions.appointment_management.schema.optional(),
  hopdrive_contacts: allDefinitions.hopdrive_contacts.schema.optional(),
  customer_values: allDefinitions.customer_values.schema.optional(),
  fuel: allDefinitions.fuel.schema.optional(),
  fraud_audit: allDefinitions.fraud_audit.schema.optional(),
  gps_transition_mode: allDefinitions.gps_transition_mode.schema.optional(),
  arrival_policy_arrive: allDefinitions.arrival_policy_arrive.schema.optional(),
  arrival_policy_depart: allDefinitions.arrival_policy_depart.schema.optional(),
  pickup_start_gate: allDefinitions.pickup_start_gate.schema.optional(),
  gps_optimistic_arrivals: allDefinitions.gps_optimistic_arrivals.schema.optional(),
  gps_optimistic_departures: allDefinitions.gps_optimistic_departures.schema.optional(),
  geofence_default_radius_meters: allDefinitions.geofence_default_radius_meters.schema.optional(),
  geofence_corroboration_window_minutes: allDefinitions.geofence_corroboration_window_minutes.schema.optional(),
  geofence_corroboration_min_pings: allDefinitions.geofence_corroboration_min_pings.schema.optional(),
  geofence_corroboration_accuracy_cutoff_meters: allDefinitions.geofence_corroboration_accuracy_cutoff_meters.schema.optional(),
  geofence_corroboration_drift_buffer_meters: allDefinitions.geofence_corroboration_drift_buffer_meters.schema.optional(),
  gps_stale_pickup_started_minutes: allDefinitions.gps_stale_pickup_started_minutes.schema.optional(),
  gps_stale_pickup_arrived_minutes: allDefinitions.gps_stale_pickup_arrived_minutes.schema.optional(),
  gps_stale_pickup_successful_minutes: allDefinitions.gps_stale_pickup_successful_minutes.schema.optional(),
  gps_stale_delivery_started_minutes: allDefinitions.gps_stale_delivery_started_minutes.schema.optional(),
  gps_stale_delivery_arrived_minutes: allDefinitions.gps_stale_delivery_arrived_minutes.schema.optional(),
  route_corridor_off_route: allDefinitions.route_corridor_off_route.schema.optional(),
  route_deviation_alerts: allDefinitions.route_deviation_alerts.schema.optional(),
  route_corridor_rejoin: allDefinitions.route_corridor_rejoin.schema.optional(),
  route_gps_accuracy_cutoff: allDefinitions.route_gps_accuracy_cutoff.schema.optional(),
  route_stop_dwell_threshold: allDefinitions.route_stop_dwell_threshold.schema.optional(),
  route_stop_radius: allDefinitions.route_stop_radius.schema.optional(),
  route_stop_on_route_distance: allDefinitions.route_stop_on_route_distance.schema.optional(),
  route_compliance_excess_distance: allDefinitions.route_compliance_excess_distance.schema.optional(),
  route_compliance_excess_time: allDefinitions.route_compliance_excess_time.schema.optional(),
  program_visuals: allDefinitions.program_visuals.schema.optional(),
  program_reporting: allDefinitions.program_reporting.schema.optional(),
  guestrides: allDefinitions.guestrides.schema.optional(),
  guestrides_access: allDefinitions.guestrides_access.schema.optional(),
  product_history: allDefinitions.product_history.schema.optional(),
  dms: allDefinitions.dms.schema.optional(),
  scheduler: allDefinitions.scheduler.schema.optional(),
  email_scrape_config: allDefinitions.email_scrape_config.schema.optional(),
  rc_config: allDefinitions.rc_config.schema.optional(),
  private: allDefinitions.private.schema.optional(),
  routing: allDefinitions.routing.schema.optional(),
  allow_vinless_valet: allDefinitions.allow_vinless_valet.schema.optional(),
  auto_create_valet_moves: allDefinitions.auto_create_valet_moves.schema.optional(),
  ar: allDefinitions.ar.schema.optional(),
  mobile_service: allDefinitions.mobile_service.schema.optional(),
  mobile_service_claims: allDefinitions.mobile_service_claims.schema.optional(),
  shuttle: allDefinitions.shuttle.schema.optional(),
  parts_delivery: allDefinitions.parts_delivery.schema.optional(),
  driver_pipeline: allDefinitions.driver_pipeline.schema.optional(),
  connector_triggers: allDefinitions.connector_triggers.schema.optional(),
  minimum_certification_ids: allDefinitions.minimum_certification_ids.schema.optional(),
  billing_frequency: allDefinitions.billing_frequency.schema.optional(),
  payment_terms: allDefinitions.payment_terms.schema.optional(),
  auto_pay: allDefinitions.auto_pay.schema.optional(),
  isShuttle: allDefinitions.isShuttle.schema.optional(),
  firstShuttleMove: allDefinitions.firstShuttleMove.schema.optional(),
  lastShuttleMove: allDefinitions.lastShuttleMove.schema.optional(),
  chase_id: allDefinitions.chase_id.schema.optional(),
  parent_chase_id: allDefinitions.parent_chase_id.schema.optional(),
  batch: allDefinitions.batch.schema.optional(),
  ride_orchestration: allDefinitions.ride_orchestration.schema.optional(),
  superDispatch: allDefinitions.superDispatch.schema.optional(),
  superdispatch_routing: allDefinitions.superdispatch_routing.schema.optional(),
  superdispatch_settings: allDefinitions.superdispatch_settings.schema.optional(),
  superdispatch_subscription: allDefinitions.superdispatch_subscription.schema.optional(),
  carsarrive_connection: allDefinitions.carsarrive_connection.schema.optional(),
  carsarrive_routing: allDefinitions.carsarrive_routing.schema.optional(),
  carsarrive_settings: allDefinitions.carsarrive_settings.schema.optional(),
  referenceNumbers: allDefinitions.referenceNumbers.schema.optional(),
  isGhost: allDefinitions.isGhost.schema.optional(),
  vin: allDefinitions.vin.schema.optional(),
  triangle_move: allDefinitions.triangle_move.schema.optional(),
  isFlex: allDefinitions.isFlex.schema.optional(),
  moveInput: allDefinitions.moveInput.schema.optional(),
  loaner_vin_edit_disabled: allDefinitions.loaner_vin_edit_disabled.schema.optional()
}).describe(
  "Complete HopDrive configuration. All properties optional (sparse configs merged hierarchically)."
);
function validateHopDriveConfig(config) {
  return HopDriveConfigSchema.parse(config);
}
function safeValidateHopDriveConfig(config) {
  return HopDriveConfigSchema.safeParse(config);
}

// src/hopdrive-default-config.ts
var HOPDRIVE_DEFAULT_CONFIG = Object.fromEntries(
  Object.entries(allDefinitions).filter(([, def]) => "defaultValue" in def && def.defaultValue !== void 0).map(([key, def]) => [key, def.defaultValue])
);
function getHopdriveDefaults() {
  return { ...HOPDRIVE_DEFAULT_CONFIG };
}

// src/encryption/decrypt.ts
import * as crypto from "crypto";
import { getLogger } from "@hopdrive/sdk-core";
function getAllEncryptionVersions() {
  return getEncryptionVersions();
}
var ALGORITHM = "aes-256-gcm";
var IV_LENGTH = 12;
var AUTH_TAG_LENGTH = 16;
function getKeyForVersion(version) {
  const normalizedVersion = version.toLowerCase();
  const keyHex = getKeyForVersionInternal(normalizedVersion);
  if (!keyHex) {
    const available = getAvailableEncryptionVersions();
    throw new EncryptionKeyNotFoundError(normalizedVersion, available);
  }
  const keyBuffer = Buffer.from(keyHex, "hex");
  if (keyBuffer.length !== 32) {
    throw new Error(
      `Invalid key length for version "${version}". Expected 32 bytes (64 hex characters), got ${keyBuffer.length} bytes.`
    );
  }
  return keyBuffer;
}
function decryptSecret(encryptedValue) {
  if (!isEncryptedString(encryptedValue)) {
    throw new Error("Value is not a valid encrypted string format");
  }
  const version = getEncryptionVersion(encryptedValue);
  const ciphertext = getCiphertext(encryptedValue);
  const parts = ciphertext.split(":");
  if (parts.length !== 3) {
    throw new Error("Invalid ciphertext format. Expected {iv}:{authTag}:{encrypted}");
  }
  const [ivBase64, authTagBase64, encryptedBase64] = parts;
  const iv = Buffer.from(ivBase64, "base64");
  const authTag = Buffer.from(authTagBase64, "base64");
  const encrypted = Buffer.from(encryptedBase64, "base64");
  if (iv.length !== IV_LENGTH) {
    throw new Error(`Invalid IV length. Expected ${IV_LENGTH} bytes, got ${iv.length}`);
  }
  if (authTag.length !== AUTH_TAG_LENGTH) {
    throw new Error(`Invalid auth tag length. Expected ${AUTH_TAG_LENGTH} bytes, got ${authTag.length}`);
  }
  const key = getKeyForVersion(version);
  const decipher = crypto.createDecipheriv(ALGORITHM, key, iv);
  decipher.setAuthTag(authTag);
  let decrypted = decipher.update(encrypted);
  decrypted = Buffer.concat([decrypted, decipher.final()]);
  return decrypted.toString("utf8");
}
function encryptSecret(plaintext, version) {
  const log = getLogger();
  const targetVersion = version ?? getPreferredEncryptionVersion();
  if (!targetVersion) {
    throw new Error(
      "No active encryption key version available. Please ensure encryption keys are provided during init()."
    );
  }
  const metadata = getEncryptionVersionMetadata(targetVersion);
  if (metadata?.status === "deprecated") {
    log.warn("configs.decrypt.deprecatedKeyVersion", {
      version: targetVersion,
      activeVersions: getActiveEncryptionVersions().join(", ") || "none"
    });
  }
  const key = getKeyForVersion(targetVersion);
  const iv = crypto.randomBytes(IV_LENGTH);
  const cipher = crypto.createCipheriv(ALGORITHM, key, iv);
  let encrypted = cipher.update(plaintext, "utf8");
  encrypted = Buffer.concat([encrypted, cipher.final()]);
  const authTag = cipher.getAuthTag();
  const ciphertext = [iv.toString("base64"), authTag.toString("base64"), encrypted.toString("base64")].join(":");
  return `encrypted:${targetVersion}:${ciphertext}`;
}
function decryptSecrets(secrets) {
  const log = getLogger();
  const decrypted = {};
  for (const [key, value] of Object.entries(secrets)) {
    if (isEncryptedString(value)) {
      decrypted[key] = decryptSecret(value);
    } else {
      log.warn("configs.decrypt.unencryptedValue", {
        key
      });
      decrypted[key] = value;
    }
  }
  return decrypted;
}
function reEncryptSecret(encryptedValue, newVersion) {
  const plaintext = decryptSecret(encryptedValue);
  return encryptSecret(plaintext, newVersion);
}

// src/encryption/key-management.ts
function isEncryptedStringSchema(schema) {
  const def = schema._def;
  if (def.typeName === "ZodBranded") {
    const innerDef = def.type?._def;
    if (innerDef?.typeName === "ZodEffects" && innerDef.schema?._def?.typeName === "ZodString") {
      return true;
    }
  }
  try {
    const testValue = "encrypted:v1:test";
    const resultA = schema.safeParse(testValue);
    const resultB = encryptedString.safeParse(testValue);
    if (resultA.success && resultB.success) {
      const invalidResult = schema.safeParse("not-encrypted");
      const invalidExpected = encryptedString.safeParse("not-encrypted");
      return invalidResult.success === invalidExpected.success && !invalidResult.success;
    }
  } catch {
  }
  return false;
}
function findEncryptedPathsInSchema(schema, currentPath = "") {
  const paths = [];
  const def = schema._def;
  if (isEncryptedStringSchema(schema)) {
    paths.push(currentPath || ".");
    return paths;
  }
  switch (def.typeName) {
    case "ZodObject": {
      const shape = def.shape?.() ?? {};
      for (const [key, value] of Object.entries(shape)) {
        const newPath = currentPath ? `${currentPath}.${key}` : key;
        paths.push(...findEncryptedPathsInSchema(value, newPath));
      }
      break;
    }
    case "ZodRecord": {
      const valueType = def.valueType;
      const wildcardPath = currentPath ? `${currentPath}.*` : "*";
      const valuePaths = findEncryptedPathsInSchema(valueType, "");
      for (const valuePath of valuePaths) {
        if (valuePath === ".") {
          paths.push(wildcardPath);
        } else {
          paths.push(`${wildcardPath}${valuePath}`);
        }
      }
      break;
    }
    case "ZodArray": {
      const elementType = def.type;
      const arrayPath = currentPath ? `${currentPath}[*]` : "[*]";
      const elementPaths = findEncryptedPathsInSchema(elementType, "");
      for (const elementPath of elementPaths) {
        if (elementPath === ".") {
          paths.push(arrayPath);
        } else {
          paths.push(`${arrayPath}${elementPath}`);
        }
      }
      break;
    }
    case "ZodOptional":
    case "ZodNullable":
    case "ZodDefault": {
      const innerType = def.innerType;
      paths.push(...findEncryptedPathsInSchema(innerType, currentPath));
      break;
    }
    case "ZodEffects": {
      const innerSchema = def.schema;
      paths.push(...findEncryptedPathsInSchema(innerSchema, currentPath));
      break;
    }
    case "ZodUnion":
    case "ZodDiscriminatedUnion": {
      const options = def.options || [];
      for (const option of options) {
        paths.push(...findEncryptedPathsInSchema(option, currentPath));
      }
      break;
    }
    case "ZodIntersection": {
      if (def.left) paths.push(...findEncryptedPathsInSchema(def.left, currentPath));
      if (def.right) paths.push(...findEncryptedPathsInSchema(def.right, currentPath));
      break;
    }
    case "ZodLazy": {
      try {
        const lazySchema = def.getter();
        paths.push(...findEncryptedPathsInSchema(lazySchema, currentPath));
      } catch {
      }
      break;
    }
    case "ZodTuple": {
      const items = def.items || [];
      items.forEach((item, index) => {
        const tuplePath = currentPath ? `${currentPath}[${index}]` : `[${index}]`;
        paths.push(...findEncryptedPathsInSchema(item, tuplePath));
      });
      break;
    }
    // Primitive types that cannot contain encrypted strings
    case "ZodString":
    case "ZodNumber":
    case "ZodBoolean":
    case "ZodDate":
    case "ZodBigInt":
    case "ZodSymbol":
    case "ZodUndefined":
    case "ZodNull":
    case "ZodVoid":
    case "ZodNever":
    case "ZodAny":
    case "ZodUnknown":
    case "ZodLiteral":
    case "ZodEnum":
    case "ZodNativeEnum":
      break;
    default:
      if (def.innerType) {
        paths.push(...findEncryptedPathsInSchema(def.innerType, currentPath));
      }
      if (def.type) {
        paths.push(...findEncryptedPathsInSchema(def.type, currentPath));
      }
      break;
  }
  return paths;
}
var cachedEncryptedFieldSchemas = null;
function discoverEncryptedFieldSchemas() {
  if (cachedEncryptedFieldSchemas !== null) {
    return cachedEncryptedFieldSchemas;
  }
  const schemas = [];
  for (const [key, definition] of Object.entries(allDefinitions)) {
    const def = definition;
    const encryptedPaths = findEncryptedPathsInSchema(def.schema);
    if (encryptedPaths.length > 0) {
      schemas.push({
        configKey: key,
        encryptedPaths,
        description: def.description
      });
    }
  }
  cachedEncryptedFieldSchemas = schemas;
  return schemas;
}
function clearEncryptedFieldSchemaCache() {
  cachedEncryptedFieldSchemas = null;
}
function getEncryptedFieldSchemas() {
  return [...discoverEncryptedFieldSchemas()];
}
function scanObjectForEncryptedValues(obj, currentPath = "") {
  const results = [];
  if (obj === null || obj === void 0) {
    return results;
  }
  if (typeof obj === "string") {
    if (isEncryptedString(obj)) {
      const version = getEncryptionVersion(obj);
      if (version) {
        results.push({
          path: currentPath,
          value: obj,
          version
        });
      }
    }
    return results;
  }
  if (Array.isArray(obj)) {
    obj.forEach((item, index) => {
      const itemPath = currentPath ? `${currentPath}[${index}]` : `[${index}]`;
      results.push(...scanObjectForEncryptedValues(item, itemPath));
    });
    return results;
  }
  if (typeof obj === "object") {
    for (const [key, value] of Object.entries(obj)) {
      const keyPath = currentPath ? `${currentPath}.${key}` : key;
      results.push(...scanObjectForEncryptedValues(value, keyPath));
    }
  }
  return results;
}
function scanConfigForEncryptedValues(config, useSchemaOptimization = true) {
  if (!useSchemaOptimization) {
    return scanObjectForEncryptedValues(config);
  }
  const results = [];
  const schemas = discoverEncryptedFieldSchemas();
  for (const schema of schemas) {
    const configSection = config[schema.configKey];
    if (!configSection || typeof configSection !== "object") {
      continue;
    }
    for (const encryptedPath of schema.encryptedPaths) {
      if (encryptedPath.endsWith(".*")) {
        const parentPath = encryptedPath.slice(0, -2);
        const parent = parentPath ? getNestedValue(configSection, parentPath) : configSection;
        if (parent && typeof parent === "object" && !Array.isArray(parent)) {
          for (const [key, value] of Object.entries(parent)) {
            if (typeof value === "string" && isEncryptedString(value)) {
              const version = getEncryptionVersion(value);
              if (version) {
                const fullPath = parentPath ? `${schema.configKey}.${parentPath}.${key}` : `${schema.configKey}.${key}`;
                results.push({
                  path: fullPath,
                  value,
                  version
                });
              }
            }
          }
        }
      } else if (encryptedPath.includes("[*]")) {
        const parts = encryptedPath.split("[*]");
        const beforeArray = parts[0].replace(/\.$/, "");
        const afterArray = parts[1]?.replace(/^\./, "") || "";
        const arrayValue = beforeArray ? getNestedValue(configSection, beforeArray) : configSection;
        if (Array.isArray(arrayValue)) {
          arrayValue.forEach((item, index) => {
            const itemValue = afterArray ? getNestedValue(item, afterArray) : item;
            if (typeof itemValue === "string" && isEncryptedString(itemValue)) {
              const version = getEncryptionVersion(itemValue);
              if (version) {
                const fullPath = beforeArray ? `${schema.configKey}.${beforeArray}[${index}]${afterArray ? "." + afterArray : ""}` : `${schema.configKey}[${index}]${afterArray ? "." + afterArray : ""}`;
                results.push({
                  path: fullPath,
                  value: itemValue,
                  version
                });
              }
            }
          });
        }
      } else {
        const value = getNestedValue(configSection, encryptedPath);
        if (typeof value === "string" && isEncryptedString(value)) {
          const version = getEncryptionVersion(value);
          if (version) {
            results.push({
              path: `${schema.configKey}.${encryptedPath}`,
              value,
              version
            });
          }
        }
      }
    }
  }
  return results;
}
function getNestedValue(obj, path) {
  if (!path) return obj;
  const parts = path.split(".");
  let current = obj;
  for (const part of parts) {
    if (current === null || current === void 0 || typeof current !== "object") {
      return void 0;
    }
    current = current[part];
  }
  return current;
}
function analyzeKeyVersionUsage(locations) {
  const usageMap = /* @__PURE__ */ new Map();
  for (const location of locations) {
    const existing = usageMap.get(location.version);
    if (existing) {
      existing.count++;
      existing.locations.push(location.path);
    } else {
      usageMap.set(location.version, {
        count: 1,
        locations: [location.path]
      });
    }
  }
  const allVersions = getAllEncryptionVersions();
  const result = [];
  for (const versionMeta of allVersions) {
    const usage = usageMap.get(versionMeta.version);
    result.push({
      version: versionMeta.version,
      count: usage?.count ?? 0,
      locations: usage?.locations ?? []
    });
  }
  return result;
}
function analyzeKeyDependencies(locations) {
  const usage = analyzeKeyVersionUsage(locations);
  const deprecatedVersions = new Set(getDeprecatedEncryptionVersions());
  const availableVersions = new Set(getAvailableEncryptionVersions());
  const purgeCandidates = [];
  const deprecatedWithDependencies = [];
  const activeVersions = [];
  for (const versionUsage of usage) {
    const isDeprecated = deprecatedVersions.has(versionUsage.version);
    const isAvailable = availableVersions.has(versionUsage.version);
    if (!isAvailable) {
      continue;
    }
    if (isDeprecated) {
      if (versionUsage.count === 0) {
        purgeCandidates.push(versionUsage.version);
      } else {
        deprecatedWithDependencies.push({
          version: versionUsage.version,
          dependencyCount: versionUsage.count,
          locations: versionUsage.locations
        });
      }
    } else {
      activeVersions.push(versionUsage.version);
    }
  }
  return {
    purgeCandidates,
    deprecatedWithDependencies,
    activeVersions,
    usage
  };
}
function scanAndAnalyzeConfigs(configs, configIdentifiers) {
  const allLocations = [];
  configs.forEach((config, index) => {
    const identifier = configIdentifiers?.[index] ?? `config[${index}]`;
    const locations = scanConfigForEncryptedValues(config);
    for (const location of locations) {
      allLocations.push({
        ...location,
        path: `${identifier}.${location.path}`
      });
    }
  });
  return analyzeKeyDependencies(allLocations);
}
function generateKeyStatusReport(analysis) {
  const lines = [
    "=== Encryption Key Status Report ===",
    ""
  ];
  lines.push("Active Versions:");
  if (analysis.activeVersions.length > 0) {
    for (const version of analysis.activeVersions) {
      const usage = analysis.usage.find((u) => u.version === version);
      lines.push(`  - ${version}: ${usage?.count ?? 0} encrypted values`);
    }
  } else {
    lines.push("  (none)");
  }
  lines.push("");
  lines.push("Deprecated Versions (still in use):");
  if (analysis.deprecatedWithDependencies.length > 0) {
    for (const dep of analysis.deprecatedWithDependencies) {
      lines.push(`  - ${dep.version}: ${dep.dependencyCount} dependencies`);
      if (dep.locations.length <= 5) {
        for (const loc of dep.locations) {
          lines.push(`      \u2022 ${loc}`);
        }
      } else {
        for (const loc of dep.locations.slice(0, 5)) {
          lines.push(`      \u2022 ${loc}`);
        }
        lines.push(`      ... and ${dep.locations.length - 5} more`);
      }
    }
  } else {
    lines.push("  (none)");
  }
  lines.push("");
  lines.push("Purge Candidates (deprecated with no dependencies):");
  if (analysis.purgeCandidates.length > 0) {
    for (const version of analysis.purgeCandidates) {
      const meta = getAllEncryptionVersions().find((v) => v.version === version);
      lines.push(`  - ${version} (${meta?.envVar ?? "unknown env var"})`);
    }
  } else {
    lines.push("  (none)");
  }
  lines.push("");
  lines.push("=== End Report ===");
  return lines.join("\n");
}

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/jsutils/devAssert.mjs
function devAssert(condition, message) {
  const booleanCondition = Boolean(condition);
  if (!booleanCondition) {
    throw new Error(message);
  }
}

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/jsutils/isObjectLike.mjs
function isObjectLike(value) {
  return typeof value == "object" && value !== null;
}

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/jsutils/invariant.mjs
function invariant(condition, message) {
  const booleanCondition = Boolean(condition);
  if (!booleanCondition) {
    throw new Error(
      message != null ? message : "Unexpected invariant triggered."
    );
  }
}

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/language/location.mjs
var LineRegExp = /\r\n|[\n\r]/g;
function getLocation(source, position) {
  let lastLineStart = 0;
  let line = 1;
  for (const match of source.body.matchAll(LineRegExp)) {
    typeof match.index === "number" || invariant(false);
    if (match.index >= position) {
      break;
    }
    lastLineStart = match.index + match[0].length;
    line += 1;
  }
  return {
    line,
    column: position + 1 - lastLineStart
  };
}

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/language/printLocation.mjs
function printLocation(location) {
  return printSourceLocation(
    location.source,
    getLocation(location.source, location.start)
  );
}
function printSourceLocation(source, sourceLocation) {
  const firstLineColumnOffset = source.locationOffset.column - 1;
  const body = "".padStart(firstLineColumnOffset) + source.body;
  const lineIndex = sourceLocation.line - 1;
  const lineOffset = source.locationOffset.line - 1;
  const lineNum = sourceLocation.line + lineOffset;
  const columnOffset = sourceLocation.line === 1 ? firstLineColumnOffset : 0;
  const columnNum = sourceLocation.column + columnOffset;
  const locationStr = `${source.name}:${lineNum}:${columnNum}
`;
  const lines = body.split(/\r\n|[\n\r]/g);
  const locationLine = lines[lineIndex];
  if (locationLine.length > 120) {
    const subLineIndex = Math.floor(columnNum / 80);
    const subLineColumnNum = columnNum % 80;
    const subLines = [];
    for (let i = 0; i < locationLine.length; i += 80) {
      subLines.push(locationLine.slice(i, i + 80));
    }
    return locationStr + printPrefixedLines([
      [`${lineNum} |`, subLines[0]],
      ...subLines.slice(1, subLineIndex + 1).map((subLine) => ["|", subLine]),
      ["|", "^".padStart(subLineColumnNum)],
      ["|", subLines[subLineIndex + 1]]
    ]);
  }
  return locationStr + printPrefixedLines([
    // Lines specified like this: ["prefix", "string"],
    [`${lineNum - 1} |`, lines[lineIndex - 1]],
    [`${lineNum} |`, locationLine],
    ["|", "^".padStart(columnNum)],
    [`${lineNum + 1} |`, lines[lineIndex + 1]]
  ]);
}
function printPrefixedLines(lines) {
  const existingLines = lines.filter(([_, line]) => line !== void 0);
  const padLen = Math.max(...existingLines.map(([prefix]) => prefix.length));
  return existingLines.map(([prefix, line]) => prefix.padStart(padLen) + (line ? " " + line : "")).join("\n");
}

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/error/GraphQLError.mjs
function toNormalizedOptions(args) {
  const firstArg = args[0];
  if (firstArg == null || "kind" in firstArg || "length" in firstArg) {
    return {
      nodes: firstArg,
      source: args[1],
      positions: args[2],
      path: args[3],
      originalError: args[4],
      extensions: args[5]
    };
  }
  return firstArg;
}
var GraphQLError = class _GraphQLError extends Error {
  /**
   * An array of `{ line, column }` locations within the source GraphQL document
   * which correspond to this error.
   *
   * Errors during validation often contain multiple locations, for example to
   * point out two things with the same name. Errors during execution include a
   * single location, the field which produced the error.
   *
   * Enumerable, and appears in the result of JSON.stringify().
   */
  /**
   * An array describing the JSON-path into the execution response which
   * corresponds to this error. Only included for errors during execution.
   *
   * Enumerable, and appears in the result of JSON.stringify().
   */
  /** An array of GraphQL AST Nodes corresponding to this error. */
  /**
   * The source GraphQL document for the first location of this error.
   *
   * Note that if this Error represents more than one node, the source may not
   * represent nodes after the first node.
   */
  /**
   * An array of character offsets within the source GraphQL document
   * which correspond to this error.
   */
  /** Original error that caused this GraphQLError, if one exists. */
  /** Extension fields to add to the formatted error. */
  /**
   * Creates a GraphQLError instance.
   * @param message - Human-readable error message.
   * @param options - Error metadata such as source locations, response path, original error, and extensions.
   * This positional-arguments constructor overload is deprecated. Use the
   * `GraphQLError(message, options)` overload instead.
   * @example
   * ```ts
   * // Create an error from AST nodes and response metadata.
   * import { parse } from 'graphql/language';
   * import { GraphQLError } from 'graphql/error';
   *
   * const document = parse('{ greeting }');
   * const fieldNode = document.definitions[0].selectionSet.selections[0];
   * const error = new GraphQLError('Cannot query this field.', {
   *   nodes: fieldNode,
   *   path: ['greeting'],
   *   extensions: { code: 'FORBIDDEN' },
   * });
   *
   * error.message; // => 'Cannot query this field.'
   * error.locations; // => [{ line: 1, column: 3 }]
   * error.path; // => ['greeting']
   * error.extensions; // => { code: 'FORBIDDEN' }
   * ```
   * @example
   * ```ts
   * // This variant derives locations from source positions and preserves the original error.
   * import { Source } from 'graphql/language';
   * import { GraphQLError } from 'graphql/error';
   *
   * const source = new Source('{ greeting }');
   * const originalError = new Error('Database unavailable.');
   * const error = new GraphQLError('Resolver failed.', {
   *   source,
   *   positions: [2],
   *   path: ['greeting'],
   *   originalError,
   * });
   *
   * error.locations; // => [{ line: 1, column: 3 }]
   * error.path; // => ['greeting']
   * error.originalError; // => originalError
   * ```
   */
  /**
   * Creates a GraphQLError instance using the legacy positional constructor.
   * This deprecated overload will be removed in v17. Prefer the
   * `GraphQLErrorOptions` object overload, which keeps optional error metadata
   * in a single options bag.
   * @param message - Human-readable error message.
   * @param nodes - AST node or nodes associated with this error.
   * @param source - Source document used to derive error locations.
   * @param positions - Character offsets in the source document associated with
   * this error.
   * @param path - Response path where this error occurred during execution.
   * @param originalError - Original error that caused this GraphQLError, if one
   * exists.
   * @param extensions - Extension fields to include in the formatted error.
   * @example
   * ```ts
   * import { Source } from 'graphql/language';
   * import { GraphQLError } from 'graphql/error';
   *
   * const source = new Source('{ greeting }');
   * const originalError = new Error('Database unavailable.');
   * const error = new GraphQLError(
   *   'Resolver failed.',
   *   undefined,
   *   source,
   *   [2],
   *   ['greeting'],
   *   originalError,
   *   { code: 'INTERNAL' },
   * );
   *
   * error.locations; // => [{ line: 1, column: 3 }]
   * error.path; // => ['greeting']
   * error.originalError; // => originalError
   * error.extensions; // => { code: 'INTERNAL' }
   * ```
   * @deprecated Please use the `GraphQLErrorOptions` constructor overload instead.
   */
  constructor(message, ...rawArgs) {
    var _this$nodes, _nodeLocations$, _ref;
    const { nodes, source, positions, path, originalError, extensions } = toNormalizedOptions(rawArgs);
    super(message);
    this.name = "GraphQLError";
    this.path = path !== null && path !== void 0 ? path : void 0;
    this.originalError = originalError !== null && originalError !== void 0 ? originalError : void 0;
    this.nodes = undefinedIfEmpty(
      Array.isArray(nodes) ? nodes : nodes ? [nodes] : void 0
    );
    const nodeLocations = undefinedIfEmpty(
      (_this$nodes = this.nodes) === null || _this$nodes === void 0 ? void 0 : _this$nodes.map((node) => node.loc).filter((loc) => loc != null)
    );
    this.source = source !== null && source !== void 0 ? source : nodeLocations === null || nodeLocations === void 0 ? void 0 : (_nodeLocations$ = nodeLocations[0]) === null || _nodeLocations$ === void 0 ? void 0 : _nodeLocations$.source;
    this.positions = positions !== null && positions !== void 0 ? positions : nodeLocations === null || nodeLocations === void 0 ? void 0 : nodeLocations.map((loc) => loc.start);
    this.locations = positions && source ? positions.map((pos) => getLocation(source, pos)) : nodeLocations === null || nodeLocations === void 0 ? void 0 : nodeLocations.map((loc) => getLocation(loc.source, loc.start));
    const originalExtensions = isObjectLike(
      originalError === null || originalError === void 0 ? void 0 : originalError.extensions
    ) ? originalError === null || originalError === void 0 ? void 0 : originalError.extensions : void 0;
    this.extensions = (_ref = extensions !== null && extensions !== void 0 ? extensions : originalExtensions) !== null && _ref !== void 0 ? _ref : /* @__PURE__ */ Object.create(null);
    Object.defineProperties(this, {
      message: {
        writable: true,
        enumerable: true
      },
      name: {
        enumerable: false
      },
      nodes: {
        enumerable: false
      },
      source: {
        enumerable: false
      },
      positions: {
        enumerable: false
      },
      originalError: {
        enumerable: false
      }
    });
    if (originalError !== null && originalError !== void 0 && originalError.stack) {
      Object.defineProperty(this, "stack", {
        value: originalError.stack,
        writable: true,
        configurable: true
      });
    } else if (Error.captureStackTrace) {
      Error.captureStackTrace(this, _GraphQLError);
    } else {
      Object.defineProperty(this, "stack", {
        value: Error().stack,
        writable: true,
        configurable: true
      });
    }
  }
  /**
   * Returns the value used by `Object.prototype.toString`.
   * @returns The built-in string tag for this object.
   */
  get [Symbol.toStringTag]() {
    return "GraphQLError";
  }
  /**
   * Returns this error as a human-readable message with source locations.
   * @returns The formatted error string.
   * @example
   * ```ts
   * import { Source } from 'graphql/language';
   * import { GraphQLError } from 'graphql/error';
   *
   * const error = new GraphQLError('Cannot query field "name".', {
   *   source: new Source('{ name }'),
   *   positions: [2],
   * });
   *
   * error.toString(); // => 'Cannot query field "name".\n\nGraphQL request:1:3\n1 | { name }\n  |   ^'
   * ```
   */
  toString() {
    let output = this.message;
    if (this.nodes) {
      for (const node of this.nodes) {
        if (node.loc) {
          output += "\n\n" + printLocation(node.loc);
        }
      }
    } else if (this.source && this.locations) {
      for (const location of this.locations) {
        output += "\n\n" + printSourceLocation(this.source, location);
      }
    }
    return output;
  }
  /**
   * Returns the JSON representation used when this object is serialized.
   * @returns The JSON-serializable representation.
   * @example
   * ```ts
   * import { GraphQLError } from 'graphql/error';
   *
   * const error = new GraphQLError('Resolver failed.', {
   *   path: ['viewer', 'name'],
   *   extensions: { code: 'INTERNAL' },
   * });
   *
   * error.toJSON(); // => { message: 'Resolver failed.', path: ['viewer', 'name'], extensions: { code: 'INTERNAL' } }
   * ```
   */
  toJSON() {
    const formattedError = {
      message: this.message
    };
    if (this.locations != null) {
      formattedError.locations = this.locations;
    }
    if (this.path != null) {
      formattedError.path = this.path;
    }
    if (this.extensions != null && Object.keys(this.extensions).length > 0) {
      formattedError.extensions = this.extensions;
    }
    return formattedError;
  }
};
function undefinedIfEmpty(array) {
  return array === void 0 || array.length === 0 ? void 0 : array;
}

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/error/syntaxError.mjs
function syntaxError(source, position, description) {
  return new GraphQLError(`Syntax Error: ${description}`, {
    source,
    positions: [position]
  });
}

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/language/ast.mjs
var Location = class {
  /** The character offset at which this Node begins. */
  /** The character offset at which this Node ends. */
  /** The Token at which this Node begins. */
  /** The Token at which this Node ends. */
  /** The Source document the AST represents. */
  /**
   * Creates a Location instance.
   * @param startToken - The start token.
   * @param endToken - The end token.
   * @param source - Source document used to derive error locations.
   * @example
   * ```ts
   * import { Location, Source, Token, TokenKind } from 'graphql/language';
   *
   * const source = new Source('{ hello }');
   * const startToken = new Token(TokenKind.BRACE_L, 0, 1, 1, 1);
   * const endToken = new Token(TokenKind.BRACE_R, 8, 9, 1, 9);
   * const location = new Location(startToken, endToken, source);
   *
   * location.start; // => 0
   * location.end; // => 9
   * location.source.body; // => '{ hello }'
   * ```
   */
  constructor(startToken, endToken, source) {
    this.start = startToken.start;
    this.end = endToken.end;
    this.startToken = startToken;
    this.endToken = endToken;
    this.source = source;
  }
  /**
   * Returns the value used by `Object.prototype.toString`.
   * @returns The built-in string tag for this object.
   */
  get [Symbol.toStringTag]() {
    return "Location";
  }
  /**
   * Returns a JSON representation of this location.
   * @returns The JSON-serializable representation.
   * @example
   * ```ts
   * import { parse } from 'graphql/language';
   *
   * const document = parse('{ hello }');
   * const location = document.loc?.toJSON();
   *
   * location; // => { start: 0, end: 9 }
   * ```
   */
  toJSON() {
    return {
      start: this.start,
      end: this.end
    };
  }
};
var Token = class {
  /** The kind of Token. */
  /** The character offset at which this Node begins. */
  /** The character offset at which this Node ends. */
  /** The 1-indexed line number on which this Token appears. */
  /** The 1-indexed column number at which this Token begins. */
  /**
   * For non-punctuation tokens, represents the interpreted value of the token.
   *
   * Note: is undefined for punctuation tokens, but typed as string for
   * convenience in the parser.
   */
  /**
   * Tokens exist as nodes in a double-linked-list amongst all tokens
   * including ignored tokens. <SOF> is always the first node and <EOF>
   * the last.
   */
  /** Next token in the token stream, including ignored tokens. */
  /**
   * Creates a Token instance.
   * @param kind - Token kind produced by lexical analysis.
   * @param start - Character offset where this token begins.
   * @param end - Character offset where this token ends.
   * @param line - One-indexed line number where this token begins.
   * @param column - One-indexed column number where this token begins.
   * @param value - Interpreted value for non-punctuation tokens.
   * @example
   * ```ts
   * import { Token, TokenKind } from 'graphql/language';
   *
   * const token = new Token(TokenKind.NAME, 2, 7, 1, 3, 'hello');
   *
   * token.kind; // => TokenKind.NAME
   * token.value; // => 'hello'
   * token.toJSON(); // => { kind: 'Name', value: 'hello', line: 1, column: 3 }
   * ```
   */
  constructor(kind, start, end, line, column, value) {
    this.kind = kind;
    this.start = start;
    this.end = end;
    this.line = line;
    this.column = column;
    this.value = value;
    this.prev = null;
    this.next = null;
  }
  /**
   * Returns the value used by `Object.prototype.toString`.
   * @returns The built-in string tag for this object.
   */
  get [Symbol.toStringTag]() {
    return "Token";
  }
  /**
   * Returns a JSON representation of this token.
   * @returns The JSON-serializable representation.
   * @example
   * ```ts
   * import { Lexer, Source } from 'graphql/language';
   *
   * const lexer = new Lexer(new Source('{ hello }'));
   * const token = lexer.advance().toJSON();
   *
   * token; // => { kind: '{', value: undefined, line: 1, column: 1 }
   * ```
   */
  toJSON() {
    return {
      kind: this.kind,
      value: this.value,
      line: this.line,
      column: this.column
    };
  }
};
var QueryDocumentKeys = {
  Name: [],
  Document: ["definitions"],
  OperationDefinition: [
    "description",
    "name",
    "variableDefinitions",
    "directives",
    "selectionSet"
  ],
  VariableDefinition: [
    "description",
    "variable",
    "type",
    "defaultValue",
    "directives"
  ],
  Variable: ["name"],
  SelectionSet: ["selections"],
  Field: ["alias", "name", "arguments", "directives", "selectionSet"],
  Argument: ["name", "value"],
  FragmentSpread: ["name", "directives"],
  InlineFragment: ["typeCondition", "directives", "selectionSet"],
  FragmentDefinition: [
    "description",
    "name",
    // Note: fragment variable definitions are deprecated and will removed in v17.0.0
    "variableDefinitions",
    "typeCondition",
    "directives",
    "selectionSet"
  ],
  IntValue: [],
  FloatValue: [],
  StringValue: [],
  BooleanValue: [],
  NullValue: [],
  EnumValue: [],
  ListValue: ["values"],
  ObjectValue: ["fields"],
  ObjectField: ["name", "value"],
  Directive: ["name", "arguments"],
  NamedType: ["name"],
  ListType: ["type"],
  NonNullType: ["type"],
  SchemaDefinition: ["description", "directives", "operationTypes"],
  OperationTypeDefinition: ["type"],
  ScalarTypeDefinition: ["description", "name", "directives"],
  ObjectTypeDefinition: [
    "description",
    "name",
    "interfaces",
    "directives",
    "fields"
  ],
  FieldDefinition: ["description", "name", "arguments", "type", "directives"],
  InputValueDefinition: [
    "description",
    "name",
    "type",
    "defaultValue",
    "directives"
  ],
  InterfaceTypeDefinition: [
    "description",
    "name",
    "interfaces",
    "directives",
    "fields"
  ],
  UnionTypeDefinition: ["description", "name", "directives", "types"],
  EnumTypeDefinition: ["description", "name", "directives", "values"],
  EnumValueDefinition: ["description", "name", "directives"],
  InputObjectTypeDefinition: ["description", "name", "directives", "fields"],
  DirectiveDefinition: [
    "description",
    "name",
    "arguments",
    "directives",
    "locations"
  ],
  SchemaExtension: ["directives", "operationTypes"],
  DirectiveExtension: ["name", "directives"],
  ScalarTypeExtension: ["name", "directives"],
  ObjectTypeExtension: ["name", "interfaces", "directives", "fields"],
  InterfaceTypeExtension: ["name", "interfaces", "directives", "fields"],
  UnionTypeExtension: ["name", "directives", "types"],
  EnumTypeExtension: ["name", "directives", "values"],
  InputObjectTypeExtension: ["name", "directives", "fields"],
  TypeCoordinate: ["name"],
  MemberCoordinate: ["name", "memberName"],
  ArgumentCoordinate: ["name", "fieldName", "argumentName"],
  DirectiveCoordinate: ["name"],
  DirectiveArgumentCoordinate: ["name", "argumentName"]
};
var kindValues = new Set(Object.keys(QueryDocumentKeys));
var OperationTypeNode;
(function(OperationTypeNode2) {
  OperationTypeNode2["QUERY"] = "query";
  OperationTypeNode2["MUTATION"] = "mutation";
  OperationTypeNode2["SUBSCRIPTION"] = "subscription";
})(OperationTypeNode || (OperationTypeNode = {}));

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/language/directiveLocation.mjs
var DirectiveLocation;
(function(DirectiveLocation2) {
  DirectiveLocation2["QUERY"] = "QUERY";
  DirectiveLocation2["MUTATION"] = "MUTATION";
  DirectiveLocation2["SUBSCRIPTION"] = "SUBSCRIPTION";
  DirectiveLocation2["FIELD"] = "FIELD";
  DirectiveLocation2["FRAGMENT_DEFINITION"] = "FRAGMENT_DEFINITION";
  DirectiveLocation2["FRAGMENT_SPREAD"] = "FRAGMENT_SPREAD";
  DirectiveLocation2["INLINE_FRAGMENT"] = "INLINE_FRAGMENT";
  DirectiveLocation2["VARIABLE_DEFINITION"] = "VARIABLE_DEFINITION";
  DirectiveLocation2["SCHEMA"] = "SCHEMA";
  DirectiveLocation2["SCALAR"] = "SCALAR";
  DirectiveLocation2["OBJECT"] = "OBJECT";
  DirectiveLocation2["FIELD_DEFINITION"] = "FIELD_DEFINITION";
  DirectiveLocation2["ARGUMENT_DEFINITION"] = "ARGUMENT_DEFINITION";
  DirectiveLocation2["INTERFACE"] = "INTERFACE";
  DirectiveLocation2["UNION"] = "UNION";
  DirectiveLocation2["ENUM"] = "ENUM";
  DirectiveLocation2["ENUM_VALUE"] = "ENUM_VALUE";
  DirectiveLocation2["INPUT_OBJECT"] = "INPUT_OBJECT";
  DirectiveLocation2["INPUT_FIELD_DEFINITION"] = "INPUT_FIELD_DEFINITION";
  DirectiveLocation2["DIRECTIVE_DEFINITION"] = "DIRECTIVE_DEFINITION";
})(DirectiveLocation || (DirectiveLocation = {}));

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/language/kinds.mjs
var Kind;
(function(Kind2) {
  Kind2["NAME"] = "Name";
  Kind2["DOCUMENT"] = "Document";
  Kind2["OPERATION_DEFINITION"] = "OperationDefinition";
  Kind2["VARIABLE_DEFINITION"] = "VariableDefinition";
  Kind2["SELECTION_SET"] = "SelectionSet";
  Kind2["FIELD"] = "Field";
  Kind2["ARGUMENT"] = "Argument";
  Kind2["FRAGMENT_SPREAD"] = "FragmentSpread";
  Kind2["INLINE_FRAGMENT"] = "InlineFragment";
  Kind2["FRAGMENT_DEFINITION"] = "FragmentDefinition";
  Kind2["VARIABLE"] = "Variable";
  Kind2["INT"] = "IntValue";
  Kind2["FLOAT"] = "FloatValue";
  Kind2["STRING"] = "StringValue";
  Kind2["BOOLEAN"] = "BooleanValue";
  Kind2["NULL"] = "NullValue";
  Kind2["ENUM"] = "EnumValue";
  Kind2["LIST"] = "ListValue";
  Kind2["OBJECT"] = "ObjectValue";
  Kind2["OBJECT_FIELD"] = "ObjectField";
  Kind2["DIRECTIVE"] = "Directive";
  Kind2["NAMED_TYPE"] = "NamedType";
  Kind2["LIST_TYPE"] = "ListType";
  Kind2["NON_NULL_TYPE"] = "NonNullType";
  Kind2["SCHEMA_DEFINITION"] = "SchemaDefinition";
  Kind2["OPERATION_TYPE_DEFINITION"] = "OperationTypeDefinition";
  Kind2["SCALAR_TYPE_DEFINITION"] = "ScalarTypeDefinition";
  Kind2["OBJECT_TYPE_DEFINITION"] = "ObjectTypeDefinition";
  Kind2["FIELD_DEFINITION"] = "FieldDefinition";
  Kind2["INPUT_VALUE_DEFINITION"] = "InputValueDefinition";
  Kind2["INTERFACE_TYPE_DEFINITION"] = "InterfaceTypeDefinition";
  Kind2["UNION_TYPE_DEFINITION"] = "UnionTypeDefinition";
  Kind2["ENUM_TYPE_DEFINITION"] = "EnumTypeDefinition";
  Kind2["ENUM_VALUE_DEFINITION"] = "EnumValueDefinition";
  Kind2["INPUT_OBJECT_TYPE_DEFINITION"] = "InputObjectTypeDefinition";
  Kind2["DIRECTIVE_DEFINITION"] = "DirectiveDefinition";
  Kind2["SCHEMA_EXTENSION"] = "SchemaExtension";
  Kind2["DIRECTIVE_EXTENSION"] = "DirectiveExtension";
  Kind2["SCALAR_TYPE_EXTENSION"] = "ScalarTypeExtension";
  Kind2["OBJECT_TYPE_EXTENSION"] = "ObjectTypeExtension";
  Kind2["INTERFACE_TYPE_EXTENSION"] = "InterfaceTypeExtension";
  Kind2["UNION_TYPE_EXTENSION"] = "UnionTypeExtension";
  Kind2["ENUM_TYPE_EXTENSION"] = "EnumTypeExtension";
  Kind2["INPUT_OBJECT_TYPE_EXTENSION"] = "InputObjectTypeExtension";
  Kind2["TYPE_COORDINATE"] = "TypeCoordinate";
  Kind2["MEMBER_COORDINATE"] = "MemberCoordinate";
  Kind2["ARGUMENT_COORDINATE"] = "ArgumentCoordinate";
  Kind2["DIRECTIVE_COORDINATE"] = "DirectiveCoordinate";
  Kind2["DIRECTIVE_ARGUMENT_COORDINATE"] = "DirectiveArgumentCoordinate";
})(Kind || (Kind = {}));

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/language/characterClasses.mjs
function isWhiteSpace(code) {
  return code === 9 || code === 32;
}
function isDigit(code) {
  return code >= 48 && code <= 57;
}
function isLetter(code) {
  return code >= 97 && code <= 122 || // A-Z
  code >= 65 && code <= 90;
}
function isNameStart(code) {
  return isLetter(code) || code === 95;
}
function isNameContinue(code) {
  return isLetter(code) || isDigit(code) || code === 95;
}

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/language/blockString.mjs
function dedentBlockStringLines(lines) {
  var _firstNonEmptyLine2;
  let commonIndent = Number.MAX_SAFE_INTEGER;
  let firstNonEmptyLine = null;
  let lastNonEmptyLine = -1;
  for (let i = 0; i < lines.length; ++i) {
    var _firstNonEmptyLine;
    const line = lines[i];
    const indent = leadingWhitespace(line);
    if (indent === line.length) {
      continue;
    }
    firstNonEmptyLine = (_firstNonEmptyLine = firstNonEmptyLine) !== null && _firstNonEmptyLine !== void 0 ? _firstNonEmptyLine : i;
    lastNonEmptyLine = i;
    if (i !== 0 && indent < commonIndent) {
      commonIndent = indent;
    }
  }
  return lines.map((line, i) => i === 0 ? line : line.slice(commonIndent)).slice(
    (_firstNonEmptyLine2 = firstNonEmptyLine) !== null && _firstNonEmptyLine2 !== void 0 ? _firstNonEmptyLine2 : 0,
    lastNonEmptyLine + 1
  );
}
function leadingWhitespace(str) {
  let i = 0;
  while (i < str.length && isWhiteSpace(str.charCodeAt(i))) {
    ++i;
  }
  return i;
}

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/language/tokenKind.mjs
var TokenKind;
(function(TokenKind2) {
  TokenKind2["SOF"] = "<SOF>";
  TokenKind2["EOF"] = "<EOF>";
  TokenKind2["BANG"] = "!";
  TokenKind2["DOLLAR"] = "$";
  TokenKind2["AMP"] = "&";
  TokenKind2["PAREN_L"] = "(";
  TokenKind2["PAREN_R"] = ")";
  TokenKind2["DOT"] = ".";
  TokenKind2["SPREAD"] = "...";
  TokenKind2["COLON"] = ":";
  TokenKind2["EQUALS"] = "=";
  TokenKind2["AT"] = "@";
  TokenKind2["BRACKET_L"] = "[";
  TokenKind2["BRACKET_R"] = "]";
  TokenKind2["BRACE_L"] = "{";
  TokenKind2["PIPE"] = "|";
  TokenKind2["BRACE_R"] = "}";
  TokenKind2["NAME"] = "Name";
  TokenKind2["INT"] = "Int";
  TokenKind2["FLOAT"] = "Float";
  TokenKind2["STRING"] = "String";
  TokenKind2["BLOCK_STRING"] = "BlockString";
  TokenKind2["COMMENT"] = "Comment";
})(TokenKind || (TokenKind = {}));

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/language/lexer.mjs
var Lexer = class {
  /** Source document used to derive error locations. */
  /** Most recent non-ignored token returned by the lexer. */
  /** Current non-ignored token at the lexer cursor. */
  /** The (1-indexed) line containing the current token. */
  /** Character offset where the current line starts. */
  /**
   * Creates a Lexer instance.
   * @param source - Source document used to derive error locations.
   * @example
   * ```ts
   * import { Lexer, Source, TokenKind } from 'graphql/language';
   *
   * const lexer = new Lexer(new Source('{ hello }'));
   *
   * lexer.token.kind; // => TokenKind.SOF
   * lexer.advance().kind; // => TokenKind.BRACE_L
   * lexer.advance().value; // => 'hello'
   * lexer.advance().kind; // => TokenKind.BRACE_R
   * ```
   */
  constructor(source) {
    const startOfFileToken = new Token(TokenKind.SOF, 0, 0, 0, 0);
    this.source = source;
    this.lastToken = startOfFileToken;
    this.token = startOfFileToken;
    this.line = 1;
    this.lineStart = 0;
  }
  /**
   * Returns the value used by `Object.prototype.toString`.
   * @returns The built-in string tag for this object.
   */
  get [Symbol.toStringTag]() {
    return "Lexer";
  }
  /**
   * Advances the token stream to the next non-ignored token.
   * @returns The next non-ignored token.
   * @example
   * ```ts
   * import { Lexer, Source } from 'graphql/language';
   *
   * const lexer = new Lexer(new Source('{ hello }'));
   * const token = lexer.advance();
   *
   * token.kind; // => '{'
   * lexer.token; // => token
   * ```
   */
  advance() {
    this.lastToken = this.token;
    const token = this.token = this.lookahead();
    return token;
  }
  /**
   * Looks ahead and returns the next non-ignored token, but does not change
   * the state of Lexer.
   * @returns The next non-ignored token without advancing the lexer.
   * @example
   * ```ts
   * import { Lexer, Source } from 'graphql/language';
   *
   * const lexer = new Lexer(new Source('{ hello }'));
   * const token = lexer.lookahead();
   *
   * token.kind; // => '{'
   * lexer.token.kind; // => '<SOF>'
   * ```
   */
  lookahead() {
    let token = this.token;
    if (token.kind !== TokenKind.EOF) {
      do {
        if (token.next) {
          token = token.next;
        } else {
          const nextToken = readNextToken(this, token.end);
          token.next = nextToken;
          nextToken.prev = token;
          token = nextToken;
        }
      } while (token.kind === TokenKind.COMMENT);
    }
    return token;
  }
};
function isPunctuatorTokenKind(kind) {
  return kind === TokenKind.BANG || kind === TokenKind.DOLLAR || kind === TokenKind.AMP || kind === TokenKind.PAREN_L || kind === TokenKind.PAREN_R || kind === TokenKind.DOT || kind === TokenKind.SPREAD || kind === TokenKind.COLON || kind === TokenKind.EQUALS || kind === TokenKind.AT || kind === TokenKind.BRACKET_L || kind === TokenKind.BRACKET_R || kind === TokenKind.BRACE_L || kind === TokenKind.PIPE || kind === TokenKind.BRACE_R;
}
function isUnicodeScalarValue(code) {
  return code >= 0 && code <= 55295 || code >= 57344 && code <= 1114111;
}
function isSupplementaryCodePoint(body, location) {
  return isLeadingSurrogate(body.charCodeAt(location)) && isTrailingSurrogate(body.charCodeAt(location + 1));
}
function isLeadingSurrogate(code) {
  return code >= 55296 && code <= 56319;
}
function isTrailingSurrogate(code) {
  return code >= 56320 && code <= 57343;
}
function printCodePointAt(lexer, location) {
  const code = lexer.source.body.codePointAt(location);
  if (code === void 0) {
    return TokenKind.EOF;
  } else if (code >= 32 && code <= 126) {
    const char = String.fromCodePoint(code);
    return char === '"' ? `'"'` : `"${char}"`;
  }
  return "U+" + code.toString(16).toUpperCase().padStart(4, "0");
}
function createToken(lexer, kind, start, end, value) {
  const line = lexer.line;
  const col = 1 + start - lexer.lineStart;
  return new Token(kind, start, end, line, col, value);
}
function readNextToken(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let position = start;
  while (position < bodyLength) {
    const code = body.charCodeAt(position);
    switch (code) {
      // Ignored ::
      //   - UnicodeBOM
      //   - WhiteSpace
      //   - LineTerminator
      //   - Comment
      //   - Comma
      //
      // UnicodeBOM :: "Byte Order Mark (U+FEFF)"
      //
      // WhiteSpace ::
      //   - "Horizontal Tab (U+0009)"
      //   - "Space (U+0020)"
      //
      // Comma :: ,
      case 65279:
      // <BOM>
      case 9:
      // \t
      case 32:
      // <space>
      case 44:
        ++position;
        continue;
      // LineTerminator ::
      //   - "New Line (U+000A)"
      //   - "Carriage Return (U+000D)" [lookahead != "New Line (U+000A)"]
      //   - "Carriage Return (U+000D)" "New Line (U+000A)"
      case 10:
        ++position;
        ++lexer.line;
        lexer.lineStart = position;
        continue;
      case 13:
        if (body.charCodeAt(position + 1) === 10) {
          position += 2;
        } else {
          ++position;
        }
        ++lexer.line;
        lexer.lineStart = position;
        continue;
      // Comment
      case 35:
        return readComment(lexer, position);
      // Token ::
      //   - Punctuator
      //   - Name
      //   - IntValue
      //   - FloatValue
      //   - StringValue
      //
      // Punctuator :: one of ! $ & ( ) ... : = @ [ ] { | }
      case 33:
        return createToken(lexer, TokenKind.BANG, position, position + 1);
      case 36:
        return createToken(lexer, TokenKind.DOLLAR, position, position + 1);
      case 38:
        return createToken(lexer, TokenKind.AMP, position, position + 1);
      case 40:
        return createToken(lexer, TokenKind.PAREN_L, position, position + 1);
      case 41:
        return createToken(lexer, TokenKind.PAREN_R, position, position + 1);
      case 46:
        if (body.charCodeAt(position + 1) === 46 && body.charCodeAt(position + 2) === 46) {
          return createToken(lexer, TokenKind.SPREAD, position, position + 3);
        }
        break;
      case 58:
        return createToken(lexer, TokenKind.COLON, position, position + 1);
      case 61:
        return createToken(lexer, TokenKind.EQUALS, position, position + 1);
      case 64:
        return createToken(lexer, TokenKind.AT, position, position + 1);
      case 91:
        return createToken(lexer, TokenKind.BRACKET_L, position, position + 1);
      case 93:
        return createToken(lexer, TokenKind.BRACKET_R, position, position + 1);
      case 123:
        return createToken(lexer, TokenKind.BRACE_L, position, position + 1);
      case 124:
        return createToken(lexer, TokenKind.PIPE, position, position + 1);
      case 125:
        return createToken(lexer, TokenKind.BRACE_R, position, position + 1);
      // StringValue
      case 34:
        if (body.charCodeAt(position + 1) === 34 && body.charCodeAt(position + 2) === 34) {
          return readBlockString(lexer, position);
        }
        return readString(lexer, position);
    }
    if (isDigit(code) || code === 45) {
      return readNumber(lexer, position, code);
    }
    if (isNameStart(code)) {
      return readName(lexer, position);
    }
    throw syntaxError(
      lexer.source,
      position,
      code === 39 ? `Unexpected single quote character ('), did you mean to use a double quote (")?` : isUnicodeScalarValue(code) || isSupplementaryCodePoint(body, position) ? `Unexpected character: ${printCodePointAt(lexer, position)}.` : `Invalid character: ${printCodePointAt(lexer, position)}.`
    );
  }
  return createToken(lexer, TokenKind.EOF, bodyLength, bodyLength);
}
function readComment(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let position = start + 1;
  while (position < bodyLength) {
    const code = body.charCodeAt(position);
    if (code === 10 || code === 13) {
      break;
    }
    if (isUnicodeScalarValue(code)) {
      ++position;
    } else if (isSupplementaryCodePoint(body, position)) {
      position += 2;
    } else {
      break;
    }
  }
  return createToken(
    lexer,
    TokenKind.COMMENT,
    start,
    position,
    body.slice(start + 1, position)
  );
}
function readNumber(lexer, start, firstCode) {
  const body = lexer.source.body;
  let position = start;
  let code = firstCode;
  let isFloat = false;
  if (code === 45) {
    code = body.charCodeAt(++position);
  }
  if (code === 48) {
    code = body.charCodeAt(++position);
    if (isDigit(code)) {
      throw syntaxError(
        lexer.source,
        position,
        `Invalid number, unexpected digit after 0: ${printCodePointAt(
          lexer,
          position
        )}.`
      );
    }
  } else {
    position = readDigits(lexer, position, code);
    code = body.charCodeAt(position);
  }
  if (code === 46) {
    isFloat = true;
    code = body.charCodeAt(++position);
    position = readDigits(lexer, position, code);
    code = body.charCodeAt(position);
  }
  if (code === 69 || code === 101) {
    isFloat = true;
    code = body.charCodeAt(++position);
    if (code === 43 || code === 45) {
      code = body.charCodeAt(++position);
    }
    position = readDigits(lexer, position, code);
    code = body.charCodeAt(position);
  }
  if (code === 46 || isNameStart(code)) {
    throw syntaxError(
      lexer.source,
      position,
      `Invalid number, expected digit but got: ${printCodePointAt(
        lexer,
        position
      )}.`
    );
  }
  return createToken(
    lexer,
    isFloat ? TokenKind.FLOAT : TokenKind.INT,
    start,
    position,
    body.slice(start, position)
  );
}
function readDigits(lexer, start, firstCode) {
  if (!isDigit(firstCode)) {
    throw syntaxError(
      lexer.source,
      start,
      `Invalid number, expected digit but got: ${printCodePointAt(
        lexer,
        start
      )}.`
    );
  }
  const body = lexer.source.body;
  let position = start + 1;
  while (isDigit(body.charCodeAt(position))) {
    ++position;
  }
  return position;
}
function readString(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let position = start + 1;
  let chunkStart = position;
  let value = "";
  while (position < bodyLength) {
    const code = body.charCodeAt(position);
    if (code === 34) {
      value += body.slice(chunkStart, position);
      return createToken(lexer, TokenKind.STRING, start, position + 1, value);
    }
    if (code === 92) {
      value += body.slice(chunkStart, position);
      const escape = body.charCodeAt(position + 1) === 117 ? body.charCodeAt(position + 2) === 123 ? readEscapedUnicodeVariableWidth(lexer, position) : readEscapedUnicodeFixedWidth(lexer, position) : readEscapedCharacter(lexer, position);
      value += escape.value;
      position += escape.size;
      chunkStart = position;
      continue;
    }
    if (code === 10 || code === 13) {
      break;
    }
    if (isUnicodeScalarValue(code)) {
      ++position;
    } else if (isSupplementaryCodePoint(body, position)) {
      position += 2;
    } else {
      throw syntaxError(
        lexer.source,
        position,
        `Invalid character within String: ${printCodePointAt(
          lexer,
          position
        )}.`
      );
    }
  }
  throw syntaxError(lexer.source, position, "Unterminated string.");
}
function readEscapedUnicodeVariableWidth(lexer, position) {
  const body = lexer.source.body;
  let point = 0;
  let size = 3;
  while (size < 12) {
    const code = body.charCodeAt(position + size++);
    if (code === 125) {
      if (size < 5 || !isUnicodeScalarValue(point)) {
        break;
      }
      return {
        value: String.fromCodePoint(point),
        size
      };
    }
    point = point << 4 | readHexDigit(code);
    if (point < 0) {
      break;
    }
  }
  throw syntaxError(
    lexer.source,
    position,
    `Invalid Unicode escape sequence: "${body.slice(
      position,
      position + size
    )}".`
  );
}
function readEscapedUnicodeFixedWidth(lexer, position) {
  const body = lexer.source.body;
  const code = read16BitHexCode(body, position + 2);
  if (isUnicodeScalarValue(code)) {
    return {
      value: String.fromCodePoint(code),
      size: 6
    };
  }
  if (isLeadingSurrogate(code)) {
    if (body.charCodeAt(position + 6) === 92 && body.charCodeAt(position + 7) === 117) {
      const trailingCode = read16BitHexCode(body, position + 8);
      if (isTrailingSurrogate(trailingCode)) {
        return {
          value: String.fromCodePoint(code, trailingCode),
          size: 12
        };
      }
    }
  }
  throw syntaxError(
    lexer.source,
    position,
    `Invalid Unicode escape sequence: "${body.slice(position, position + 6)}".`
  );
}
function read16BitHexCode(body, position) {
  return readHexDigit(body.charCodeAt(position)) << 12 | readHexDigit(body.charCodeAt(position + 1)) << 8 | readHexDigit(body.charCodeAt(position + 2)) << 4 | readHexDigit(body.charCodeAt(position + 3));
}
function readHexDigit(code) {
  return code >= 48 && code <= 57 ? code - 48 : code >= 65 && code <= 70 ? code - 55 : code >= 97 && code <= 102 ? code - 87 : -1;
}
function readEscapedCharacter(lexer, position) {
  const body = lexer.source.body;
  const code = body.charCodeAt(position + 1);
  switch (code) {
    case 34:
      return {
        value: '"',
        size: 2
      };
    case 92:
      return {
        value: "\\",
        size: 2
      };
    case 47:
      return {
        value: "/",
        size: 2
      };
    case 98:
      return {
        value: "\b",
        size: 2
      };
    case 102:
      return {
        value: "\f",
        size: 2
      };
    case 110:
      return {
        value: "\n",
        size: 2
      };
    case 114:
      return {
        value: "\r",
        size: 2
      };
    case 116:
      return {
        value: "	",
        size: 2
      };
  }
  throw syntaxError(
    lexer.source,
    position,
    `Invalid character escape sequence: "${body.slice(
      position,
      position + 2
    )}".`
  );
}
function readBlockString(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let lineStart = lexer.lineStart;
  let position = start + 3;
  let chunkStart = position;
  let currentLine = "";
  const blockLines = [];
  while (position < bodyLength) {
    const code = body.charCodeAt(position);
    if (code === 34 && body.charCodeAt(position + 1) === 34 && body.charCodeAt(position + 2) === 34) {
      currentLine += body.slice(chunkStart, position);
      blockLines.push(currentLine);
      const token = createToken(
        lexer,
        TokenKind.BLOCK_STRING,
        start,
        position + 3,
        // Return a string of the lines joined with U+000A.
        dedentBlockStringLines(blockLines).join("\n")
      );
      lexer.line += blockLines.length - 1;
      lexer.lineStart = lineStart;
      return token;
    }
    if (code === 92 && body.charCodeAt(position + 1) === 34 && body.charCodeAt(position + 2) === 34 && body.charCodeAt(position + 3) === 34) {
      currentLine += body.slice(chunkStart, position);
      chunkStart = position + 1;
      position += 4;
      continue;
    }
    if (code === 10 || code === 13) {
      currentLine += body.slice(chunkStart, position);
      blockLines.push(currentLine);
      if (code === 13 && body.charCodeAt(position + 1) === 10) {
        position += 2;
      } else {
        ++position;
      }
      currentLine = "";
      chunkStart = position;
      lineStart = position;
      continue;
    }
    if (isUnicodeScalarValue(code)) {
      ++position;
    } else if (isSupplementaryCodePoint(body, position)) {
      position += 2;
    } else {
      throw syntaxError(
        lexer.source,
        position,
        `Invalid character within String: ${printCodePointAt(
          lexer,
          position
        )}.`
      );
    }
  }
  throw syntaxError(lexer.source, position, "Unterminated string.");
}
function readName(lexer, start) {
  const body = lexer.source.body;
  const bodyLength = body.length;
  let position = start + 1;
  while (position < bodyLength) {
    const code = body.charCodeAt(position);
    if (isNameContinue(code)) {
      ++position;
    } else {
      break;
    }
  }
  return createToken(
    lexer,
    TokenKind.NAME,
    start,
    position,
    body.slice(start, position)
  );
}

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/jsutils/inspect.mjs
var MAX_ARRAY_LENGTH = 10;
var MAX_RECURSIVE_DEPTH = 2;
function inspect(value) {
  return formatValue(value, []);
}
function formatValue(value, seenValues) {
  switch (typeof value) {
    case "string":
      return JSON.stringify(value);
    case "function":
      return value.name ? `[function ${value.name}]` : "[function]";
    case "object":
      return formatObjectValue(value, seenValues);
    default:
      return String(value);
  }
}
function formatObjectValue(value, previouslySeenValues) {
  if (value === null) {
    return "null";
  }
  if (previouslySeenValues.includes(value)) {
    return "[Circular]";
  }
  const seenValues = [...previouslySeenValues, value];
  if (isJSONable(value)) {
    const jsonValue = value.toJSON();
    if (jsonValue !== value) {
      return typeof jsonValue === "string" ? jsonValue : formatValue(jsonValue, seenValues);
    }
  } else if (Array.isArray(value)) {
    return formatArray(value, seenValues);
  }
  return formatObject(value, seenValues);
}
function isJSONable(value) {
  return typeof value.toJSON === "function";
}
function formatObject(object, seenValues) {
  const entries = Object.entries(object);
  if (entries.length === 0) {
    return "{}";
  }
  if (seenValues.length > MAX_RECURSIVE_DEPTH) {
    return "[" + getObjectTag(object) + "]";
  }
  const properties = entries.map(
    ([key, value]) => key + ": " + formatValue(value, seenValues)
  );
  return "{ " + properties.join(", ") + " }";
}
function formatArray(array, seenValues) {
  if (array.length === 0) {
    return "[]";
  }
  if (seenValues.length > MAX_RECURSIVE_DEPTH) {
    return "[Array]";
  }
  const len = Math.min(MAX_ARRAY_LENGTH, array.length);
  const remaining = array.length - len;
  const items = [];
  for (let i = 0; i < len; ++i) {
    items.push(formatValue(array[i], seenValues));
  }
  if (remaining === 1) {
    items.push("... 1 more item");
  } else if (remaining > 1) {
    items.push(`... ${remaining} more items`);
  }
  return "[" + items.join(", ") + "]";
}
function getObjectTag(object) {
  const tag = Object.prototype.toString.call(object).replace(/^\[object /, "").replace(/]$/, "");
  if (tag === "Object" && typeof object.constructor === "function") {
    const name = object.constructor.name;
    if (typeof name === "string" && name !== "") {
      return name;
    }
  }
  return tag;
}

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/jsutils/instanceOf.mjs
var isProduction = globalThis.process && // eslint-disable-next-line no-undef
process.env.NODE_ENV === "production";
var instanceOf = (
  /* c8 ignore next 6 */
  // FIXME: https://github.com/graphql/graphql-js/issues/2317
  isProduction ? function instanceOf2(value, constructor) {
    return value instanceof constructor;
  } : function instanceOf3(value, constructor) {
    if (value instanceof constructor) {
      return true;
    }
    if (typeof value === "object" && value !== null) {
      var _value$constructor;
      const className = constructor.prototype[Symbol.toStringTag];
      const valueClassName = (
        // We still need to support constructor's name to detect conflicts with older versions of this library.
        Symbol.toStringTag in value ? value[Symbol.toStringTag] : (_value$constructor = value.constructor) === null || _value$constructor === void 0 ? void 0 : _value$constructor.name
      );
      if (className === valueClassName) {
        const stringifiedValue = inspect(value);
        throw new Error(`Cannot use ${className} "${stringifiedValue}" from another module or realm.

Ensure that there is only one instance of "graphql" in the node_modules
directory. If different versions of "graphql" are the dependencies of other
relied on modules, use "resolutions" to ensure only one version is installed.

https://yarnpkg.com/en/docs/selective-version-resolutions

Duplicate "graphql" modules cannot be used at the same time since different
versions may have different capabilities and behavior. The data from one
version used in the function from another could produce confusing and
spurious results.`);
      }
    }
    return false;
  }
);

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/language/source.mjs
var Source = class {
  /** The GraphQL source text. */
  /** Name used in diagnostics for this source, such as a file path or request name. */
  /** One-indexed line and column where this source begins. */
  /**
   * Creates a Source instance.
   * @param body - The GraphQL source text.
   * @param name - Name used in diagnostics for this source.
   * @param locationOffset - One-indexed line and column where this source begins.
   * @example
   * ```ts
   * import { Source } from 'graphql/language';
   *
   * const source = new Source(
   *   'type Query { greeting: String }',
   *   'schema.graphql',
   *   { line: 10, column: 1 },
   * );
   *
   * source.body; // => 'type Query { greeting: String }'
   * source.name; // => 'schema.graphql'
   * source.locationOffset; // => { line: 10, column: 1 }
   * ```
   */
  constructor(body, name = "GraphQL request", locationOffset = {
    line: 1,
    column: 1
  }) {
    typeof body === "string" || devAssert(false, `Body must be a string. Received: ${inspect(body)}.`);
    this.body = body;
    this.name = name;
    this.locationOffset = locationOffset;
    this.locationOffset.line > 0 || devAssert(
      false,
      "line in locationOffset is 1-indexed and must be positive."
    );
    this.locationOffset.column > 0 || devAssert(
      false,
      "column in locationOffset is 1-indexed and must be positive."
    );
  }
  /**
   * Returns the value used by `Object.prototype.toString`.
   * @returns The built-in string tag for this object.
   */
  get [Symbol.toStringTag]() {
    return "Source";
  }
};
function isSource(source) {
  return instanceOf(source, Source);
}

// ../../node_modules/.pnpm/graphql@16.14.2/node_modules/graphql/language/parser.mjs
function parse(source, options) {
  const parser = new Parser(source, options);
  const document = parser.parseDocument();
  Object.defineProperty(document, "tokenCount", {
    enumerable: false,
    value: parser.tokenCount
  });
  return document;
}
var Parser = class {
  constructor(source, options = {}) {
    const { lexer, ..._options } = options;
    if (lexer) {
      this._lexer = lexer;
    } else {
      const sourceObj = isSource(source) ? source : new Source(source);
      this._lexer = new Lexer(sourceObj);
    }
    this._options = _options;
    this._tokenCounter = 0;
  }
  get tokenCount() {
    return this._tokenCounter;
  }
  /**
   * Converts a name lex token into a name parse node.
   *
   * @internal
   */
  parseName() {
    const token = this.expectToken(TokenKind.NAME);
    return this.node(token, {
      kind: Kind.NAME,
      value: token.value
    });
  }
  // Implements the parsing rules in the Document section.
  /**
   * Document : Definition+
   *
   * @internal
   */
  parseDocument() {
    return this.node(this._lexer.token, {
      kind: Kind.DOCUMENT,
      definitions: this.many(
        TokenKind.SOF,
        this.parseDefinition,
        TokenKind.EOF
      )
    });
  }
  /**
   * Definition :
   *   - ExecutableDefinition
   *   - TypeSystemDefinition
   *   - TypeSystemExtension
   *
   * ExecutableDefinition :
   *   - OperationDefinition
   *   - FragmentDefinition
   *
   * TypeSystemDefinition :
   *   - SchemaDefinition
   *   - TypeDefinition
   *   - DirectiveDefinition
   *
   * TypeDefinition :
   *   - ScalarTypeDefinition
   *   - ObjectTypeDefinition
   *   - InterfaceTypeDefinition
   *   - UnionTypeDefinition
   *   - EnumTypeDefinition
   *   - InputObjectTypeDefinition
   *
   * @internal
   */
  parseDefinition() {
    if (this.peek(TokenKind.BRACE_L)) {
      return this.parseOperationDefinition();
    }
    const hasDescription = this.peekDescription();
    const keywordToken = hasDescription ? this._lexer.lookahead() : this._lexer.token;
    if (hasDescription && keywordToken.kind === TokenKind.BRACE_L) {
      throw syntaxError(
        this._lexer.source,
        this._lexer.token.start,
        "Unexpected description, descriptions are not supported on shorthand queries."
      );
    }
    if (keywordToken.kind === TokenKind.NAME) {
      switch (keywordToken.value) {
        case "schema":
          return this.parseSchemaDefinition();
        case "scalar":
          return this.parseScalarTypeDefinition();
        case "type":
          return this.parseObjectTypeDefinition();
        case "interface":
          return this.parseInterfaceTypeDefinition();
        case "union":
          return this.parseUnionTypeDefinition();
        case "enum":
          return this.parseEnumTypeDefinition();
        case "input":
          return this.parseInputObjectTypeDefinition();
        case "directive":
          return this.parseDirectiveDefinition();
      }
      switch (keywordToken.value) {
        case "query":
        case "mutation":
        case "subscription":
          return this.parseOperationDefinition();
        case "fragment":
          return this.parseFragmentDefinition();
      }
      if (hasDescription) {
        throw syntaxError(
          this._lexer.source,
          this._lexer.token.start,
          "Unexpected description, only GraphQL definitions support descriptions."
        );
      }
      switch (keywordToken.value) {
        case "extend":
          return this.parseTypeSystemExtension();
      }
    }
    throw this.unexpected(keywordToken);
  }
  // Implements the parsing rules in the Operations section.
  /**
   * OperationDefinition :
   *  - SelectionSet
   *  - OperationType Name? VariableDefinitions? Directives? SelectionSet
   *
   * @internal
   */
  parseOperationDefinition() {
    const start = this._lexer.token;
    if (this.peek(TokenKind.BRACE_L)) {
      return this.node(start, {
        kind: Kind.OPERATION_DEFINITION,
        operation: OperationTypeNode.QUERY,
        description: void 0,
        name: void 0,
        variableDefinitions: [],
        directives: [],
        selectionSet: this.parseSelectionSet()
      });
    }
    const description = this.parseDescription();
    const operation = this.parseOperationType();
    let name;
    if (this.peek(TokenKind.NAME)) {
      name = this.parseName();
    }
    return this.node(start, {
      kind: Kind.OPERATION_DEFINITION,
      operation,
      description,
      name,
      variableDefinitions: this.parseVariableDefinitions(),
      directives: this.parseDirectives(false),
      selectionSet: this.parseSelectionSet()
    });
  }
  /**
   * OperationType : one of query mutation subscription
   *
   * @internal
   */
  parseOperationType() {
    const operationToken = this.expectToken(TokenKind.NAME);
    switch (operationToken.value) {
      case "query":
        return OperationTypeNode.QUERY;
      case "mutation":
        return OperationTypeNode.MUTATION;
      case "subscription":
        return OperationTypeNode.SUBSCRIPTION;
    }
    throw this.unexpected(operationToken);
  }
  /**
   * VariableDefinitions : ( VariableDefinition+ )
   *
   * @internal
   */
  parseVariableDefinitions() {
    return this.optionalMany(
      TokenKind.PAREN_L,
      this.parseVariableDefinition,
      TokenKind.PAREN_R
    );
  }
  /**
   * VariableDefinition : Variable : Type DefaultValue? Directives[Const]?
   *
   * @internal
   */
  parseVariableDefinition() {
    return this.node(this._lexer.token, {
      kind: Kind.VARIABLE_DEFINITION,
      description: this.parseDescription(),
      variable: this.parseVariable(),
      type: (this.expectToken(TokenKind.COLON), this.parseTypeReference()),
      defaultValue: this.expectOptionalToken(TokenKind.EQUALS) ? this.parseConstValueLiteral() : void 0,
      directives: this.parseConstDirectives()
    });
  }
  /**
   * Variable : $ Name
   *
   * @internal
   */
  parseVariable() {
    const start = this._lexer.token;
    this.expectToken(TokenKind.DOLLAR);
    return this.node(start, {
      kind: Kind.VARIABLE,
      name: this.parseName()
    });
  }
  /**
   * ```
   * SelectionSet : { Selection+ }
   * ```
   *
   * @internal
   */
  parseSelectionSet() {
    return this.node(this._lexer.token, {
      kind: Kind.SELECTION_SET,
      selections: this.many(
        TokenKind.BRACE_L,
        this.parseSelection,
        TokenKind.BRACE_R
      )
    });
  }
  /**
   * Selection :
   *   - Field
   *   - FragmentSpread
   *   - InlineFragment
   *
   * @internal
   */
  parseSelection() {
    return this.peek(TokenKind.SPREAD) ? this.parseFragment() : this.parseField();
  }
  /**
   * Field : Alias? Name Arguments? Directives? SelectionSet?
   *
   * Alias : Name :
   *
   * @internal
   */
  parseField() {
    const start = this._lexer.token;
    const nameOrAlias = this.parseName();
    let alias;
    let name;
    if (this.expectOptionalToken(TokenKind.COLON)) {
      alias = nameOrAlias;
      name = this.parseName();
    } else {
      name = nameOrAlias;
    }
    return this.node(start, {
      kind: Kind.FIELD,
      alias,
      name,
      arguments: this.parseArguments(false),
      directives: this.parseDirectives(false),
      selectionSet: this.peek(TokenKind.BRACE_L) ? this.parseSelectionSet() : void 0
    });
  }
  /**
   * Arguments[Const] : ( Argument[?Const]+ )
   *
   * @internal
   */
  parseArguments(isConst) {
    const item = isConst ? this.parseConstArgument : this.parseArgument;
    return this.optionalMany(TokenKind.PAREN_L, item, TokenKind.PAREN_R);
  }
  /**
   * Argument[Const] : Name : Value[?Const]
   *
   * @internal
   */
  parseArgument(isConst = false) {
    const start = this._lexer.token;
    const name = this.parseName();
    this.expectToken(TokenKind.COLON);
    return this.node(start, {
      kind: Kind.ARGUMENT,
      name,
      value: this.parseValueLiteral(isConst)
    });
  }
  parseConstArgument() {
    return this.parseArgument(true);
  }
  // Implements the parsing rules in the Fragments section.
  /**
   * Corresponds to both FragmentSpread and InlineFragment in the spec.
   *
   * FragmentSpread : ... FragmentName Directives?
   *
   * InlineFragment : ... TypeCondition? Directives? SelectionSet
   *
   * @internal
   */
  parseFragment() {
    const start = this._lexer.token;
    this.expectToken(TokenKind.SPREAD);
    const hasTypeCondition = this.expectOptionalKeyword("on");
    if (!hasTypeCondition && this.peek(TokenKind.NAME)) {
      return this.node(start, {
        kind: Kind.FRAGMENT_SPREAD,
        name: this.parseFragmentName(),
        directives: this.parseDirectives(false)
      });
    }
    return this.node(start, {
      kind: Kind.INLINE_FRAGMENT,
      typeCondition: hasTypeCondition ? this.parseNamedType() : void 0,
      directives: this.parseDirectives(false),
      selectionSet: this.parseSelectionSet()
    });
  }
  /**
   * FragmentDefinition :
   *   - fragment FragmentName on TypeCondition Directives? SelectionSet
   *
   * TypeCondition : NamedType
   *
   * @internal
   */
  parseFragmentDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword("fragment");
    if (this._options.allowLegacyFragmentVariables === true) {
      return this.node(start, {
        kind: Kind.FRAGMENT_DEFINITION,
        description,
        name: this.parseFragmentName(),
        variableDefinitions: this.parseVariableDefinitions(),
        typeCondition: (this.expectKeyword("on"), this.parseNamedType()),
        directives: this.parseDirectives(false),
        selectionSet: this.parseSelectionSet()
      });
    }
    return this.node(start, {
      kind: Kind.FRAGMENT_DEFINITION,
      description,
      name: this.parseFragmentName(),
      typeCondition: (this.expectKeyword("on"), this.parseNamedType()),
      directives: this.parseDirectives(false),
      selectionSet: this.parseSelectionSet()
    });
  }
  /**
   * FragmentName : Name but not `on`
   *
   * @internal
   */
  parseFragmentName() {
    if (this._lexer.token.value === "on") {
      throw this.unexpected();
    }
    return this.parseName();
  }
  // Implements the parsing rules in the Values section.
  /**
   * Value[Const] :
   *   - [~Const] Variable
   *   - IntValue
   *   - FloatValue
   *   - StringValue
   *   - BooleanValue
   *   - NullValue
   *   - EnumValue
   *   - ListValue[?Const]
   *   - ObjectValue[?Const]
   *
   * BooleanValue : one of `true` `false`
   *
   * NullValue : `null`
   *
   * EnumValue : Name but not `true`, `false` or `null`
   *
   * @internal
   */
  parseValueLiteral(isConst) {
    const token = this._lexer.token;
    switch (token.kind) {
      case TokenKind.BRACKET_L:
        return this.parseList(isConst);
      case TokenKind.BRACE_L:
        return this.parseObject(isConst);
      case TokenKind.INT:
        this.advanceLexer();
        return this.node(token, {
          kind: Kind.INT,
          value: token.value
        });
      case TokenKind.FLOAT:
        this.advanceLexer();
        return this.node(token, {
          kind: Kind.FLOAT,
          value: token.value
        });
      case TokenKind.STRING:
      case TokenKind.BLOCK_STRING:
        return this.parseStringLiteral();
      case TokenKind.NAME:
        this.advanceLexer();
        switch (token.value) {
          case "true":
            return this.node(token, {
              kind: Kind.BOOLEAN,
              value: true
            });
          case "false":
            return this.node(token, {
              kind: Kind.BOOLEAN,
              value: false
            });
          case "null":
            return this.node(token, {
              kind: Kind.NULL
            });
          default:
            return this.node(token, {
              kind: Kind.ENUM,
              value: token.value
            });
        }
      case TokenKind.DOLLAR:
        if (isConst) {
          this.expectToken(TokenKind.DOLLAR);
          if (this._lexer.token.kind === TokenKind.NAME) {
            const varName = this._lexer.token.value;
            throw syntaxError(
              this._lexer.source,
              token.start,
              `Unexpected variable "$${varName}" in constant value.`
            );
          } else {
            throw this.unexpected(token);
          }
        }
        return this.parseVariable();
      default:
        throw this.unexpected();
    }
  }
  parseConstValueLiteral() {
    return this.parseValueLiteral(true);
  }
  parseStringLiteral() {
    const token = this._lexer.token;
    this.advanceLexer();
    return this.node(token, {
      kind: Kind.STRING,
      value: token.value,
      block: token.kind === TokenKind.BLOCK_STRING
    });
  }
  /**
   * ListValue[Const] :
   *   - [ ]
   *   - [ Value[?Const]+ ]
   *
   * @internal
   */
  parseList(isConst) {
    const item = () => this.parseValueLiteral(isConst);
    return this.node(this._lexer.token, {
      kind: Kind.LIST,
      values: this.any(TokenKind.BRACKET_L, item, TokenKind.BRACKET_R)
    });
  }
  /**
   * ```
   * ObjectValue[Const] :
   *   - { }
   *   - { ObjectField[?Const]+ }
   * ```
   *
   * @internal
   */
  parseObject(isConst) {
    const item = () => this.parseObjectField(isConst);
    return this.node(this._lexer.token, {
      kind: Kind.OBJECT,
      fields: this.any(TokenKind.BRACE_L, item, TokenKind.BRACE_R)
    });
  }
  /**
   * ObjectField[Const] : Name : Value[?Const]
   *
   * @internal
   */
  parseObjectField(isConst) {
    const start = this._lexer.token;
    const name = this.parseName();
    this.expectToken(TokenKind.COLON);
    return this.node(start, {
      kind: Kind.OBJECT_FIELD,
      name,
      value: this.parseValueLiteral(isConst)
    });
  }
  // Implements the parsing rules in the Directives section.
  /**
   * Directives[Const] : Directive[?Const]+
   *
   * @internal
   */
  parseDirectives(isConst) {
    const directives = [];
    while (this.peek(TokenKind.AT)) {
      directives.push(this.parseDirective(isConst));
    }
    return directives;
  }
  parseConstDirectives() {
    return this.parseDirectives(true);
  }
  /**
   * ```
   * Directive[Const] : @ Name Arguments[?Const]?
   * ```
   *
   * @internal
   */
  parseDirective(isConst) {
    const start = this._lexer.token;
    this.expectToken(TokenKind.AT);
    return this.node(start, {
      kind: Kind.DIRECTIVE,
      name: this.parseName(),
      arguments: this.parseArguments(isConst)
    });
  }
  // Implements the parsing rules in the Types section.
  /**
   * Type :
   *   - NamedType
   *   - ListType
   *   - NonNullType
   *
   * @internal
   */
  parseTypeReference() {
    const start = this._lexer.token;
    let type;
    if (this.expectOptionalToken(TokenKind.BRACKET_L)) {
      const innerType = this.parseTypeReference();
      this.expectToken(TokenKind.BRACKET_R);
      type = this.node(start, {
        kind: Kind.LIST_TYPE,
        type: innerType
      });
    } else {
      type = this.parseNamedType();
    }
    if (this.expectOptionalToken(TokenKind.BANG)) {
      return this.node(start, {
        kind: Kind.NON_NULL_TYPE,
        type
      });
    }
    return type;
  }
  /**
   * NamedType : Name
   *
   * @internal
   */
  parseNamedType() {
    return this.node(this._lexer.token, {
      kind: Kind.NAMED_TYPE,
      name: this.parseName()
    });
  }
  // Implements the parsing rules in the Type Definition section.
  peekDescription() {
    return this.peek(TokenKind.STRING) || this.peek(TokenKind.BLOCK_STRING);
  }
  /**
   * Description : StringValue
   *
   * @internal
   */
  parseDescription() {
    if (this.peekDescription()) {
      return this.parseStringLiteral();
    }
  }
  /**
   * ```
   * SchemaDefinition : Description? schema Directives[Const]? { OperationTypeDefinition+ }
   * ```
   *
   * @internal
   */
  parseSchemaDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword("schema");
    const directives = this.parseConstDirectives();
    const operationTypes = this.many(
      TokenKind.BRACE_L,
      this.parseOperationTypeDefinition,
      TokenKind.BRACE_R
    );
    return this.node(start, {
      kind: Kind.SCHEMA_DEFINITION,
      description,
      directives,
      operationTypes
    });
  }
  /**
   * OperationTypeDefinition : OperationType : NamedType
   *
   * @internal
   */
  parseOperationTypeDefinition() {
    const start = this._lexer.token;
    const operation = this.parseOperationType();
    this.expectToken(TokenKind.COLON);
    const type = this.parseNamedType();
    return this.node(start, {
      kind: Kind.OPERATION_TYPE_DEFINITION,
      operation,
      type
    });
  }
  /**
   * ScalarTypeDefinition : Description? scalar Name Directives[Const]?
   *
   * @internal
   */
  parseScalarTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword("scalar");
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    return this.node(start, {
      kind: Kind.SCALAR_TYPE_DEFINITION,
      description,
      name,
      directives
    });
  }
  /**
   * ObjectTypeDefinition :
   *   Description?
   *   type Name ImplementsInterfaces? Directives[Const]? FieldsDefinition?
   *
   * @internal
   */
  parseObjectTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword("type");
    const name = this.parseName();
    const interfaces = this.parseImplementsInterfaces();
    const directives = this.parseConstDirectives();
    const fields = this.parseFieldsDefinition();
    return this.node(start, {
      kind: Kind.OBJECT_TYPE_DEFINITION,
      description,
      name,
      interfaces,
      directives,
      fields
    });
  }
  /**
   * ImplementsInterfaces :
   *   - implements `&`? NamedType
   *   - ImplementsInterfaces & NamedType
   *
   * @internal
   */
  parseImplementsInterfaces() {
    return this.expectOptionalKeyword("implements") ? this.delimitedMany(TokenKind.AMP, this.parseNamedType) : [];
  }
  /**
   * ```
   * FieldsDefinition : { FieldDefinition+ }
   * ```
   *
   * @internal
   */
  parseFieldsDefinition() {
    return this.optionalMany(
      TokenKind.BRACE_L,
      this.parseFieldDefinition,
      TokenKind.BRACE_R
    );
  }
  /**
   * FieldDefinition :
   *   - Description? Name ArgumentsDefinition? : Type Directives[Const]?
   *
   * @internal
   */
  parseFieldDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    const name = this.parseName();
    const args = this.parseArgumentDefs();
    this.expectToken(TokenKind.COLON);
    const type = this.parseTypeReference();
    const directives = this.parseConstDirectives();
    return this.node(start, {
      kind: Kind.FIELD_DEFINITION,
      description,
      name,
      arguments: args,
      type,
      directives
    });
  }
  /**
   * ArgumentsDefinition : ( InputValueDefinition+ )
   *
   * @internal
   */
  parseArgumentDefs() {
    return this.optionalMany(
      TokenKind.PAREN_L,
      this.parseInputValueDef,
      TokenKind.PAREN_R
    );
  }
  /**
   * InputValueDefinition :
   *   - Description? Name : Type DefaultValue? Directives[Const]?
   *
   * @internal
   */
  parseInputValueDef() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    const name = this.parseName();
    this.expectToken(TokenKind.COLON);
    const type = this.parseTypeReference();
    let defaultValue;
    if (this.expectOptionalToken(TokenKind.EQUALS)) {
      defaultValue = this.parseConstValueLiteral();
    }
    const directives = this.parseConstDirectives();
    return this.node(start, {
      kind: Kind.INPUT_VALUE_DEFINITION,
      description,
      name,
      type,
      defaultValue,
      directives
    });
  }
  /**
   * InterfaceTypeDefinition :
   *   - Description? interface Name Directives[Const]? FieldsDefinition?
   *
   * @internal
   */
  parseInterfaceTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword("interface");
    const name = this.parseName();
    const interfaces = this.parseImplementsInterfaces();
    const directives = this.parseConstDirectives();
    const fields = this.parseFieldsDefinition();
    return this.node(start, {
      kind: Kind.INTERFACE_TYPE_DEFINITION,
      description,
      name,
      interfaces,
      directives,
      fields
    });
  }
  /**
   * UnionTypeDefinition :
   *   - Description? union Name Directives[Const]? UnionMemberTypes?
   *
   * @internal
   */
  parseUnionTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword("union");
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const types = this.parseUnionMemberTypes();
    return this.node(start, {
      kind: Kind.UNION_TYPE_DEFINITION,
      description,
      name,
      directives,
      types
    });
  }
  /**
   * UnionMemberTypes :
   *   - = `|`? NamedType
   *   - UnionMemberTypes | NamedType
   *
   * @internal
   */
  parseUnionMemberTypes() {
    return this.expectOptionalToken(TokenKind.EQUALS) ? this.delimitedMany(TokenKind.PIPE, this.parseNamedType) : [];
  }
  /**
   * EnumTypeDefinition :
   *   - Description? enum Name Directives[Const]? EnumValuesDefinition?
   *
   * @internal
   */
  parseEnumTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword("enum");
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const values = this.parseEnumValuesDefinition();
    return this.node(start, {
      kind: Kind.ENUM_TYPE_DEFINITION,
      description,
      name,
      directives,
      values
    });
  }
  /**
   * ```
   * EnumValuesDefinition : { EnumValueDefinition+ }
   * ```
   *
   * @internal
   */
  parseEnumValuesDefinition() {
    return this.optionalMany(
      TokenKind.BRACE_L,
      this.parseEnumValueDefinition,
      TokenKind.BRACE_R
    );
  }
  /**
   * EnumValueDefinition : Description? EnumValue Directives[Const]?
   *
   * @internal
   */
  parseEnumValueDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    const name = this.parseEnumValueName();
    const directives = this.parseConstDirectives();
    return this.node(start, {
      kind: Kind.ENUM_VALUE_DEFINITION,
      description,
      name,
      directives
    });
  }
  /**
   * EnumValue : Name but not `true`, `false` or `null`
   *
   * @internal
   */
  parseEnumValueName() {
    if (this._lexer.token.value === "true" || this._lexer.token.value === "false" || this._lexer.token.value === "null") {
      throw syntaxError(
        this._lexer.source,
        this._lexer.token.start,
        `${getTokenDesc(
          this._lexer.token
        )} is reserved and cannot be used for an enum value.`
      );
    }
    return this.parseName();
  }
  /**
   * InputObjectTypeDefinition :
   *   - Description? input Name Directives[Const]? InputFieldsDefinition?
   *
   * @internal
   */
  parseInputObjectTypeDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword("input");
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const fields = this.parseInputFieldsDefinition();
    return this.node(start, {
      kind: Kind.INPUT_OBJECT_TYPE_DEFINITION,
      description,
      name,
      directives,
      fields
    });
  }
  /**
   * ```
   * InputFieldsDefinition : { InputValueDefinition+ }
   * ```
   *
   * @internal
   */
  parseInputFieldsDefinition() {
    return this.optionalMany(
      TokenKind.BRACE_L,
      this.parseInputValueDef,
      TokenKind.BRACE_R
    );
  }
  /**
   * TypeSystemExtension :
   *   - SchemaExtension
   *   - TypeExtension
   *
   * TypeExtension :
   *   - ScalarTypeExtension
   *   - ObjectTypeExtension
   *   - InterfaceTypeExtension
   *   - UnionTypeExtension
   *   - EnumTypeExtension
   *   - InputObjectTypeDefinition
   *   - DirectiveDefinitionExtension
   *
   * @internal
   */
  parseTypeSystemExtension() {
    const keywordToken = this._lexer.lookahead();
    if (keywordToken.kind === TokenKind.NAME) {
      switch (keywordToken.value) {
        case "schema":
          return this.parseSchemaExtension();
        case "scalar":
          return this.parseScalarTypeExtension();
        case "type":
          return this.parseObjectTypeExtension();
        case "interface":
          return this.parseInterfaceTypeExtension();
        case "union":
          return this.parseUnionTypeExtension();
        case "enum":
          return this.parseEnumTypeExtension();
        case "input":
          return this.parseInputObjectTypeExtension();
        case "directive":
          if (this._options.experimentalDirectivesOnDirectiveDefinitions) {
            return this.parseDirectiveDefinitionExtension();
          }
          break;
      }
    }
    throw this.unexpected(keywordToken);
  }
  /**
   * ```
   * SchemaExtension :
   *  - extend schema Directives[Const]? { OperationTypeDefinition+ }
   *  - extend schema Directives[Const]
   * ```
   *
   * @internal
   */
  parseSchemaExtension() {
    const start = this._lexer.token;
    this.expectKeyword("extend");
    this.expectKeyword("schema");
    const directives = this.parseConstDirectives();
    const operationTypes = this.optionalMany(
      TokenKind.BRACE_L,
      this.parseOperationTypeDefinition,
      TokenKind.BRACE_R
    );
    if (directives.length === 0 && operationTypes.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.SCHEMA_EXTENSION,
      directives,
      operationTypes
    });
  }
  /**
   * ScalarTypeExtension :
   *   - extend scalar Name Directives[Const]
   *
   * @internal
   */
  parseScalarTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword("extend");
    this.expectKeyword("scalar");
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    if (directives.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.SCALAR_TYPE_EXTENSION,
      name,
      directives
    });
  }
  /**
   * ObjectTypeExtension :
   *  - extend type Name ImplementsInterfaces? Directives[Const]? FieldsDefinition
   *  - extend type Name ImplementsInterfaces? Directives[Const]
   *  - extend type Name ImplementsInterfaces
   *
   * @internal
   */
  parseObjectTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword("extend");
    this.expectKeyword("type");
    const name = this.parseName();
    const interfaces = this.parseImplementsInterfaces();
    const directives = this.parseConstDirectives();
    const fields = this.parseFieldsDefinition();
    if (interfaces.length === 0 && directives.length === 0 && fields.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.OBJECT_TYPE_EXTENSION,
      name,
      interfaces,
      directives,
      fields
    });
  }
  /**
   * InterfaceTypeExtension :
   *  - extend interface Name ImplementsInterfaces? Directives[Const]? FieldsDefinition
   *  - extend interface Name ImplementsInterfaces? Directives[Const]
   *  - extend interface Name ImplementsInterfaces
   *
   * @internal
   */
  parseInterfaceTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword("extend");
    this.expectKeyword("interface");
    const name = this.parseName();
    const interfaces = this.parseImplementsInterfaces();
    const directives = this.parseConstDirectives();
    const fields = this.parseFieldsDefinition();
    if (interfaces.length === 0 && directives.length === 0 && fields.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.INTERFACE_TYPE_EXTENSION,
      name,
      interfaces,
      directives,
      fields
    });
  }
  /**
   * UnionTypeExtension :
   *   - extend union Name Directives[Const]? UnionMemberTypes
   *   - extend union Name Directives[Const]
   *
   * @internal
   */
  parseUnionTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword("extend");
    this.expectKeyword("union");
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const types = this.parseUnionMemberTypes();
    if (directives.length === 0 && types.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.UNION_TYPE_EXTENSION,
      name,
      directives,
      types
    });
  }
  /**
   * EnumTypeExtension :
   *   - extend enum Name Directives[Const]? EnumValuesDefinition
   *   - extend enum Name Directives[Const]
   *
   * @internal
   */
  parseEnumTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword("extend");
    this.expectKeyword("enum");
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const values = this.parseEnumValuesDefinition();
    if (directives.length === 0 && values.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.ENUM_TYPE_EXTENSION,
      name,
      directives,
      values
    });
  }
  /**
   * InputObjectTypeExtension :
   *   - extend input Name Directives[Const]? InputFieldsDefinition
   *   - extend input Name Directives[Const]
   *
   * @internal
   */
  parseInputObjectTypeExtension() {
    const start = this._lexer.token;
    this.expectKeyword("extend");
    this.expectKeyword("input");
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    const fields = this.parseInputFieldsDefinition();
    if (directives.length === 0 && fields.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.INPUT_OBJECT_TYPE_EXTENSION,
      name,
      directives,
      fields
    });
  }
  parseDirectiveDefinitionExtension() {
    const start = this._lexer.token;
    this.expectKeyword("extend");
    this.expectKeyword("directive");
    this.expectToken(TokenKind.AT);
    const name = this.parseName();
    const directives = this.parseConstDirectives();
    if (directives.length === 0) {
      throw this.unexpected();
    }
    return this.node(start, {
      kind: Kind.DIRECTIVE_EXTENSION,
      name,
      directives
    });
  }
  /**
   * ```
   * DirectiveDefinition :
   *   - Description? directive @ Name ArgumentsDefinition? `repeatable`? on DirectiveLocations
   * ```
   *
   * @internal
   */
  parseDirectiveDefinition() {
    const start = this._lexer.token;
    const description = this.parseDescription();
    this.expectKeyword("directive");
    this.expectToken(TokenKind.AT);
    const name = this.parseName();
    const args = this.parseArgumentDefs();
    const directives = this._options.experimentalDirectivesOnDirectiveDefinitions ? this.parseConstDirectives() : [];
    const repeatable = this.expectOptionalKeyword("repeatable");
    this.expectKeyword("on");
    const locations = this.parseDirectiveLocations();
    return this.node(start, {
      kind: Kind.DIRECTIVE_DEFINITION,
      description,
      name,
      arguments: args,
      directives,
      repeatable,
      locations
    });
  }
  /**
   * DirectiveLocations :
   *   - `|`? DirectiveLocation
   *   - DirectiveLocations | DirectiveLocation
   *
   * @internal
   */
  parseDirectiveLocations() {
    return this.delimitedMany(TokenKind.PIPE, this.parseDirectiveLocation);
  }
  /*
   * DirectiveLocation :
   *   - ExecutableDirectiveLocation
   *   - TypeSystemDirectiveLocation
   *
   * ExecutableDirectiveLocation : one of
   *   `QUERY`
   *   `MUTATION`
   *   `SUBSCRIPTION`
   *   `FIELD`
   *   `FRAGMENT_DEFINITION`
   *   `FRAGMENT_SPREAD`
   *   `INLINE_FRAGMENT`
   *
   * TypeSystemDirectiveLocation : one of
   *   `SCHEMA`
   *   `SCALAR`
   *   `OBJECT`
   *   `FIELD_DEFINITION`
   *   `ARGUMENT_DEFINITION`
   *   `INTERFACE`
   *   `UNION`
   *   `ENUM`
   *   `ENUM_VALUE`
   *   `INPUT_OBJECT`
   *   `INPUT_FIELD_DEFINITION`
   *   `DIRECTIVE_DEFINITION`
   */
  parseDirectiveLocation() {
    const start = this._lexer.token;
    const name = this.parseName();
    if (Object.prototype.hasOwnProperty.call(DirectiveLocation, name.value)) {
      return name;
    }
    throw this.unexpected(start);
  }
  // Schema Coordinates
  /**
   * SchemaCoordinate :
   *   - Name
   *   - Name . Name
   *   - Name . Name ( Name : )
   *   - \@ Name
   *   - \@ Name ( Name : )
   * @returns Parsed schema coordinate AST.
   * @example
   * ```ts
   * import { Parser, Source } from 'graphql/language';
   *
   * const typeCoordinate = new Parser(new Source('User.name')).parseSchemaCoordinate();
   * const directiveCoordinate = new Parser(new Source('@include(if:)')).parseSchemaCoordinate();
   *
   * typeCoordinate.name.value; // => 'User'
   * typeCoordinate.memberName?.value; // => 'name'
   * directiveCoordinate.name.value; // => 'deprecated'
   * directiveCoordinate.argumentName?.value; // => 'reason'
   * ```
   */
  parseSchemaCoordinate() {
    const start = this._lexer.token;
    const ofDirective = this.expectOptionalToken(TokenKind.AT);
    const name = this.parseName();
    let memberName;
    if (!ofDirective && this.expectOptionalToken(TokenKind.DOT)) {
      memberName = this.parseName();
    }
    let argumentName;
    if ((ofDirective || memberName) && this.expectOptionalToken(TokenKind.PAREN_L)) {
      argumentName = this.parseName();
      this.expectToken(TokenKind.COLON);
      this.expectToken(TokenKind.PAREN_R);
    }
    if (ofDirective) {
      if (argumentName) {
        return this.node(start, {
          kind: Kind.DIRECTIVE_ARGUMENT_COORDINATE,
          name,
          argumentName
        });
      }
      return this.node(start, {
        kind: Kind.DIRECTIVE_COORDINATE,
        name
      });
    } else if (memberName) {
      if (argumentName) {
        return this.node(start, {
          kind: Kind.ARGUMENT_COORDINATE,
          name,
          fieldName: memberName,
          argumentName
        });
      }
      return this.node(start, {
        kind: Kind.MEMBER_COORDINATE,
        name,
        memberName
      });
    }
    return this.node(start, {
      kind: Kind.TYPE_COORDINATE,
      name
    });
  }
  // Core parsing utility functions
  /**
   * Returns a node that, if configured to do so, sets a "loc" field as a
   * location object, used to identify the place in the source that created a
   * given parsed object.
   *
   * @internal
   */
  node(startToken, node) {
    if (this._options.noLocation !== true) {
      node.loc = new Location(
        startToken,
        this._lexer.lastToken,
        this._lexer.source
      );
    }
    return node;
  }
  /**
   * Determines if the next token is of a given kind
   *
   * @internal
   */
  peek(kind) {
    return this._lexer.token.kind === kind;
  }
  /**
   * If the next token is of the given kind, return that token after advancing the lexer.
   * Otherwise, do not change the parser state and throw an error.
   *
   * @internal
   */
  expectToken(kind) {
    const token = this._lexer.token;
    if (token.kind === kind) {
      this.advanceLexer();
      return token;
    }
    throw syntaxError(
      this._lexer.source,
      token.start,
      `Expected ${getTokenKindDesc(kind)}, found ${getTokenDesc(token)}.`
    );
  }
  /**
   * If the next token is of the given kind, return "true" after advancing the lexer.
   * Otherwise, do not change the parser state and return "false".
   *
   * @internal
   */
  expectOptionalToken(kind) {
    const token = this._lexer.token;
    if (token.kind === kind) {
      this.advanceLexer();
      return true;
    }
    return false;
  }
  /**
   * If the next token is a given keyword, advance the lexer.
   * Otherwise, do not change the parser state and throw an error.
   *
   * @internal
   */
  expectKeyword(value) {
    const token = this._lexer.token;
    if (token.kind === TokenKind.NAME && token.value === value) {
      this.advanceLexer();
    } else {
      throw syntaxError(
        this._lexer.source,
        token.start,
        `Expected "${value}", found ${getTokenDesc(token)}.`
      );
    }
  }
  /**
   * If the next token is a given keyword, return "true" after advancing the lexer.
   * Otherwise, do not change the parser state and return "false".
   *
   * @internal
   */
  expectOptionalKeyword(value) {
    const token = this._lexer.token;
    if (token.kind === TokenKind.NAME && token.value === value) {
      this.advanceLexer();
      return true;
    }
    return false;
  }
  /**
   * Helper function for creating an error when an unexpected lexed token is encountered.
   *
   * @internal
   */
  unexpected(atToken) {
    const token = atToken !== null && atToken !== void 0 ? atToken : this._lexer.token;
    return syntaxError(
      this._lexer.source,
      token.start,
      `Unexpected ${getTokenDesc(token)}.`
    );
  }
  /**
   * Returns a possibly empty list of parse nodes, determined by the parseFn.
   * This list begins with a lex token of openKind and ends with a lex token of closeKind.
   * Advances the parser to the next lex token after the closing token.
   *
   * @internal
   */
  any(openKind, parseFn, closeKind) {
    this.expectToken(openKind);
    const nodes = [];
    while (!this.expectOptionalToken(closeKind)) {
      nodes.push(parseFn.call(this));
    }
    return nodes;
  }
  /**
   * Returns a list of parse nodes, determined by the parseFn.
   * It can be empty only if open token is missing otherwise it will always return non-empty list
   * that begins with a lex token of openKind and ends with a lex token of closeKind.
   * Advances the parser to the next lex token after the closing token.
   *
   * @internal
   */
  optionalMany(openKind, parseFn, closeKind) {
    if (this.expectOptionalToken(openKind)) {
      const nodes = [];
      do {
        nodes.push(parseFn.call(this));
      } while (!this.expectOptionalToken(closeKind));
      return nodes;
    }
    return [];
  }
  /**
   * Returns a non-empty list of parse nodes, determined by the parseFn.
   * This list begins with a lex token of openKind and ends with a lex token of closeKind.
   * Advances the parser to the next lex token after the closing token.
   *
   * @internal
   */
  many(openKind, parseFn, closeKind) {
    this.expectToken(openKind);
    const nodes = [];
    do {
      nodes.push(parseFn.call(this));
    } while (!this.expectOptionalToken(closeKind));
    return nodes;
  }
  /**
   * Returns a non-empty list of parse nodes, determined by the parseFn.
   * This list may begin with a lex token of delimiterKind followed by items separated by lex tokens of tokenKind.
   * Advances the parser to the next lex token after last item in the list.
   *
   * @internal
   */
  delimitedMany(delimiterKind, parseFn) {
    this.expectOptionalToken(delimiterKind);
    const nodes = [];
    do {
      nodes.push(parseFn.call(this));
    } while (this.expectOptionalToken(delimiterKind));
    return nodes;
  }
  advanceLexer() {
    const { maxTokens } = this._options;
    const token = this._lexer.advance();
    if (token.kind !== TokenKind.EOF) {
      ++this._tokenCounter;
      if (maxTokens !== void 0 && this._tokenCounter > maxTokens) {
        throw syntaxError(
          this._lexer.source,
          token.start,
          `Document contains more that ${maxTokens} tokens. Parsing aborted.`
        );
      }
    }
  }
};
function getTokenDesc(token) {
  const value = token.value;
  return getTokenKindDesc(token.kind) + (value != null ? ` "${value}"` : "");
}
function getTokenKindDesc(kind) {
  return isPunctuatorTokenKind(kind) ? `"${kind}"` : kind;
}

// src/encryption/database-key-scanner.ts
import { resolveExecutor as resolveExecutor3 } from "@hopdrive/sdk-core";
function buildScanQuery(table, configKeys) {
  const fields = [table.idField, "config"];
  if (table.nameField) {
    fields.push(table.nameField);
  }
  const keyConditions = configKeys.map((key) => `{ config: { _has_key: "${key}" } }`);
  const whereClause = keyConditions.length > 1 ? `{ _or: [${keyConditions.join(", ")}] }` : keyConditions[0] || "{ config: { _is_null: false } }";
  return `
    query ScanTable {
      ${table.tableName}(where: ${whereClause}) {
        ${fields.join("\n        ")}
      }
    }
  `;
}
function buildUpdateMutation(table, id, newConfig) {
  const configJson = JSON.stringify(newConfig);
  return `
    mutation UpdateConfig {
      update_${table.tableName}_by_pk(
        pk_columns: { ${table.idField}: ${id} },
        _set: { config: ${configJson} }
      ) {
        ${table.idField}
      }
    }
  `;
}
async function scanTable(executor, table, configKeys, targetVersion) {
  if (configKeys.length === 0) {
    return { records: [], scanned: 0 };
  }
  const queryString = buildScanQuery(table, configKeys);
  const document = parse(queryString);
  const result = await executor.query(
    document,
    {}
  );
  const rows = result?.[table.tableName] ?? [];
  const records = [];
  for (const row of rows) {
    const config = row.config;
    if (!config) continue;
    const encryptedValues = scanObjectForEncryptedValues(config);
    const filteredValues = targetVersion ? encryptedValues.filter((v) => v.version === targetVersion) : encryptedValues;
    if (filteredValues.length > 0) {
      records.push({
        table: table.tableName,
        level: table.level,
        id: row[table.idField],
        name: table.nameField ? row[table.nameField] : void 0,
        config,
        encryptedValues: filteredValues
      });
    }
  }
  return { records, scanned: rows.length };
}
async function scanDatabaseForEncryptedKeys(params) {
  const { targetVersion } = params;
  const executor = resolveExecutor3(params.executor);
  const encryptedSchemas = discoverEncryptedFieldSchemas();
  const configKeys = encryptedSchemas.map((s) => s.configKey);
  const result = {
    targetVersion,
    totalRecordsScanned: 0,
    affectedRecords: [],
    summaryByTable: {}
  };
  for (const table of CONFIG_TABLES) {
    const { records, scanned } = await scanTable(executor, table, configKeys, targetVersion);
    result.totalRecordsScanned += scanned;
    result.affectedRecords.push(...records);
    result.summaryByTable[table.tableName] = {
      scanned,
      affected: records.length
    };
  }
  return result;
}
async function findRecordsUsingKeyVersion(params) {
  const result = await scanDatabaseForEncryptedKeys({
    targetVersion: params.version,
    executor: params.executor
  });
  return result.affectedRecords;
}
function setNestedValue(obj, path, value) {
  const parts = path.split(".");
  let current = obj;
  for (let i = 0; i < parts.length - 1; i++) {
    const part = parts[i];
    if (!(part in current) || typeof current[part] !== "object") {
      current[part] = {};
    }
    current = current[part];
  }
  current[parts[parts.length - 1]] = value;
}
function prepareKeyRotationPlan(params) {
  const { fromVersion, records } = params;
  const plan = {
    fromVersion,
    toVersion: params.toVersion ?? "preferred",
    updates: [],
    errors: []
  };
  for (const record of records) {
    for (const encryptedValue of record.encryptedValues) {
      if (encryptedValue.version !== fromVersion) {
        continue;
      }
      try {
        const newValue = reEncryptSecret(encryptedValue.value, params.toVersion);
        plan.updates.push({
          table: record.table,
          id: record.id,
          path: encryptedValue.path,
          oldValue: encryptedValue.value,
          newValue,
          config: record.config
        });
        if (plan.toVersion === "preferred") {
          const match = newValue.match(/^encrypted:(v\d+):/);
          if (match) {
            plan.toVersion = match[1];
          }
        }
      } catch (error) {
        plan.errors.push({
          record,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }
  }
  return plan;
}
async function applyKeyRotationPlan(params) {
  const { plan, dryRun = false } = params;
  const executor = resolveExecutor3(params.executor);
  const result = {
    applied: 0,
    failed: [],
    dryRun
  };
  if (dryRun) {
    result.applied = plan.updates.length;
    return result;
  }
  const updatesByRecord = /* @__PURE__ */ new Map();
  for (const update of plan.updates) {
    const key = `${update.table}:${update.id}`;
    const existing = updatesByRecord.get(key);
    if (existing) {
      existing.updates.push(update);
    } else {
      const tableConfig = CONFIG_TABLES.find((t) => t.tableName === update.table);
      if (!tableConfig) {
        result.failed.push({ update, error: `Unknown table: ${update.table}` });
        continue;
      }
      updatesByRecord.set(key, {
        table: tableConfig,
        id: update.id,
        updates: [update],
        config: update.config
      });
    }
  }
  for (const [, recordUpdate] of updatesByRecord) {
    try {
      const newConfig = JSON.parse(JSON.stringify(recordUpdate.config));
      for (const update of recordUpdate.updates) {
        setNestedValue(newConfig, update.path, update.newValue);
      }
      const mutationString = buildUpdateMutation(recordUpdate.table, recordUpdate.id, newConfig);
      const document = parse(mutationString);
      await executor.mutate(document, {});
      result.applied += recordUpdate.updates.length;
    } catch (error) {
      for (const update of recordUpdate.updates) {
        result.failed.push({
          update,
          error: error instanceof Error ? error.message : String(error)
        });
      }
    }
  }
  return result;
}
async function rotateEncryptionVersion(params) {
  const scanResult = await scanDatabaseForEncryptedKeys({
    targetVersion: params.fromVersion,
    executor: params.executor
  });
  const plan = prepareKeyRotationPlan({
    fromVersion: params.fromVersion,
    toVersion: params.toVersion,
    records: scanResult.affectedRecords
  });
  const applyResult = await applyKeyRotationPlan({
    plan,
    executor: params.executor,
    dryRun: params.dryRun
  });
  return { scanResult, plan, applyResult };
}
function generateDatabaseKeyReport(scanResults) {
  const lines = [
    "=== Database Encryption Key Usage Report ===",
    ""
  ];
  for (const result of scanResults) {
    lines.push(`Version: ${result.targetVersion}`);
    lines.push(`  Total records scanned: ${result.totalRecordsScanned}`);
    lines.push(`  Affected records: ${result.affectedRecords.length}`);
    lines.push("");
    lines.push("  By table:");
    for (const [table, summary] of Object.entries(result.summaryByTable)) {
      if (summary.affected > 0) {
        lines.push(`    ${table}: ${summary.affected} records (of ${summary.scanned} scanned)`);
      }
    }
    lines.push("");
    if (result.affectedRecords.length > 0) {
      lines.push("  Affected records:");
      for (const record of result.affectedRecords.slice(0, 10)) {
        const name = record.name ? ` (${record.name})` : "";
        lines.push(`    - ${record.table} #${record.id}${name}: ${record.encryptedValues.length} encrypted values`);
        for (const ev of record.encryptedValues.slice(0, 3)) {
          lines.push(`        \u2022 ${ev.path}`);
        }
        if (record.encryptedValues.length > 3) {
          lines.push(`        ... and ${record.encryptedValues.length - 3} more`);
        }
      }
      if (result.affectedRecords.length > 10) {
        lines.push(`    ... and ${result.affectedRecords.length - 10} more records`);
      }
    }
    lines.push("");
  }
  lines.push("=== End Report ===");
  return lines.join("\n");
}
export {
  ALL_CONFIG_LEVELS,
  AUTOCOMPLETE_FIELDS,
  AppraisalTimestampSchema,
  CONFIG_LEVELS_REGISTRY,
  CONFIG_TABLES,
  ENCRYPTED_STRING_PATTERN,
  EmailScrapeMappingSchema,
  EmailScrapePipelineStepSchema,
  EncryptionKeyNotFoundError,
  EnrichmentSchema,
  EntityNotFoundError,
  FETCH_RESULT_FIELDS,
  GUESTRIDES_ACCESS_FIELDS,
  GUESTRIDES_ACCESS_KEY,
  GUESTRIDES_DEALER_FIELDS,
  GUESTRIDES_KEY,
  HOPDRIVE_DEFAULT_CONFIG,
  HopDriveConfigSchema,
  IntegrationSchema,
  LEVELS,
  MoveTypeSchema,
  QueryGeneratorError,
  RcEmailScrapeConfigSchema,
  RideTypeOverrideSchema,
  UNSET_SOURCE_LEVEL,
  analyzeKeyDependencies,
  analyzeKeyVersionUsage,
  applyKeyRotationPlan,
  buildLinkTableInsertObjects,
  buildSettingJsonSchema,
  clearEncryptedFieldSchemaCache,
  combineMutations,
  composeEditableSettings,
  composeSettingsView,
  createEncryptionConfig,
  createEncryptionKeys,
  createFetchExecutor2 as createFetchExecutor,
  decryptSecret,
  decryptSecrets,
  discoverEncryptedFieldSchemas,
  effectiveReadableAtLevels,
  effectiveWritableByRoles,
  encryptSecret,
  encryptedString,
  enrichMetadata,
  extractLinkTableValues,
  filterConfigByRole,
  findRecordsUsingKeyVersion,
  formatKeyVersion,
  generateDatabaseKeyReport,
  generateDedicatedColumnGetterQuery,
  generateDedicatedColumnSetterMutation,
  generateEnumOptionsQuery,
  generateJsonbDeleteMutation,
  generateJsonbGetterQuery,
  generateJsonbSetterMutation,
  generateKeyStatusReport,
  generateLinkTableGetterQuery,
  generateLinkTableSetterMutation,
  generateStorageQueries,
  getActiveEncryptionVersions,
  getAllEncryptionVersions,
  getAvailableEncryptionVersions,
  getCiphertext,
  getConfigLevelInfo,
  getConfigTableForLevel,
  getContractConfig2 as getContractConfig,
  getCustomerConfig2 as getCustomerConfig,
  getDeprecatedEncryptionVersions,
  getEncryptedFieldSchemas,
  getEncryptionVersion,
  getEncryptionVersionMetadata,
  getHopdriveDefaults,
  getJsonSchemaForSetting,
  getKeyForVersion,
  getMoveConfig2 as getMoveConfig,
  getOrganizationConfig2 as getOrganizationConfig,
  getPreferredEncryptionVersion,
  getRateruleGroupConfig,
  getRegionConfig2 as getRegionConfig,
  getSettingAccess,
  getWorkflowsetConfig2 as getWorkflowsetConfig,
  init,
  isEncryptedString,
  levelHasTable,
  prepareKeyRotationPlan,
  reEncryptSecret,
  readGuestridesAccessSection,
  resetDefaultExecutor,
  resetEncryptionConfig,
  resolveExecutor,
  resolveUpdatedBy,
  rotateEncryptionVersion,
  safeValidateHopDriveConfig,
  scanAndAnalyzeConfigs,
  scanConfigForEncryptedValues,
  scanDatabaseForEncryptedKeys,
  scanObjectForEncryptedValues,
  splitGuestridesConfig,
  validateHopDriveConfig
};
//# sourceMappingURL=index.mjs.map